// ========================================
// DEBUG CONTROL - Set to false to disable all console.log
// ========================================
const DEBUG = true; // Change to true to enable console logs

// Override console.log based on DEBUG flag
const originalConsoleLog = console.log;
const originalConsoleError = console.error;
const originalConsoleWarn = console.warn;

if (!DEBUG) {
    console.log = function() {};
    console.warn = function() {};
    // Keep console.error always enabled for critical errors
}

// Shopify Add to Cart Function
async function addToShopifyCart(items) {
    try {
        const formData = {
            items: items.map(item => ({
                id: item.variantId,
                quantity: item.quantity
            }))
        };
        
        const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(formData)
        });
        
        if (!response.ok) {
            throw new Error('Failed to add to cart');
        }
        
        const data = await response.json();
        return data;
    } catch (error) {
        console.error('Error adding to cart:', error);
        throw error;
    }
}

// Elements & Configuration
const canvas = document.getElementById('trackCanvas');
const canvasContainer = document.getElementById('canvas-container');
const ctx = canvas.getContext('2d');
const resultsList = document.getElementById('results-list');
const inlineLengthInput = document.getElementById('inlineLengthInput');
const modeIndicator = document.getElementById('modeIndicator');
const selectionHint = document.getElementById('selectionHint');
const jointTooltip = document.getElementById('jointTooltip');
const angleIndicator = document.getElementById('angleIndicator');
const totalCostDiv = document.getElementById('totalCost');
const costValueSpan = document.getElementById('costValue');

// Constants
const BASE_CANVAS_SIZE = 600;
const PIXELS_PER_METER = 80;
const SNAP_TOLERANCE = 15;
const MIN_SEGMENT_PIXELS = 5;
const SELECT_TOLERANCE = 10;
const FLOAT_TOLERANCE = 0.01;
const OUTLET_RADIUS = 16; // 32px / 2
const OUTLET_SNAP_DISTANCE = 50; // Distance for snapping to tracks (increased)
const POINT_SNAP_TOLERANCE = 10;
const JOINT_SNAP_TOLERANCE = 12;
const WALL_SNAP_DISTANCE = 20;
const ANGLE_TOLERANCE = 5;

// Helper function to round length to avoid floating point precision issues
function roundLength(lengthMeters) {
    // First round to 0.01m precision
    let rounded = Math.round(lengthMeters * 100) / 100;
    
    // If close to a whole meter (within 0.09m), snap to that whole meter
    // This prevents 1.05m from requiring a 2m track instead of 1m
    const nearestMeter = Math.round(rounded);
    if (Math.abs(rounded - nearestMeter) < 0.1) {
        return nearestMeter;
    }
    
    return rounded;
}

// State
let points = [];
let segments = [];
window.segments = segments; // Make segments accessible to other sections (lamps)
let features = [];
let componentList = {};
let pointCounter = 0;
let isDrawing = false;
let currentDrawingStart = null;
let currentDrawingEnd = null;
let editingSegmentIndex = null;
let selectedElement = null;
let currentTool = 'draw';
let isConnectingPower = false;
let powerConnectionStart = null;
let powerConnectionEndCandidate = null;
let powerConnection = null;
let powerGhostPreview = null; // For showing snap preview when placing power
let lastMousePos = null; // For custom cursor drawing
let isDraggingEndpoint = false;
let draggingSegmentIndex = null;
let draggingPointId = null;
let dragStartPos = null;
let activeGuideLines = []; // Guide lines for alignment during dragging
let isShiftPressed = false;
let isCtrlPressed = false;
let canvasZoom = 1;
let canvasPanX = 0;
let canvasPanY = 0;
let isAltPressed = false;
let isSpacePressed = false;
let isPanning = false;
let panStartX = 0;
let panStartY = 0;
let panStartCanvasX = 0;
let panStartCanvasY = 0;
window.outletSnapPreview = null;

let segmentJointPositions = new Map();

// Multi-selection state
let selectedElements = [];
let isBoxSelecting = false;
let selectionBoxStart = null;
let selectionBoxEnd = null;

// Configuration state
let trackColor = 'black';
let suspensionEnabled = false;

// Animation for power flow
let powerFlowOffset = 0;

// Global state for other sections to access
window.configuratorState = {
    selectedColor: trackColor
};

// 3D View Variables
let scene, camera, renderer, controls;
let is3DView = false;
let animationId = null;
let updateCamera = null;
let savedCameraPosition = null;
let savedCameraTarget = null;

// Undo/Redo state
let historyStack = [];
let historyIndex = -1;
const MAX_HISTORY = 50;

// CRITICAL: Cache for segment offset directions
// This is calculated ONCE after power connection is set
// and reused in drawAnimatedPowerFlow() every frame
let cachedSegmentOffsetDirections = null;
let cachedTConnectorAnalysis = null;
let cachedGlobalOffsetDirection = null;

// REAL Rendl Components - Only SKU codes (all other data comes from Shopify)
const RENDL_COMPONENTS = {
    // White tracks
    TRACK_1M_WHITE: { code: 'R12254', color: 'white' },
    TRACK_2M_WHITE: { code: 'R12257', color: 'white' },
    
    // Black tracks
    TRACK_1M_BLACK: { code: 'R12255', color: 'black' },
    TRACK_2M_BLACK: { code: 'R12258', color: 'black' },
    
    // Silver tracks
    TRACK_1M_SILVER: { code: 'R12256', color: 'silver' },
    TRACK_2M_SILVER: { code: 'R12259', color: 'silver' },
    
    // L Connector Inner - White
    L_CONNECTOR_INNER_WHITE: { code: 'R12269', color: 'white' },
    L_CONNECTOR_INNER_BLACK: { code: 'R12270', color: 'black' },
    L_CONNECTOR_INNER_SILVER: { code: 'R12271', color: 'silver' },
    
    // L Connector Outer - White
    L_CONNECTOR_OUTER_WHITE: { code: 'R12266', color: 'white' },
    L_CONNECTOR_OUTER_BLACK: { code: 'R12267', color: 'black' },
    L_CONNECTOR_OUTER_SILVER: { code: 'R12268', color: 'silver' },
    
    // T Connector Right
    T_CONNECTOR_RIGHT_WHITE: { code: 'R12275', color: 'white' },
    T_CONNECTOR_RIGHT_BLACK: { code: 'R12276', color: 'black' },
    T_CONNECTOR_RIGHT_SILVER: { code: 'R12277', color: 'silver' },
    
    // T Connector Left
    T_CONNECTOR_LEFT_WHITE: { code: 'R12272', color: 'white' },
    T_CONNECTOR_LEFT_BLACK: { code: 'R12273', color: 'black' },
    T_CONNECTOR_LEFT_SILVER: { code: 'R12274', color: 'silver' },
    
    // X Connector
    X_CONNECTOR_WHITE: { code: 'R12278', color: 'white' },
    X_CONNECTOR_BLACK: { code: 'R12279', color: 'black' },
    X_CONNECTOR_SILVER: { code: 'R12280', color: 'silver' },
    
    // Feed End Right
    FEED_END_RIGHT_WHITE: { code: 'R12287', color: 'white' },
    FEED_END_RIGHT_BLACK: { code: 'R12288', color: 'black' },
    FEED_END_RIGHT_SILVER: { code: 'R12289', color: 'silver' },
    
    // Feed End Left
    FEED_END_LEFT_WHITE: { code: 'R12284', color: 'white' },
    FEED_END_LEFT_BLACK: { code: 'R12285', color: 'black' },
    FEED_END_LEFT_SILVER: { code: 'R12286', color: 'silver' },
    
    // Feed Straight
    FEED_STRAIGHT_WHITE: { code: 'R12263', color: 'white' },
    FEED_STRAIGHT_BLACK: { code: 'R12264', color: 'black' },
    FEED_STRAIGHT_SILVER: { code: 'R12265', color: 'silver' },
    
    // End Cap
    END_CAP_WHITE: { code: 'R12290', color: 'white' },
    END_CAP_BLACK: { code: 'R12291', color: 'black' },
    END_CAP_SILVER: { code: 'R12292', color: 'silver' },
    
    // Straight Connector
    STRAIGHT_CONNECTOR_WHITE: { code: 'R12260', color: 'white' },
    STRAIGHT_CONNECTOR_BLACK: { code: 'R12261', color: 'black' },
    STRAIGHT_CONNECTOR_SILVER: { code: 'R12262', color: 'silver' },
    
    // Flex Connector
    FLEX_CONNECTOR_WHITE: { code: 'R12281', color: 'white' },
    FLEX_CONNECTOR_BLACK: { code: 'R12282', color: 'black' },
    FLEX_CONNECTOR_SILVER: { code: 'R12283', color: 'silver' },
    
    // Adapter
    ADAPTER_WHITE: { code: 'R12293', color: 'white' },
    ADAPTER_BLACK: { code: 'R12294', color: 'black' },
    ADAPTER_SILVER: { code: 'R12295', color: 'silver' },
    
    // Suspension kits (by color)
    SUSPENSION_KIT_WHITE: { code: 'R12296', color: 'white' },
    SUSPENSION_KIT_BLACK: { code: 'R12297', color: 'black' },
    SUSPENSION_KIT_SILVER: { code: 'R12298', color: 'silver' }
};

// Helper function to get component - just return the component as is
function getComponent(componentKey) {
    return RENDL_COMPONENTS[componentKey];
}

// Helper function to get the correct track component based on color
function getTrackComponent(length, color) {
    const colorUpper = color.toUpperCase();
    if (length === 1) {
        return getComponent(`TRACK_1M_${colorUpper}`);
    } else if (length === 2) {
        return getComponent(`TRACK_2M_${colorUpper}`);
    }
    return null;
}

// Helper function to get the correct suspension kit based on color
function getSuspensionKit(color) {
    const colorUpper = color.toUpperCase();
    return getComponent(`SUSPENSION_KIT_${colorUpper}`);
}

// Helper functions for all connector types (now with color support)
function getStraightConnector(color = 'white') {
    const colorUpper = color.toUpperCase();
    return getComponent(`STRAIGHT_CONNECTOR_${colorUpper}`);
}

function getLConnectorInner(color = 'white') {
    const colorUpper = color.toUpperCase();
    return getComponent(`L_CONNECTOR_INNER_${colorUpper}`);
}

function getLConnectorOuter(color = 'white') {
    const colorUpper = color.toUpperCase();
    return getComponent(`L_CONNECTOR_OUTER_${colorUpper}`);
}

function getTConnectorLeft(color = 'white') {
    const colorUpper = color.toUpperCase();
    return getComponent(`T_CONNECTOR_LEFT_${colorUpper}`);
}

function getTConnectorRight(color = 'white') {
    const colorUpper = color.toUpperCase();
    return getComponent(`T_CONNECTOR_RIGHT_${colorUpper}`);
}

function getXConnector(color = 'white') {
    const colorUpper = color.toUpperCase();
    return getComponent(`X_CONNECTOR_${colorUpper}`);
}

function getFeedEndRight(color = 'white') {
    const colorUpper = color.toUpperCase();
    return getComponent(`FEED_END_RIGHT_${colorUpper}`);
}

function getFeedEndLeft(color = 'white') {
    const colorUpper = color.toUpperCase();
    return getComponent(`FEED_END_LEFT_${colorUpper}`);
}

function getFeedStraight(color = 'white') {
    const colorUpper = color.toUpperCase();
    return getComponent(`FEED_STRAIGHT_${colorUpper}`);
}

function getEndCap(color = 'white') {
    const colorUpper = color.toUpperCase();
    return getComponent(`END_CAP_${colorUpper}`);
}

function getFlexConnector(color = 'white') {
    const colorUpper = color.toUpperCase();
    return getComponent(`FLEX_CONNECTOR_${colorUpper}`);
}

function getAdapter(color = 'white') {
    const colorUpper = color.toUpperCase();
    return getComponent(`ADAPTER_${colorUpper}`);
}

const AVAILABLE_TRACK_LENGTHS = [2, 1];

// Helper Functions
function getMousePos(canvas, evt) {
    const rect = canvas.getBoundingClientRect();
    
    // Get mouse position relative to canvas element (in screen pixels)
    const mouseX = evt.clientX - rect.left;
    const mouseY = evt.clientY - rect.top;
    
    // Canvas logical size is always 600x600 (BASE_CANVAS_SIZE)
    // Calculate actual displayed size considering aspect ratio and object-fit: contain
    const canvasAspect = 1; // Always 1:1 square (BASE_CANVAS_SIZE x BASE_CANVAS_SIZE)
    const rectAspect = rect.width / rect.height;
    
    let displayWidth, displayHeight;
    let offsetX = 0, offsetY = 0;
    
    if (rectAspect > canvasAspect) {
        // Container is wider - letterboxing on sides
        displayHeight = rect.height;
        displayWidth = displayHeight * canvasAspect;
        offsetX = (rect.width - displayWidth) / 2;
    } else {
        // Container is taller - letterboxing on top/bottom
        displayWidth = rect.width;
        displayHeight = displayWidth / canvasAspect;
        offsetY = (rect.height - displayHeight) / 2;
    }
    
    // Adjust mouse position for letterboxing
    const adjustedMouseX = mouseX - offsetX;
    const adjustedMouseY = mouseY - offsetY;
    
    // Scale to canvas logical coordinate system (600x600)
    const scaleX = BASE_CANVAS_SIZE / displayWidth;
    const scaleY = BASE_CANVAS_SIZE / displayHeight;
    
    const canvasX = adjustedMouseX * scaleX;
    const canvasY = adjustedMouseY * scaleY;
    
    // Apply inverse zoom transformation (zoom from center)
    const centerX = BASE_CANVAS_SIZE / 2;
    const centerY = BASE_CANVAS_SIZE / 2;
    
    // Apply inverse pan transformation
    return {
        x: centerX + (canvasX - centerX) / canvasZoom - canvasPanX,
        y: centerY + (canvasY - centerY) / canvasZoom - canvasPanY
    };
}

function distance(p1, p2) {
    if (!p1 || !p2) return Infinity;
    return Math.sqrt(Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2));
}

function snapPointOrthogonal(currentPos, startPoint) {
    if (!startPoint) return currentPos;
    
    const dx = currentPos.x - startPoint.x;
    const dy = currentPos.y - startPoint.y;
    
    if (Math.abs(dx) > Math.abs(dy)) {
        return { x: currentPos.x, y: startPoint.y };
    } else {
        return { x: startPoint.x, y: currentPos.y };
    }
}

function snapToMeterIncrements(startPoint, endPoint) {
    const currentDist = distance(startPoint, endPoint);
    const currentMeters = currentDist / PIXELS_PER_METER;
    const snappedMeters = Math.round(currentMeters);
    
    if (snappedMeters > 0) {
        const targetDist = snappedMeters * PIXELS_PER_METER;
        const dx = endPoint.x - startPoint.x;
        const dy = endPoint.y - startPoint.y;
        const currentLength = Math.sqrt(dx * dx + dy * dy);
        
        if (currentLength > 0) {
            const scale = targetDist / currentLength;
            return {
                x: startPoint.x + dx * scale,
                y: startPoint.y + dy * scale
            };
        }
    }
    return endPoint;
}

function countPointConnections(pointId) {
    let count = 0;
    segments.forEach(seg => {
        if (seg.startIndex === pointId || seg.endIndex === pointId) {
            count++;
        }
    });
    return count;
}

// Smart anchor point selection for segment length editing
// Returns priority score - higher score = more important to keep fixed
function getPointAnchorPriority(pointId) {
    let priority = 0;
    
    // Priority 1: Point has power connection (outlet) - highest priority
    if (powerConnection) {
        const point = points.find(p => p.id === pointId);
        if (point && powerConnection.pointCoords) {
            const distToPower = distance(point, powerConnection.pointCoords);
            if (distToPower < 5) {
                priority += 1000; // Very high priority
            }
        }
        
        // Check if outlet is directly on this point
        if (powerConnection.targetId === pointId) {
            priority += 1000;
        }
    }
    
    // Priority 2: Number of connections (T-junction, X-junction, etc)
    const connections = countPointConnections(pointId);
    priority += connections * 100;
    
    // Priority 3: If point is an outlet location
    features.forEach(feature => {
        if (feature.type === 'outlet') {
            const point = points.find(p => p.id === pointId);
            if (point && feature.coords) {
                const distToOutlet = distance(point, feature.coords);
                if (distToOutlet < 5) {
                    priority += 500;
                }
            }
        }
    });
    
    // Priority 4: Sum of connected segment lengths (longer = more external = higher priority)
    // This helps with H-shape where center connection should move
    let totalConnectedLength = 0;
    segments.forEach(seg => {
        if (seg.startIndex === pointId || seg.endIndex === pointId) {
            const p1 = points.find(p => p.id === seg.startIndex);
            const p2 = points.find(p => p.id === seg.endIndex);
            if (p1 && p2) {
                totalConnectedLength += distance(p1, p2);
            }
        }
    });
    priority += totalConnectedLength * 0.1; // Small weight for length
    
    return priority;
}

function findNearbyEndpoint(clickPos) {
    let closestPoint = null;
    let minDist = SNAP_TOLERANCE;
    
    points.forEach(p => {
        const d = distance(clickPos, p);
        if (d < minDist) {
            const connections = countPointConnections(p.id);
            if (connections < 4) {
                minDist = d;
                closestPoint = p;
            }
        }
    });
    
    return closestPoint;
}

function pointToSegmentDistanceInfo(p, a, b) {
    const l2 = distance(a, b) ** 2;
    if (l2 === 0) return { dist: distance(p, a), t: 0, proj: {x: a.x, y: a.y} };
    
    let t = ((p.x - a.x) * (b.x - a.x) + (p.y - a.y) * (b.y - a.y)) / l2;
    const originalT = t;
    const clampedT = Math.max(0, Math.min(1, t));
    
    const projection = {
        x: a.x + clampedT * (b.x - a.x),
        y: a.y + clampedT * (b.y - a.y)
    };
    
    return {
        dist: distance(p, projection),
        t: clampedT,
        originalT: originalT,
        proj: projection
    };
}

// FIXED: Detect if two line segments intersect (for X-junction creation)
function segmentsIntersect(p1, p2, p3, p4) {
    const denominator = ((p4.y - p3.y) * (p2.x - p1.x)) - ((p4.x - p3.x) * (p2.y - p1.y));
    
    // Lines are parallel
    if (Math.abs(denominator) < FLOAT_TOLERANCE) return null;
    
    const ua = (((p4.x - p3.x) * (p1.y - p3.y)) - ((p4.y - p3.y) * (p1.x - p3.x))) / denominator;
    const ub = (((p2.x - p1.x) * (p1.y - p3.y)) - ((p2.y - p1.y) * (p1.x - p3.x))) / denominator;
    
    // Check if intersection is within both line segments
    if (ua >= 0.05 && ua <= 0.95 && ub >= 0.05 && ub <= 0.95) {
        const intersectionX = p1.x + (ua * (p2.x - p1.x));
        const intersectionY = p1.y + (ua * (p2.y - p1.y));
        
        return {
            x: intersectionX,
            y: intersectionY,
            t1: ua,  // Position along first segment (0-1)
            t2: ub   // Position along second segment (0-1)
        };
    }
    
    return null;
}

function normalize(vec) {
    const mag = Math.sqrt(vec.x * vec.x + vec.y * vec.y);
    if (mag > FLOAT_TOLERANCE) {
        vec.x /= mag;
        vec.y /= mag;
    }
    return vec;
}

function getAngleBetweenVectors(v1, v2) {
    const dot = v1.x * v2.x + v1.y * v2.y;
    const mag1 = Math.sqrt(v1.x * v1.x + v1.y * v1.y);
    const mag2 = Math.sqrt(v2.x * v2.x + v2.y * v2.y);
    
    if (mag1 < FLOAT_TOLERANCE || mag2 < FLOAT_TOLERANCE) return 0;
    
    const cosAngle = dot / (mag1 * mag2);
    const angleRad = Math.acos(Math.max(-1, Math.min(1, cosAngle)));
    return angleRad * 180 / Math.PI;
}

function isRightAngle(angleDegrees) {
    return Math.abs(angleDegrees - 90) < ANGLE_TOLERANCE;
}

function isStraightLine(angleDegrees) {
    // Check if angle is close to 180 degrees (straight line)
    return Math.abs(angleDegrees - 180) < ANGLE_TOLERANCE;
}

// Analyze T-connector to determine which segment is the "stem" (vertical part)
// Returns { baseSegments: [seg1, seg2], stemSegment: seg3, stemDirection: 'left'|'right' }
function analyzeTConnector(tPoint) {
    if (!tPoint) return null;
    
    // Find all 3 segments connected to T-point
    const connectedSegs = [];
    segments.forEach((seg, idx) => {
        if (seg.startIndex === tPoint.id || seg.endIndex === tPoint.id) {
            const otherPointId = seg.startIndex === tPoint.id ? seg.endIndex : seg.startIndex;
            const otherPoint = points.find(p => p.id === otherPointId);
            if (otherPoint) {
                connectedSegs.push({
                    segment: seg,
                    segIndex: idx,
                    otherPoint: otherPoint,
                    vector: { x: otherPoint.x - tPoint.x, y: otherPoint.y - tPoint.y }
                });
            }
        }
    });
    
    if (connectedSegs.length !== 3) return null;
    
    // Find which two segments are collinear (form the base of T)
    for (let i = 0; i < connectedSegs.length; i++) {
        for (let j = i + 1; j < connectedSegs.length; j++) {
            const v1 = connectedSegs[i].vector;
            const v2 = connectedSegs[j].vector;
            
            // Normalize vectors
            const len1 = Math.sqrt(v1.x * v1.x + v1.y * v1.y);
            const len2 = Math.sqrt(v2.x * v2.x + v2.y * v2.y);
            const n1 = { x: v1.x / len1, y: v1.y / len1 };
            const n2 = { x: v2.x / len2, y: v2.y / len2 };
            
            // Check if vectors are opposite (dot product ≈ -1)
            const dot = n1.x * n2.x + n1.y * n2.y;
            if (dot < -0.9) { // They are opposite = collinear
                // These two form the base
                const k = 3 - i - j; // The third segment
                const stemSeg = connectedSegs[k];
                
                // Normalize base direction for consistent cross product
                // Always use positive direction: right for horizontal, down for vertical
                let baseNorm = { ...n1 };
                const baseIsHorizontal = Math.abs(n1.x) > Math.abs(n1.y);
                
                if (baseIsHorizontal) {
                    // Horizontal base: always point right (positive x)
                    if (baseNorm.x < 0) {
                        baseNorm.x = -baseNorm.x;
                        baseNorm.y = -baseNorm.y;
                    }
                } else {
                    // Vertical base: always point down (positive y)
                    if (baseNorm.y < 0) {
                        baseNorm.x = -baseNorm.x;
                        baseNorm.y = -baseNorm.y;
                    }
                }
                
                // CRITICAL: Ensure stem vector always points AWAY from T-point
                // stemSeg.vector is from one endpoint to another, but we need it to point
                // AWAY from tPoint (outward from T-junction)
                let stemVectorX = stemSeg.otherPoint.x - tPoint.x;
                let stemVectorY = stemSeg.otherPoint.y - tPoint.y;
                
                const stemLen = Math.sqrt(stemVectorX ** 2 + stemVectorY ** 2);
                const stemNorm = { 
                    x: stemVectorX / stemLen, 
                    y: stemVectorY / stemLen 
                };
                
                // Cross product: baseNorm × stemNorm
                // If positive: stem is on LEFT side
                // If negative: stem is on RIGHT side
                const cross = baseNorm.x * stemNorm.y - baseNorm.y * stemNorm.x;
                
                let stemDirection = cross > 0 ? 'left' : 'right';
                
                // CANVAS ABSOLUTE DIRECTIONS (for better debugging and logic)
                let baseCanvasDir, stemCanvasDir;
                if (baseIsHorizontal) {
                    baseCanvasDir = baseNorm.x > 0 ? 'RIGHT →' : 'LEFT ←';
                } else {
                    baseCanvasDir = baseNorm.y > 0 ? 'DOWN ↓' : 'UP ↑';
                }
                
                const stemIsHorizontal = Math.abs(stemNorm.x) > Math.abs(stemNorm.y);
                if (stemIsHorizontal) {
                    stemCanvasDir = stemNorm.x > 0 ? 'RIGHT →' : 'LEFT ←';
                } else {
                    stemCanvasDir = stemNorm.y > 0 ? 'DOWN ↓' : 'UP ↑';
                }
                
                // DEBUG: Log T-connector analysis
                if (!window._tConnectorDebugLogged) window._tConnectorDebugLogged = new Set();
                if (!window._tConnectorDebugLogged.has(tPoint.id)) {
                    console.log(`\n🔧 T-CONNECTOR ANALYSIS at point ${tPoint.id}:`);
                    console.log(`   T-Point: (${tPoint.x.toFixed(0)}, ${tPoint.y.toFixed(0)})`);
                    console.log(`   ═══════════════════════════════════════`);
                    console.log(`   📐 BASE: ${baseIsHorizontal ? 'HORIZONTAL' : 'VERTICAL'} pointing ${baseCanvasDir}`);
                    console.log(`      Vector: (${baseNorm.x.toFixed(2)}, ${baseNorm.y.toFixed(2)})`);
                    console.log(`      Base segments: ${connectedSegs[i].segIndex}, ${connectedSegs[j].segIndex}`);
                    console.log(`   📐 STEM: ${stemIsHorizontal ? 'HORIZONTAL' : 'VERTICAL'} pointing ${stemCanvasDir}`);
                    console.log(`      Vector (from T-point): (${stemNorm.x.toFixed(2)}, ${stemNorm.y.toFixed(2)})`);
                    console.log(`      Points to: (${stemSeg.otherPoint.x.toFixed(0)}, ${stemSeg.otherPoint.y.toFixed(0)})`);
                    console.log(`      Stem segment: ${stemSeg.segIndex}`);
                    console.log(`   ═══════════════════════════════════════`);
                    console.log(`   Cross product (base × stem): ${cross.toFixed(3)}`);
                    console.log(`   → Stem is on ${stemDirection.toUpperCase()} side of base`);
                    console.log(`   → Component: T SPOJ ${stemDirection.toUpperCase()} (${stemDirection === 'right' ? 'R12275/76/77' : 'R12272/73/74'})`);
                    window._tConnectorDebugLogged.add(tPoint.id);
                }
                
                return {
                    baseSegments: [connectedSegs[i], connectedSegs[j]],
                    stemSegment: stemSeg,
                    stemDirection: stemDirection,
                    tPoint: tPoint,
                    baseVector: baseNorm, // Normalized base direction
                    stemVector: stemNorm, // CRITICAL: Normalized stem direction (from T-point)
                    baseIsHorizontal: baseIsHorizontal,
                    stemIsHorizontal: stemIsHorizontal,
                    baseCanvasDir: baseCanvasDir, // Canvas absolute direction (RIGHT/LEFT/UP/DOWN)
                    stemCanvasDir: stemCanvasDir  // Canvas absolute direction (RIGHT/LEFT/UP/DOWN)
                };
            }
        }
    }
    
    return null;
}

// NEW SIMPLE LOGIC: Calculate line offset direction for each segment
// Returns Map<segmentIndex, offsetDirection> where offsetDirection is 1 (OUTER) or -1 (INNER)
function calculateConsistentOffsetDirections(tConnectorAnalysisParam = null) {
    const segmentDirections = new Map(); // Map<segmentIndex, 1|-1>
    
    // Step 1: Determine system type (closed loop or open system)
    const hasEndpoints = points.some(p => countPointConnections(p.id) === 1);
    const isClosedLoop = !hasEndpoints && segments.length > 0;
    
    // Step 2: Use provided T-connector analysis OR create new one
    // CRITICAL: Must use the SAME analysis to ensure consistency
    const tConnectorAnalysis = tConnectorAnalysisParam || new Map();
    if (!tConnectorAnalysisParam) {
        // Only create new analysis if not provided
        points.forEach(point => {
            if (countPointConnections(point.id) === 3) {
                const analysis = analyzeTConnector(point);
                if (analysis) {
                    tConnectorAnalysis.set(point.id, analysis);
                }
                    }
                });
            }
    
    // Step 3: Find power connection and determine initial direction
    if (!powerConnection) {
        // No power - return empty map
        return segmentDirections;
    }
    
    // Determine power segment and position
    let powerSegmentIdx = null;
    let powerPoint = null;
    let powerPosition = null; // 'start', 'end', 'middle'
    
    if (powerConnection.type === 'middle' || powerConnection.type === 'joint') {
        // Power is on a segment (middle or joint)
        powerSegmentIdx = typeof powerConnection.targetId === 'number' ? powerConnection.targetId : 
                         segments.findIndex(seg => seg.startIndex === powerConnection.targetId || seg.endIndex === powerConnection.targetId);
        powerPosition = 'middle';
    } else {
        // Power is on a point (end or corner)
        powerPoint = points.find(p => p.id === powerConnection.targetId);
        if (powerPoint) {
            const connectedSeg = segments.find(seg => 
                seg.startIndex === powerPoint.id || seg.endIndex === powerPoint.id
            );
            if (connectedSeg) {
                powerSegmentIdx = segments.indexOf(connectedSeg);
                // Determine if power is at start or end of segment
                const p1 = points.find(p => p.id === connectedSeg.startIndex);
                const p2 = points.find(p => p.id === connectedSeg.endIndex);
                if (p1 && p2) {
                    const distToStart = distance(p1, powerConnection.pointCoords);
                    const distToEnd = distance(p2, powerConnection.pointCoords);
                    powerPosition = distToStart < distToEnd ? 'start' : 'end';
                }
            }
        }
    }
    
    // Step 4: Set initial direction based on power type and system type
    if (isClosedLoop) {
        // Closed loop: calculate signed area to determine orientation
        let signedArea = 0;
        segments.forEach(seg => {
            const p1 = points.find(p => p.id === seg.startIndex);
            const p2 = points.find(p => p.id === seg.endIndex);
            if (p1 && p2) {
                signedArea += (p2.x - p1.x) * (p2.y + p1.y);
            }
        });
        
        // Calculate centroid
        let centroidX = 0, centroidY = 0, pointCount = 0;
        const uniquePoints = new Set();
        segments.forEach(seg => {
            if (!uniquePoints.has(seg.startIndex)) {
                const p = points.find(p => p.id === seg.startIndex);
                if (p) {
                    centroidX += p.x;
                    centroidY += p.y;
                    pointCount++;
                    uniquePoints.add(seg.startIndex);
                        }
                    }
                });
        centroidX /= pointCount;
        centroidY /= pointCount;
        
        // For closed loops: all segments should have SAME direction
        // For CCW (counter-clockwise): "RIGHT of vector" points INSIDE, so for inner use +1
        // For CW (clockwise): "RIGHT of vector" points OUTSIDE, so for inner use -1
        const wantInner = signedArea < 0; // CCW wants inner, CW wants outer
        
        // Set direction for all segments (except T-connector stems)
        const tConnectorStems = new Set();
        const tConnectorBases = new Set();
        tConnectorAnalysis.forEach((tAnalysis) => {
            tConnectorStems.add(tAnalysis.stemSegment.segIndex);
            tAnalysis.baseSegments.forEach(baseSeg => {
                tConnectorBases.add(baseSeg.segIndex);
            });
        });
        
        // For closed loops WITHOUT T-connectors: use EXPLICIT SIDES like T-connectors!
        // This avoids issues with drawing direction and ensures consistent visualization
        segments.forEach((seg, segIdx) => {
            // T-connector segments will be set separately
            if (tConnectorStems.has(segIdx) || tConnectorBases.has(segIdx)) {
                return; // Skip T-connector segments
            }
            
            const p1 = points.find(p => p.id === seg.startIndex);
            const p2 = points.find(p => p.id === seg.endIndex);
            if (!p1 || !p2) return;
            
            const dx = p2.x - p1.x;
            const dy = p2.y - p1.y;
            
            // Determine if segment is horizontal or vertical
            const isHorizontal = Math.abs(dx) > Math.abs(dy);
            
            // Calculate segment midpoint
            const midX = (p1.x + p2.x) / 2;
            const midY = (p1.y + p2.y) / 2;
            
            let explicitSide;
            
            if (isHorizontal) {
                // HORIZONTAL segment: lines TOP or BOTTOM
                if (wantInner) {
                    // For INNER (CCW): 
                    // - Top wall (midY < centroidY) → lines BOTTOM (down into square)
                    // - Bottom wall (midY > centroidY) → lines TOP (up into square)
                    explicitSide = midY < centroidY ? 'BOTTOM' : 'TOP';
                } else {
                    // For OUTER (CW): opposite
                    explicitSide = midY < centroidY ? 'TOP' : 'BOTTOM';
                }
            } else {
                // VERTICAL segment: lines LEFT or RIGHT
                if (wantInner) {
                    // For INNER (CCW):
                    // - Left wall (midX < centroidX) → lines RIGHT (right into square)
                    // - Right wall (midX > centroidX) → lines LEFT (left into square)
                    explicitSide = midX < centroidX ? 'RIGHT' : 'LEFT';
                } else {
                    // For OUTER (CW): opposite
                    explicitSide = midX < centroidX ? 'LEFT' : 'RIGHT';
                }
            }
            
            segmentDirections.set(segIdx, explicitSide);
        });
    } else {
        // Open system: start from power connection
        if (powerSegmentIdx !== null) {
            // Check if power segment is part of a T-connector
            const isPowerSegmentInTConnector = Array.from(tConnectorAnalysis.values()).some(tAnalysis => {
                return tAnalysis.baseSegments.some(baseSeg => baseSeg.segIndex === powerSegmentIdx) ||
                       tAnalysis.stemSegment.segIndex === powerSegmentIdx;
            });
            
            // If power is on a T-connector segment, skip initial direction setting
            // Step 5 will handle it correctly
            if (isPowerSegmentInTConnector) {
                if (!window._powerOnTSkipLogged) {
                    console.log(`⚠️ STEP 4: Power on T-connector segment ${powerSegmentIdx}. Skipping initial direction. Step 5 will handle it.`);
                    window._powerOnTSkipLogged = true;
                }
            } else if (powerConnection.type === 'end' || powerConnection.type === 'corner') {
                // Feed End: direction depends on which side the feed is on
                // For now, we'll determine this based on geometry
                // LEFT feed = -1 (INNER), RIGHT feed = +1 (OUTER)
                // This will be determined later when we know the feed type
                // For now, set a default and it will be corrected
                segmentDirections.set(powerSegmentIdx, 1); // Default to OUTER
            } else if (powerConnection.type === 'middle') {
                // Middle power: determine based on geometry (U-shape → inner)
                const powerSeg = segments[powerSegmentIdx];
                const p1 = points.find(p => p.id === powerSeg.startIndex);
                const p2 = points.find(p => p.id === powerSeg.endIndex);
                    
                if (p1 && p2) {
                    // Find endpoints to determine if U-shape
                    const endpoints = points.filter(p => countPointConnections(p.id) === 1);
                    if (endpoints.length >= 2) {
                        const endpointCentroidX = endpoints.reduce((sum, p) => sum + p.x, 0) / endpoints.length;
                        const endpointCentroidY = endpoints.reduce((sum, p) => sum + p.y, 0) / endpoints.length;
                        
                        const segmentMidX = (p1.x + p2.x) / 2;
                        const segmentMidY = (p1.y + p2.y) / 2;
                        
                    const dx = p2.x - p1.x;
                    const dy = p2.y - p1.y;
                    const len = Math.sqrt(dx * dx + dy * dy);
                    
                    if (len > 0) {
                        const perpX = dy / len;
                        const perpY = -dx / len;
                        
                            const toEndpointsX = endpointCentroidX - segmentMidX;
                            const toEndpointsY = endpointCentroidY - segmentMidY;
                            
                            const dot = perpX * toEndpointsX + perpY * toEndpointsY;
                            const initialDirection = (dot > 0) ? -1 : 1; // U-shape: lines inside
                            
                            segmentDirections.set(powerSegmentIdx, initialDirection);
                        }
                        } else {
                        segmentDirections.set(powerSegmentIdx, -1); // Default to INNER for middle power
                    }
                }
            }
        }
    }
    
    // Step 5: Set T-connector directions
    // EXPLICIT RULES based on canvas orientation:
    // 
    // Case 1: Base VERTICAL, Stem RIGHT → Base: LEFT, Stem: depends on levy/pravy
    // Case 2: Base VERTICAL, Stem LEFT  → Base: RIGHT, Stem: depends on levy/pravy
    // Case 3: Base HORIZONTAL, Stem UP    → Base: BOTTOM, Stem: depends on levy/pravy
    // Case 4: Base HORIZONTAL, Stem DOWN  → Base: TOP, Stem: depends on levy/pravy
    //
    // Offset directions:
    //   +1 = OUTER = right/down perpendicular
    //   -1 = INNER = left/up perpendicular
    
    if (!window._step5TConnectorLogged) window._step5TConnectorLogged = new Set();
    
    if (tConnectorAnalysis.size > 0 && !window._step5StartLogged) {
        console.log(`\n🔧 STEP 5: Processing ${tConnectorAnalysis.size} T-connector(s)`);
        window._step5StartLogged = true;
    }
    
    tConnectorAnalysis.forEach((tAnalysis, tPointId) => {
        const baseIsHorizontal = tAnalysis.baseIsHorizontal;
        const stemCanvasDir = tAnalysis.stemCanvasDir; // "RIGHT →", "LEFT ←", "UP ↑", "DOWN ↓"
        const stemDirection = tAnalysis.stemDirection; // "left" (levy) or "right" (pravy)
        
        // Log only once per T-connector
        const shouldLog = !window._step5TConnectorLogged.has(tPointId);
        
        if (shouldLog) {
            console.log(`\n🔍 STEP 5 DEBUG: T-connector at point ${tPointId}`);
            console.log(`   baseIsHorizontal: ${baseIsHorizontal}`);
            console.log(`   stemCanvasDir: "${stemCanvasDir}"`);
            console.log(`   stemDirection: "${stemDirection}"`);
        }
        
        let baseDir, stemDir;
        let ruleName = "";
        
        // EXPLICIT RULES FROM USER:
        // 
        // Case 1: Base VERTICAL, Stem RIGHT → Base: LEFT
        // Case 2: Base VERTICAL, Stem LEFT  → Base: RIGHT
        // Case 3: Base HORIZONTAL, Stem UP   → Base: BOTTOM
        // Case 4: Base HORIZONTAL, Stem DOWN → Base: TOP
        
        if (!baseIsHorizontal) {
            // Base VERTICAL (top to bottom)
            if (stemCanvasDir.includes('RIGHT')) {
                // Case 1: base power line to the LEFT of base
                baseDir = -1; // LEFT
                ruleName = "Case 1: VERTICAL base, RIGHT stem → Base LEFT";
            } else if (stemCanvasDir.includes('LEFT')) {
                // Case 2: base power line to the RIGHT of base
                baseDir = +1; // RIGHT
                ruleName = "Case 2: VERTICAL base, LEFT stem → Base RIGHT";
            } else {
                console.error(`❌ VERTICAL base but stem direction not recognized: "${stemCanvasDir}"`);
                baseDir = -1;
                ruleName = "ERROR: VERTICAL base, unknown stem";
            }
        } else {
            // Base HORIZONTAL (left to right)
            if (stemCanvasDir.includes('UP')) {
                // Case 3: base power line to the BOTTOM of base
                baseDir = +1; // BOTTOM
                ruleName = "Case 3: HORIZONTAL base, UP stem → Base BOTTOM";
            } else if (stemCanvasDir.includes('DOWN')) {
                // Case 4: base power line to the TOP of base
                baseDir = -1; // TOP
                ruleName = "Case 4: HORIZONTAL base, DOWN stem → Base TOP";
            } else {
                console.error(`❌ HORIZONTAL base but stem direction not recognized: "${stemCanvasDir}"`);
                baseDir = +1;
                ruleName = "ERROR: HORIZONTAL base, unknown stem";
            }
        }
        
        // ⚠️ CRITICAL: Store EXPLICIT sides (LEFT/RIGHT/TOP/BOTTOM) instead of +1/-1!
        // This is because +1/-1 depends on normalized direction, which can be wrong!
        let baseSide, stemSide;
        
        // BASE side based on baseDir
        if (!baseIsHorizontal) {
            // VERTICAL base
            baseSide = baseDir > 0 ? 'RIGHT' : 'LEFT';
        } else {
            // HORIZONTAL base
            baseSide = baseDir > 0 ? 'BOTTOM' : 'TOP';
        }
        
        // STEM side based on T-spoj type (LEVY/PRAVY) and stem direction
        // USER SCHEMA: 4 possible cases for each T-spoj type
        // 
        // PRAVY T-spoj:
        // Case 1: Base HORIZONTAL, Stem UP (vertical) → STEM PRAVY line RIGHT
        // Case 2: Base VERTICAL, Stem RIGHT (horizontal) → STEM PRAVY line BOTTOM
        // Case 3: Base HORIZONTAL, Stem DOWN (vertical) → STEM PRAVY line LEFT
        // Case 4: Base VERTICAL, Stem LEFT (horizontal) → STEM PRAVY line TOP
        //
        // LEVY T-spoj:
        // Case 1: Base HORIZONTAL, Stem UP (vertical) → STEM LEVY line LEFT
        // Case 2: Base VERTICAL, Stem RIGHT (horizontal) → STEM LEVY line TOP
        // Case 3: Base HORIZONTAL, Stem DOWN (vertical) → STEM LEVY line RIGHT
        // Case 4: Base VERTICAL, Stem LEFT (horizontal) → STEM LEVY line BOTTOM
        
        if (stemDirection === 'left') {
            // LEVY T-spoj: use explicit rules from schema
            if (stemCanvasDir.includes('UP')) {
                // Case 1: Base HORIZONTAL, Stem UP → STEM LEVY line LEFT
                stemSide = 'LEFT';
            } else if (stemCanvasDir.includes('DOWN')) {
                // Case 3: Base HORIZONTAL, Stem DOWN → STEM LEVY line RIGHT
                stemSide = 'RIGHT';
            } else if (stemCanvasDir.includes('RIGHT')) {
                // Case 2: Base VERTICAL, Stem RIGHT → STEM LEVY line TOP
                stemSide = 'TOP';
            } else if (stemCanvasDir.includes('LEFT')) {
                // Case 4: Base VERTICAL, Stem LEFT → STEM LEVY line BOTTOM
                stemSide = 'BOTTOM';
            } else {
                // Fallback
                stemSide = 'LEFT';
            }
        } else {
            // PRAVY T-spoj: use explicit rules from schema
            if (stemCanvasDir.includes('UP')) {
                // Case 1: Base HORIZONTAL, Stem UP → STEM PRAVY line RIGHT
                stemSide = 'RIGHT';
            } else if (stemCanvasDir.includes('DOWN')) {
                // Case 3: Base HORIZONTAL, Stem DOWN → STEM PRAVY line LEFT
                stemSide = 'LEFT';
            } else if (stemCanvasDir.includes('RIGHT')) {
                // Case 2: Base VERTICAL, Stem RIGHT → STEM PRAVY line BOTTOM
                stemSide = 'BOTTOM';
            } else if (stemCanvasDir.includes('LEFT')) {
                // Case 4: Base VERTICAL, Stem LEFT → STEM PRAVY line TOP
                stemSide = 'TOP';
            } else {
                // Fallback
                stemSide = 'LEFT';
            }
        }
        
        if (shouldLog) {
            console.log(`   → Matched rule: "${ruleName}"`);
            console.log(`   → stemDirection (levy/pravy): "${stemDirection}"`);
            console.log(`   → baseDir: ${baseDir} → baseSide: "${baseSide}"`);
            console.log(`   → stemDir: ${stemDir} → stemSide: "${stemSide}"`);
        }
        
        // Apply EXPLICIT SIDES to segments
        tAnalysis.baseSegments.forEach(baseSeg => {
            segmentDirections.set(baseSeg.segIndex, baseSide);
            if (shouldLog) {
                console.log(`      ✓ Setting base segment ${baseSeg.segIndex} direction = "${baseSide}"`);
            }
        });
        
        segmentDirections.set(tAnalysis.stemSegment.segIndex, stemSide);
        if (shouldLog) {
            console.log(`      ✓ Setting stem segment ${tAnalysis.stemSegment.segIndex} direction = "${stemSide}"`);
        }
        
        // For logging display
        let baseSideName = baseSide;
        let stemSideName = stemSide;
        
        // Log T-connector setup (only once)
        if (shouldLog) {
            console.log(`\n✅ STEP 5 RESULT: T-connector at point ${tPointId} (${stemDirection.toUpperCase()} T-spoj)`);
            console.log(`   ════════════════════════════════════════════════════════════`);
            console.log(`   📐 BASE: ${baseIsHorizontal ? 'HORIZONTAL' : 'VERTICAL'} pointing ${tAnalysis.baseCanvasDir}`);
            console.log(`   📐 STEM: ${tAnalysis.stemIsHorizontal ? 'HORIZONTAL' : 'VERTICAL'} pointing ${stemCanvasDir}`);
            console.log(`   ────────────────────────────────────────────────────────────`);
            console.log(`   🎯 RULE: ${ruleName}`);
            console.log(`   🔑 T-spoj type: ${stemDirection === 'left' ? 'LEVY (LEFT)' : 'PRAVY (RIGHT)'}`);
            console.log(`   ────────────────────────────────────────────────────────────`);
            console.log(`   🔵 BASE lines (segments ${tAnalysis.baseSegments.map(s => s.segIndex).join(', ')}): ${baseSideName} (${baseDir > 0 ? '+1' : '-1'})`);
            console.log(`   🔴 STEM line (segment ${tAnalysis.stemSegment.segIndex}): ${stemSideName} (${stemDir > 0 ? '+1' : '-1'})`);
            console.log(`   ════════════════════════════════════════════════════════════`);
            
            window._step5TConnectorLogged.add(tPointId);
        }
    });
    
    // Create a set of segment indices that belong to T-connectors (PROTECTED from BFS)
    const tConnectorSegments = new Set();
    tConnectorAnalysis.forEach((tAnalysis) => {
            tAnalysis.baseSegments.forEach(baseSeg => {
            tConnectorSegments.add(baseSeg.segIndex);
        });
        tConnectorSegments.add(tAnalysis.stemSegment.segIndex);
    });
    
    // Step 6: BFS propagation from power/T-connectors
    // CRITICAL: T-connector segments are starting points for propagation
    // After Step 5, ALL T-connector segments have directions set
    // These must be in the queue to propagate to adjacent segments
    
    if (!window._step6BFSLogged) {
        console.log(`\n🔧 STEP 6: BFS propagation (protecting T-connector segments)`);
        console.log(`   Segments with directions BEFORE BFS: ${segmentDirections.size}`);
        tConnectorAnalysis.forEach((tAnalysis, tPointId) => {
            console.log(`   T-connector ${tPointId}: base[${tAnalysis.baseSegments.map(b => b.segIndex).join(',')}] stem[${tAnalysis.stemSegment.segIndex}]`);
        });
        window._step6BFSLogged = true;
    }
    
    const visited = new Set(segmentDirections.keys());
    const queue = Array.from(segmentDirections.keys());
    
    // Ensure T-connector segments are marked as visited (they should already be)
    // This prevents them from being overwritten by BFS
    tConnectorSegments.forEach(segIdx => {
        if (segmentDirections.has(segIdx)) {
            visited.add(segIdx);
            // Make sure they're in queue to propagate from
            if (!queue.includes(segIdx)) {
                queue.push(segIdx);
            }
        }
    });
        
        while (queue.length > 0) {
            const currentSegIdx = queue.shift();
            const currentDir = segmentDirections.get(currentSegIdx);
            if (currentDir === undefined) continue;
            
            const currentSeg = segments[currentSegIdx];
            const currentP1 = points.find(p => p.id === currentSeg.startIndex);
            const currentP2 = points.find(p => p.id === currentSeg.endIndex);
            if (!currentP1 || !currentP2) continue;
            
        // Check both endpoints
            [currentSeg.startIndex, currentSeg.endIndex].forEach(pointId => {
                const connectionCount = countPointConnections(pointId);
                
            // Only propagate through L-corners (2 connections)
            if (connectionCount !== 2) return;
                
            // Check if this is a T-connector point (should not propagate through T)
            if (tConnectorAnalysis.has(pointId)) return;
            
            // Find the other segment at this corner
                segments.forEach((otherSeg, otherIdx) => {
                    if (otherIdx === currentSegIdx) return;
                    if (visited.has(otherIdx)) return;
                    
                // CRITICAL: Don't overwrite T-connector segment directions!
                if (tConnectorSegments.has(otherIdx)) return;
                
                // Check if this segment shares the corner point
                if (otherSeg.startIndex === pointId || otherSeg.endIndex === pointId) {
                    // L-corner: direction stays the same (both lines on same side)
                    const oldDir = segmentDirections.get(otherIdx);
                    if (oldDir !== undefined && oldDir !== currentDir) {
                        console.warn(`⚠️ BFS: Overwriting segment ${otherIdx} direction from ${oldDir} to ${currentDir} at point ${pointId}!`);
                        console.warn(`   This should NOT happen for T-connector segments!`);
                    }
                    segmentDirections.set(otherIdx, currentDir);
                    visited.add(otherIdx);
                    queue.push(otherIdx);
                }
                });
            });
        }
    
    // Step 6.5: VALIDATION - Check T-connector base segments have same direction
    if (!window._step6_5ValidationLogged && tConnectorAnalysis.size > 0) {
        console.log(`\n🔍 STEP 6.5: Validating T-connector directions AFTER BFS`);
        window._step6_5ValidationLogged = true;
    }
    
    tConnectorAnalysis.forEach((tAnalysis, tPointId) => {
        const base1Idx = tAnalysis.baseSegments[0].segIndex;
        const base2Idx = tAnalysis.baseSegments[1].segIndex;
        const stemIdx = tAnalysis.stemSegment.segIndex;
        
        const base1Dir = segmentDirections.get(base1Idx);
        const base2Dir = segmentDirections.get(base2Idx);
        const stemDirFinal = segmentDirections.get(stemIdx);
        
        if (!window._step6_5TConnectorLogged) window._step6_5TConnectorLogged = new Set();
        const shouldLogValidation = !window._step6_5TConnectorLogged.has(tPointId);
        
        if (shouldLogValidation) {
            console.log(`   T-connector at point ${tPointId}:`);
            console.log(`      Base segment ${base1Idx}: direction = ${base1Dir}`);
            console.log(`      Base segment ${base2Idx}: direction = ${base2Dir}`);
            console.log(`      Stem segment ${stemIdx}: direction = ${stemDirFinal}`);
        }
        
        if (base1Dir !== base2Dir) {
            console.error(`   ❌ ERROR: Base segments have DIFFERENT directions!`);
            console.error(`      This should NEVER happen! BFS overwrote one of them!`);
            console.error(`      FIXING: Setting both to ${base1Dir}`);
            // Force both to the same direction
            segmentDirections.set(base2Idx, base1Dir);
        } else {
            if (shouldLogValidation) {
                console.log(`      ✅ Base segments have SAME direction: ${base1Dir}`);
                window._step6_5TConnectorLogged.add(tPointId);
            }
        }
    });
        
    // Step 7: Fill any remaining segments (should not happen, but safety check)
        segments.forEach((seg, segIdx) => {
            if (!segmentDirections.has(segIdx)) {
            // Try to infer from neighbors
                const adjacentDirs = [];
                segments.forEach((otherSeg, otherIdx) => {
                    if (otherIdx === segIdx) return;
                    if (!segmentDirections.has(otherIdx)) return;
                    
                    if (seg.startIndex === otherSeg.startIndex || seg.startIndex === otherSeg.endIndex ||
                        seg.endIndex === otherSeg.startIndex || seg.endIndex === otherSeg.endIndex) {
                        adjacentDirs.push(segmentDirections.get(otherIdx));
                    }
                });
                
                if (adjacentDirs.length > 0) {
                    const firstDir = adjacentDirs[0];
                    const allSame = adjacentDirs.every(d => d === firstDir);
                    if (allSame) {
                        segmentDirections.set(segIdx, firstDir);
                } else {
                    // Default to +1 (OUTER) if can't determine
                    segmentDirections.set(segIdx, 1);
                    }
            } else {
                // No neighbors - default to +1 (OUTER)
                segmentDirections.set(segIdx, 1);
                }
            }
        });
    
    return segmentDirections;
}

// FIXED: Calculate segment joint positions correctly for ALL arrangements
function calculateSegmentJointPositions(segmentIndex) {
    const segment = segments[segmentIndex];
    if (!segment) return [];
    
    const p1 = points.find(p => p.id === segment.startIndex);
    const p2 = points.find(p => p.id === segment.endIndex);
    if (!p1 || !p2) return [];
    
    const lengthMeters = segment.desiredLengthMeters !== null ? 
        segment.desiredLengthMeters :
        Math.sqrt(Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2)) / PIXELS_PER_METER;
    
    const segmentPiecesInfo = calculateSegmentPieces(lengthMeters, AVAILABLE_TRACK_LENGTHS);
    
    if (segmentPiecesInfo.joints === 0) return [];
    
    const dx = p2.x - p1.x;
    const dy = p2.y - p1.y;
    const segLenPixels = distance(p1, p2);
    
    if (segLenPixels <= FLOAT_TOLERANCE) return [];
    
    const unit_dx = dx / segLenPixels;
    const unit_dy = dy / segLenPixels;
    
    const jointPositions = [];
    
    // Check if power is on this segment
    let powerAtJoint = null;
    if (powerConnection && powerConnection.targetId === segmentIndex) {
        if (powerConnection.type === 'middle' || powerConnection.type === 'joint') {
            const powerDist = distance(p1, powerConnection.pointCoords);
            powerAtJoint = powerDist;
        }
    } else if (powerConnection && (powerConnection.type === 'corner' || powerConnection.type === 'end')) {
        if (segment.startIndex === powerConnection.targetId) {
            powerAtJoint = 0;
        } else if (segment.endIndex === powerConnection.targetId) {
            powerAtJoint = segLenPixels;
        }
    }
    
    // FIXED: For segments with cuts, determine the active arrangement based on power position
    if (segmentPiecesInfo.cutNeeded) {
        const cutLength = segmentPiecesInfo.cutNeeded.length;
        const cutDistPixels = cutLength * PIXELS_PER_METER;
        const fullPiecesLength = lengthMeters - cutLength;
        const fullPiecesDistPixels = fullPiecesLength * PIXELS_PER_METER;
        
        // Determine which arrangement is active
        let useAlternativeArrangement = false;
        
        // If power is near the cut position, use alternative arrangement (cut first)
        if (powerAtJoint !== null && Math.abs(powerAtJoint - cutDistPixels) < JOINT_SNAP_TOLERANCE) {
            useAlternativeArrangement = true;
        }
        
        if (useAlternativeArrangement) {
            // Alternative: cut piece first, then full pieces
            jointPositions.push({
                x: p1.x + unit_dx * cutDistPixels,
                y: p1.y + unit_dy * cutDistPixels,
                distanceFromStart: cutDistPixels,
                type: 'natural',
                isPowerPoint: powerAtJoint !== null && Math.abs(powerAtJoint - cutDistPixels) < JOINT_SNAP_TOLERANCE
            });
        } else {
            // Standard: full pieces first, then cut piece
            let currentDistPixels = 0;
            
            for (let i = 0; i < segmentPiecesInfo.fullPieces.twoMeter; i++) {
                currentDistPixels += AVAILABLE_TRACK_LENGTHS[0] * PIXELS_PER_METER;
                if (i < segmentPiecesInfo.fullPieces.twoMeter - 1 || 
                    segmentPiecesInfo.fullPieces.oneMeter > 0 || 
                    segmentPiecesInfo.cutNeeded) {
                    
                    jointPositions.push({
                        x: p1.x + unit_dx * currentDistPixels,
                        y: p1.y + unit_dy * currentDistPixels,
                        distanceFromStart: currentDistPixels,
                        type: 'natural',
                        isPowerPoint: powerAtJoint !== null && Math.abs(currentDistPixels - powerAtJoint) < JOINT_SNAP_TOLERANCE
                    });
                }
            }
            
            for (let i = 0; i < segmentPiecesInfo.fullPieces.oneMeter; i++) {
                currentDistPixels += AVAILABLE_TRACK_LENGTHS[1] * PIXELS_PER_METER;
                if (i < segmentPiecesInfo.fullPieces.oneMeter - 1 || 
                    segmentPiecesInfo.cutNeeded) {
                    
                    jointPositions.push({
                        x: p1.x + unit_dx * currentDistPixels,
                        y: p1.y + unit_dy * currentDistPixels,
                        distanceFromStart: currentDistPixels,
                        type: 'natural',
                        isPowerPoint: powerAtJoint !== null && Math.abs(currentDistPixels - powerAtJoint) < JOINT_SNAP_TOLERANCE
                    });
                }
            }
        }
    }
    // 3m segments: show the correct arrangement based on power position
    else if (Math.abs(lengthMeters - 3.0) < 0.05) {
        const oneMeterDist = AVAILABLE_TRACK_LENGTHS[1] * PIXELS_PER_METER;
        const twoMeterDist = AVAILABLE_TRACK_LENGTHS[0] * PIXELS_PER_METER;
        
        if (powerAtJoint !== null) {
            if (Math.abs(powerAtJoint - oneMeterDist) < JOINT_SNAP_TOLERANCE) {
                // Power at 1m: use 1m + 2m
                jointPositions.push({
                    x: p1.x + unit_dx * oneMeterDist,
                    y: p1.y + unit_dy * oneMeterDist,
                    distanceFromStart: oneMeterDist,
                    type: 'natural',
                    isPowerPoint: true
                });
            } else if (Math.abs(powerAtJoint - twoMeterDist) < JOINT_SNAP_TOLERANCE) {
                // Power at 2m: use 2m + 1m (preferred)
                jointPositions.push({
                    x: p1.x + unit_dx * twoMeterDist,
                    y: p1.y + unit_dy * twoMeterDist,
                    distanceFromStart: twoMeterDist,
                    type: 'natural',
                    isPowerPoint: true
                });
            } else {
                // Default to 2m + 1m
                jointPositions.push({
                    x: p1.x + unit_dx * twoMeterDist,
                    y: p1.y + unit_dy * twoMeterDist,
                    distanceFromStart: twoMeterDist,
                    type: 'natural',
                    isPowerPoint: false
                });
            }
        } else {
            // No power: default to 2m + 1m
            jointPositions.push({
                x: p1.x + unit_dx * twoMeterDist,
                y: p1.y + unit_dy * twoMeterDist,
                distanceFromStart: twoMeterDist,
                type: 'natural',
                isPowerPoint: false
            });
        }
    }
    // Normal segments without cuts
    else {
        let currentDistPixels = 0;
        
        for (let i = 0; i < segmentPiecesInfo.fullPieces.twoMeter; i++) {
            currentDistPixels += AVAILABLE_TRACK_LENGTHS[0] * PIXELS_PER_METER;
            if (i < segmentPiecesInfo.fullPieces.twoMeter - 1 || 
                segmentPiecesInfo.fullPieces.oneMeter > 0) {
                
                jointPositions.push({
                    x: p1.x + unit_dx * currentDistPixels,
                    y: p1.y + unit_dy * currentDistPixels,
                    distanceFromStart: currentDistPixels,
                    type: 'natural',
                    isPowerPoint: powerAtJoint !== null && Math.abs(currentDistPixels - powerAtJoint) < JOINT_SNAP_TOLERANCE
                });
            }
        }
        
        for (let i = 0; i < segmentPiecesInfo.fullPieces.oneMeter; i++) {
            currentDistPixels += AVAILABLE_TRACK_LENGTHS[1] * PIXELS_PER_METER;
            if (i < segmentPiecesInfo.fullPieces.oneMeter - 1) {
                
                jointPositions.push({
                    x: p1.x + unit_dx * currentDistPixels,
                    y: p1.y + unit_dy * currentDistPixels,
                    distanceFromStart: currentDistPixels,
                    type: 'natural',
                    isPowerPoint: powerAtJoint !== null && Math.abs(currentDistPixels - powerAtJoint) < JOINT_SNAP_TOLERANCE
                });
            }
        }
    }
    
    return jointPositions;
}

function updateAllSegmentJoints() {
    segmentJointPositions.clear();
    segments.forEach((segment, index) => {
        const joints = calculateSegmentJointPositions(index);
        if (joints.length > 0) {
            segmentJointPositions.set(index, joints);
        }
    });
}

// FIXED: Draw track piece boundaries showing individual physical track piece lengths
function drawTrackPieceBoundaries(segment, segmentIndex, p1, p2) {
    if (!powerConnection) return;
    
    const dx = p2.x - p1.x;
    const dy = p2.y - p1.y;
    const segLenPixels = distance(p1, p2);
    
    if (segLenPixels <= FLOAT_TOLERANCE) return;
    
    const unit_dx = dx / segLenPixels;
    const unit_dy = dy / segLenPixels;
    
    const perp_dx = -unit_dy;
    const perp_dy = unit_dx;
    
    const lengthMeters = segment.desiredLengthMeters !== null ? 
        segment.desiredLengthMeters : segment.calcLengthMeters;
    
    // Check if power is on this segment
    const isPowerOnThisSegment = (powerConnection.type === 'middle' || powerConnection.type === 'joint') && 
                                 powerConnection.targetId === segmentIndex;
    
    // Build the track piece structure
    let trackPieces = [];
    
    if (isPowerOnThisSegment) {
        const powerDistFromStart = distance(p1, powerConnection.pointCoords);
        const powerDistMeters = powerDistFromStart / PIXELS_PER_METER;
        
        // Check if power is at a natural joint
        const joints = calculateSegmentJointPositions(segmentIndex);
        let isAtNaturalJoint = false;
        for (const joint of joints) {
            if (Math.abs(joint.distanceFromStart - powerDistFromStart) < JOINT_SNAP_TOLERANCE) {
                isAtNaturalJoint = true;
                break;
            }
        }
        
        if (isAtNaturalJoint) {
            // Power at natural joint - use standard arrangement
            const segmentPiecesInfo = calculateSegmentPieces(lengthMeters, AVAILABLE_TRACK_LENGTHS);
            
            // Add 2m pieces
            for (let i = 0; i < segmentPiecesInfo.fullPieces.twoMeter; i++) {
                trackPieces.push(2.0);
            }
            // Add 1m pieces
            for (let i = 0; i < segmentPiecesInfo.fullPieces.oneMeter; i++) {
                trackPieces.push(1.0);
            }
            // Add cut piece if needed
            if (segmentPiecesInfo.cutNeeded) {
                trackPieces.push(segmentPiecesInfo.cutNeeded.length);
            }
        } else {
            // Power in middle - split into left and right sides
            const leftSideMeters = powerDistMeters;
            const rightSideMeters = lengthMeters - powerDistMeters;
            
            // Calculate left side pieces using proper logic
            if (leftSideMeters > FLOAT_TOLERANCE) {
                const leftPiecesInfo = calculateSegmentPieces(leftSideMeters, AVAILABLE_TRACK_LENGTHS);
                // Add 2m pieces first
                for (let i = 0; i < leftPiecesInfo.fullPieces.twoMeter; i++) {
                    trackPieces.push(2.0);
                }
                // Add 1m pieces
                for (let i = 0; i < leftPiecesInfo.fullPieces.oneMeter; i++) {
                    trackPieces.push(1.0);
                }
                // Add cut piece if needed
                if (leftPiecesInfo.cutNeeded) {
                    trackPieces.push(leftPiecesInfo.cutNeeded.length);
                }
            }
            
            // Calculate right side pieces using proper logic
            if (rightSideMeters > FLOAT_TOLERANCE) {
                const rightPiecesInfo = calculateSegmentPieces(rightSideMeters, AVAILABLE_TRACK_LENGTHS);
                // Add 2m pieces first
                for (let i = 0; i < rightPiecesInfo.fullPieces.twoMeter; i++) {
                    trackPieces.push(2.0);
                }
                // Add 1m pieces
                for (let i = 0; i < rightPiecesInfo.fullPieces.oneMeter; i++) {
                    trackPieces.push(1.0);
                }
                // Add cut piece if needed
                if (rightPiecesInfo.cutNeeded) {
                    trackPieces.push(rightPiecesInfo.cutNeeded.length);
                }
            }
        }
    } else {
        // No power on this segment - standard arrangement
        const segmentPiecesInfo = calculateSegmentPieces(lengthMeters, AVAILABLE_TRACK_LENGTHS);
        
        // Add 2m pieces
        for (let i = 0; i < segmentPiecesInfo.fullPieces.twoMeter; i++) {
            trackPieces.push(2.0);
        }
        // Add 1m pieces
        for (let i = 0; i < segmentPiecesInfo.fullPieces.oneMeter; i++) {
            trackPieces.push(1.0);
        }
        // Add cut piece if needed
        if (segmentPiecesInfo.cutNeeded) {
            trackPieces.push(segmentPiecesInfo.cutNeeded.length);
        }
    }
    
    // Filter out any zero-length pieces
    trackPieces = trackPieces.filter(piece => piece > FLOAT_TOLERANCE);
    
    // If no pieces, don't draw anything
    if (trackPieces.length === 0) return;
    
    // Check for nearby segments to avoid crowding
    let nearbySegmentCount = 0;
    const proximityThreshold = 100;
    const midX = (p1.x + p2.x) / 2;
    const midY = (p1.y + p2.y) / 2;
    
    let atJunction = false;
    [segment.startIndex, segment.endIndex].forEach(pointId => {
        const connections = countPointConnections(pointId);
        if (connections >= 3) {
            atJunction = true;
        }
    });
    
    segments.forEach((seg, idx) => {
        if (idx === segmentIndex) return;
        const sp1 = points.find(p => p.id === seg.startIndex);
        const sp2 = points.find(p => p.id === seg.endIndex);
        if (!sp1 || !sp2) return;
        const sMidX = (sp1.x + sp2.x) / 2;
        const sMidY = (sp1.y + sp2.y) / 2;
        const dist = Math.sqrt(Math.pow(midX - sMidX, 2) + Math.pow(midY - sMidY, 2));
        if (dist < proximityThreshold) {
            nearbySegmentCount++;
        }
    });
    
    const canvasHeight = BASE_CANVAS_SIZE;
    const canvasWidth = BASE_CANVAS_SIZE;
    
    let baseDimensionOffset = 50;
    if (atJunction) baseDimensionOffset = 80;
    else if (nearbySegmentCount >= 2) baseDimensionOffset = 70;
    
    let dimensionOffset;
    const isHorizontal = Math.abs(dx) > Math.abs(dy) * 1.5;
    const isVertical = Math.abs(dy) > Math.abs(dx) * 1.5;
    
    if (isHorizontal) {
        dimensionOffset = midY > canvasHeight / 2 ? baseDimensionOffset : -baseDimensionOffset;
    } else if (isVertical) {
        dimensionOffset = midX < canvasWidth / 2 ? -baseDimensionOffset : baseDimensionOffset;
    } else {
        dimensionOffset = midY > canvasHeight / 2 ? baseDimensionOffset : -baseDimensionOffset;
    }
    
    function drawArrow(fromX, fromY, toX, toY) {
        const angle = Math.atan2(toY - fromY, toX - fromX);
        const arrowSize = 5;
        ctx.beginPath();
        ctx.moveTo(toX, toY);
        ctx.lineTo(toX - arrowSize * Math.cos(angle - Math.PI/6),
                  toY - arrowSize * Math.sin(angle - Math.PI/6));
        ctx.moveTo(toX, toY);
        ctx.lineTo(toX - arrowSize * Math.cos(angle + Math.PI/6),
                  toY - arrowSize * Math.sin(angle + Math.PI/6));
        ctx.stroke();
    }
    
    // Calculate joint positions based on track pieces
    const jointPositions = [];
    let currentDistMeters = 0;
    let isPowerJoint = false;
    
    for (let i = 0; i < trackPieces.length - 1; i++) {
        currentDistMeters += trackPieces[i];
        const currentDistPixels = currentDistMeters * PIXELS_PER_METER;
        
        // Check if this joint is the power point
        if (isPowerOnThisSegment) {
            const powerDistFromStart = distance(p1, powerConnection.pointCoords);
            isPowerJoint = Math.abs(currentDistPixels - powerDistFromStart) < JOINT_SNAP_TOLERANCE;
        }
        
        jointPositions.push({
            x: p1.x + unit_dx * currentDistPixels,
            y: p1.y + unit_dy * currentDistPixels,
            distanceFromStart: currentDistPixels,
            isPowerPoint: isPowerJoint
        });
    }
    
    // Draw extension lines to dimension line
    ctx.strokeStyle = 'rgba(42, 127, 98, 0.5)';
    ctx.lineWidth = 1;
    ctx.setLineDash([2, 2]);
    ctx.beginPath();
    ctx.moveTo(p1.x, p1.y);
    ctx.lineTo(p1.x + perp_dx * dimensionOffset, p1.y + perp_dy * dimensionOffset);
    ctx.moveTo(p2.x, p2.y);
    ctx.lineTo(p2.x + perp_dx * dimensionOffset, p2.y + perp_dy * dimensionOffset);
    ctx.stroke();
    ctx.setLineDash([]);
    
    // Draw joint cutting lines
    jointPositions.forEach(joint => {
        ctx.strokeStyle = joint.isPowerPoint ? 'rgba(42, 127, 98, 0.5)' : '#B0B0B0';
        ctx.lineWidth = joint.isPowerPoint ? 2 : 1.5;
        ctx.setLineDash([4, 4]);
        
        const boundaryLength = 15;
        ctx.beginPath();
        ctx.moveTo(joint.x - perp_dx * boundaryLength, 
                  joint.y - perp_dy * boundaryLength);
        ctx.lineTo(joint.x + perp_dx * boundaryLength, 
                  joint.y + perp_dy * boundaryLength);
        ctx.stroke();
        ctx.setLineDash([]);
        
        // Extension line to dimension
        ctx.setLineDash([2, 2]);
        ctx.strokeStyle = 'rgba(42, 127, 98, 0.5)';
        ctx.beginPath();
        ctx.moveTo(joint.x, joint.y);
        ctx.lineTo(joint.x + perp_dx * dimensionOffset, joint.y + perp_dy * dimensionOffset);
        ctx.stroke();
        ctx.setLineDash([]);
    });
    
    // Draw dimension lines for each track piece
    let currentDistPixels = 0;
    for (let i = 0; i < trackPieces.length; i++) {
        const pieceLength = trackPieces[i];
        const nextDistPixels = currentDistPixels + pieceLength * PIXELS_PER_METER;
        
        const startPoint = {
            x: p1.x + unit_dx * currentDistPixels,
            y: p1.y + unit_dy * currentDistPixels
        };
        const endPoint = {
            x: p1.x + unit_dx * nextDistPixels,
            y: p1.y + unit_dy * nextDistPixels
        };
        
        const dimStartPoint = {
            x: startPoint.x + perp_dx * dimensionOffset,
            y: startPoint.y + perp_dy * dimensionOffset
        };
        const dimEndPoint = {
            x: endPoint.x + perp_dx * dimensionOffset,
            y: endPoint.y + perp_dy * dimensionOffset
        };
        
        // Draw dimension line
        ctx.strokeStyle = 'rgba(42, 127, 98, 0.5)';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(dimStartPoint.x, dimStartPoint.y);
        ctx.lineTo(dimEndPoint.x, dimEndPoint.y);
        ctx.stroke();
        
        // Draw arrows
        drawArrow(dimEndPoint.x, dimEndPoint.y, dimStartPoint.x, dimStartPoint.y);
        drawArrow(dimStartPoint.x, dimStartPoint.y, dimEndPoint.x, dimEndPoint.y);
        
        // Draw text label
        const midPoint = {
            x: (dimStartPoint.x + dimEndPoint.x) / 2,
            y: (dimStartPoint.y + dimEndPoint.y) / 2
        };
        
        const text = `${pieceLength.toFixed(1)}m`;
        
        ctx.fillStyle = 'rgba(42, 127, 98, 0.5)';
        ctx.font = 'bold 11px sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        
        const metrics = ctx.measureText(text);
        const padding = 3;
        
        ctx.fillStyle = 'rgba(255, 255, 255, 0.95)';
        ctx.fillRect(
            midPoint.x - metrics.width/2 - padding,
            midPoint.y - 6 - padding,
            metrics.width + padding * 2,
            12 + padding * 2
        );
        
        ctx.fillStyle = 'rgba(42, 127, 98, 0.5)';
        ctx.fillText(text, midPoint.x, midPoint.y);
        
        currentDistPixels = nextDistPixels;
    }
}

// Canvas Resize Function
let canvasScale = 1;

function resizeCanvas() {
    // Simple approach - always use BASE_CANVAS_SIZE (600x600) internal resolution
    canvas.width = BASE_CANVAS_SIZE;
    canvas.height = BASE_CANVAS_SIZE;
    
    // Enable smoothing for better quality
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    
    drawAll();
}

// Calculate guide lines for alignment during dragging
function calculateGuideLines(draggedPoint) {
    const GUIDE_TOLERANCE = 5; // pixels
    const guides = [];
    
    // Check alignment with all other points
    points.forEach(point => {
        // Skip the dragged point itself
        if (point.id === draggedPoint.id) return;
        
        // Check horizontal alignment (same Y)
        if (Math.abs(draggedPoint.y - point.y) < GUIDE_TOLERANCE) {
            guides.push({
                type: 'horizontal',
                y: point.y,
                x1: Math.min(draggedPoint.x, point.x) - 50,
                x2: Math.max(draggedPoint.x, point.x) + 50
            });
        }
        
        // Check vertical alignment (same X)
        if (Math.abs(draggedPoint.x - point.x) < GUIDE_TOLERANCE) {
            guides.push({
                type: 'vertical',
                x: point.x,
                y1: Math.min(draggedPoint.y, point.y) - 50,
                y2: Math.max(draggedPoint.y, point.y) + 50
            });
        }
    });
    
    return guides;
}

// Draw guide lines for alignment
function drawGuideLines() {
    if (activeGuideLines.length === 0) return;
    
    ctx.strokeStyle = '#4A90E2';
    ctx.lineWidth = 1 / canvasZoom;
    ctx.setLineDash([5 / canvasZoom, 5 / canvasZoom]);
    
    activeGuideLines.forEach(guide => {
        ctx.beginPath();
        if (guide.type === 'horizontal') {
            ctx.moveTo(guide.x1, guide.y);
            ctx.lineTo(guide.x2, guide.y);
        } else if (guide.type === 'vertical') {
            ctx.moveTo(guide.x, guide.y1);
            ctx.lineTo(guide.x, guide.y2);
        }
        ctx.stroke();
    });
    
    ctx.setLineDash([]); // Reset to solid line
}

// Draw snap points (available connection points) during drawing
function drawSnapPoints() {
    points.forEach(point => {
        // Skip the current drawing start point
        if (currentDrawingStart && 'id' in currentDrawingStart && point.id === currentDrawingStart.id) {
            return;
        }
        
        // Only show points that have connections (are part of the track)
        const connections = countPointConnections(point.id);
        if (connections === 0) return;
        
        // Check if this point is near the mouse
        const isNearMouse = currentDrawingEnd && distance(point, currentDrawingEnd) < SNAP_TOLERANCE;
        
        // Draw snap point
        ctx.fillStyle = isNearMouse ? '#4A90E2' : 'rgba(150, 150, 150, 0.5)';
        ctx.strokeStyle = isNearMouse ? '#4A90E2' : 'rgba(100, 100, 100, 0.7)';
        ctx.lineWidth = 1.5 / canvasZoom;
        
        ctx.beginPath();
        ctx.arc(point.x, point.y, (isNearMouse ? 6 : 4) / canvasZoom, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
    });
}

// Drawing Functions
function drawAll() {
    ctx.save();
    
    // Clear canvas
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    // Apply zoom from center
    const centerX = BASE_CANVAS_SIZE / 2;
    const centerY = BASE_CANVAS_SIZE / 2;
    
    ctx.translate(centerX, centerY);
    ctx.scale(canvasZoom, canvasZoom);
    ctx.translate(-centerX, -centerY);
    
    // Apply panning
    ctx.translate(canvasPanX, canvasPanY);
    
    // Clear segment logging flags on every render
    // This ensures logs show correctly after configuration changes
    if (!window._firstRenderDone) {
        window._firstRenderDone = true;
    } else if (window._needsRevalidation) {
        window._segmentLogsShown = new Set();
        window._validationLogged = false;
        window._tConnectorDebugLogged = new Set();
        window._step5TConnectorLogged = new Set();
        window._step5StartLogged = false;
        window._powerOnTSkipLogged = false;
        window._needsRevalidation = false;
    }
    
    drawGrid();
    
    updateAllSegmentJoints();
    
    drawFeatures();
    drawExistingSegments();
    drawPowerConnection();
    drawAnimatedPowerFlow();
    
    // DEBUG: Draw segment directions and L-connector validation
    drawDebugSegmentDirections();
    
    // Show guide lines during endpoint dragging in select mode
    if (isDraggingEndpoint) {
        drawGuideLines();
    }
    
    // Show snap points during drawing
    if (isDrawing && currentDrawingStart) {
        drawSnapPoints();
    }
    
    if (isDrawing && currentDrawingStart && currentDrawingEnd) {
        const currentLengthPixels = distance(currentDrawingStart, currentDrawingEnd);
        const currentLengthMeters = currentLengthPixels / PIXELS_PER_METER;
        drawCurrentDrawingSegment(currentLengthMeters);
    }
    
    if (isConnectingPower && powerConnectionStart && powerConnectionEndCandidate) {
        drawTemporaryPowerConnection();
    }
    
    drawPoints();
    drawSnapHighlight();
    
    // Draw box selection rectangle
    if (isBoxSelecting && selectionBoxStart && selectionBoxEnd) {
        ctx.strokeStyle = '#2196F3';
        ctx.fillStyle = 'rgba(33, 150, 243, 0.1)';
        ctx.lineWidth = 2 / canvasZoom;
        ctx.setLineDash([5 / canvasZoom, 5 / canvasZoom]);
        
        const x = Math.min(selectionBoxStart.x, selectionBoxEnd.x);
        const y = Math.min(selectionBoxStart.y, selectionBoxEnd.y);
        const width = Math.abs(selectionBoxEnd.x - selectionBoxStart.x);
        const height = Math.abs(selectionBoxEnd.y - selectionBoxStart.y);
        
        ctx.fillRect(x, y, width, height);
        ctx.strokeRect(x, y, width, height);
        ctx.setLineDash([]);
    }
    
    // Draw custom cursor for connect_power tool
    if (currentTool === 'connect_power' && lastMousePos) {
        // Color depends on whether snap is available
        const hasSnap = powerGhostPreview !== null;
        const cursorColor = hasSnap ? '#FF8C00' : '#999999'; // Orange if snap, gray if not
        
        // Draw cursor circle
        ctx.beginPath();
        ctx.arc(lastMousePos.x, lastMousePos.y, OUTLET_RADIUS / canvasZoom, 0, Math.PI * 2);
        ctx.fillStyle = cursorColor;
        ctx.fill();
        
        // Draw power icon
        drawPowerIcon(lastMousePos.x, lastMousePos.y, 14 / canvasZoom, '#FFF');
    }
    
    ctx.restore();
    
    // Draw canvas orientation labels (after restore, so they're fixed to screen)
    // drawCanvasOrientationLabels(); // Disabled - keep for logic only
    
    if (is3DView) {
        update3DFromCanvas();
    }
}

// Helper function to draw power icon
function drawPowerIcon(x, y, size, color) {
    ctx.fillStyle = color;
    ctx.font = `${size}px sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('⚡︎', x, y);
}

// Draw canvas orientation labels (TOP, BOTTOM, LEFT, RIGHT)
function drawCanvasOrientationLabels() {
    const padding = 15;
    const fontSize = 14;
    
    ctx.save();
    ctx.font = `bold ${fontSize}px Montserrat, sans-serif`;
    ctx.fillStyle = '#666';
    ctx.textBaseline = 'middle';
    
    // TOP (↑)
    ctx.textAlign = 'center';
    ctx.fillText('TOP ↑', canvas.width / 2, padding);
    
    // BOTTOM (↓)
    ctx.textAlign = 'center';
    ctx.fillText('BOTTOM ↓', canvas.width / 2, canvas.height - padding);
    
    // LEFT (←)
    ctx.save();
    ctx.translate(padding, canvas.height / 2);
    ctx.rotate(-Math.PI / 2);
    ctx.textAlign = 'center';
    ctx.fillText('LEFT ←', 0, 0);
    ctx.restore();
    
    // RIGHT (→)
    ctx.save();
    ctx.translate(canvas.width - padding, canvas.height / 2);
    ctx.rotate(Math.PI / 2);
    ctx.textAlign = 'center';
    ctx.fillText('RIGHT →', 0, 0);
    ctx.restore();
    
    ctx.restore();
}

function drawGrid() {
    ctx.strokeStyle = '#EBEBEB';
    ctx.lineWidth = 0.6 / canvasZoom;
    const gridSize = PIXELS_PER_METER / 2; // Grid every 0.5m instead of 1m
    
    // Calculate visible area based on zoom and pan (infinite canvas)
    const centerX = BASE_CANVAS_SIZE / 2;
    const centerY = BASE_CANVAS_SIZE / 2;
    const visibleWidth = BASE_CANVAS_SIZE / canvasZoom;
    const visibleHeight = BASE_CANVAS_SIZE / canvasZoom;
    
    // Account for panning to extend grid infinitely
    const minX = Math.floor((centerX - visibleWidth / 2 - canvasPanX) / gridSize) * gridSize;
    const maxX = Math.ceil((centerX + visibleWidth / 2 - canvasPanX) / gridSize) * gridSize;
    const minY = Math.floor((centerY - visibleHeight / 2 - canvasPanY) / gridSize) * gridSize;
    const maxY = Math.ceil((centerY + visibleHeight / 2 - canvasPanY) / gridSize) * gridSize;
    
    // Draw vertical lines
    for (let x = minX; x <= maxX; x += gridSize) {
        ctx.beginPath();
        ctx.moveTo(x, minY);
        ctx.lineTo(x, maxY);
        ctx.stroke();
    }
    
    // Draw horizontal lines
    for (let y = minY; y <= maxY; y += gridSize) {
        ctx.beginPath();
        ctx.moveTo(minX, y);
        ctx.lineTo(maxX, y);
        ctx.stroke();
    }
    
    // Draw dots at intersections
    ctx.fillStyle = '#D8D8D8';
    const dotRadius = 1.8 / canvasZoom;
    for (let x = Math.floor(minX / (gridSize * 2)) * (gridSize * 2); x <= maxX; x += gridSize * 2) {
        for (let y = Math.floor(minY / (gridSize * 2)) * (gridSize * 2); y <= maxY; y += gridSize * 2) {
            ctx.beginPath();
            ctx.arc(x, y, dotRadius, 0, Math.PI * 2);
            ctx.fill();
        }
    }
    
    // Draw center marker
    const centerMarkX = BASE_CANVAS_SIZE / 2;
    const centerMarkY = BASE_CANVAS_SIZE / 2;
    const markerSize = 15 / canvasZoom;
    
    ctx.strokeStyle = 'rgba(33, 150, 243, 0.5)'; // 50% transparent blue
    ctx.lineWidth = 2 / canvasZoom;
    
    // Draw cross at center
    ctx.beginPath();
    ctx.moveTo(centerMarkX - markerSize, centerMarkY);
    ctx.lineTo(centerMarkX + markerSize, centerMarkY);
    ctx.moveTo(centerMarkX, centerMarkY - markerSize);
    ctx.lineTo(centerMarkX, centerMarkY + markerSize);
    ctx.stroke();
    
    // Draw small circle at center
    ctx.beginPath();
    ctx.arc(centerMarkX, centerMarkY, 4 / canvasZoom, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(33, 150, 243, 0.5)'; // 50% transparent blue
    ctx.fill();
}

function drawFeatures() {
    // Draw ghost preview when placing power outlet
    if (currentTool === 'connect_power' && powerGhostPreview) {
        ctx.globalAlpha = 0.7;
        ctx.beginPath();
        ctx.arc(powerGhostPreview.x, powerGhostPreview.y, OUTLET_RADIUS / canvasZoom, 0, Math.PI * 2);
        ctx.fillStyle = '#FF8C00'; // Orange
        ctx.fill();
        
        ctx.globalAlpha = 1.0;
        drawPowerIcon(powerGhostPreview.x, powerGhostPreview.y, 14 / canvasZoom, '#FFF');
    }
    
    features.forEach((feature) => {
        if (feature.type === 'outlet') {
            const isSelected = selectedElement?.type === 'feature' && selectedElement.id === feature.id;
            
            if (isSelected) {
                ctx.shadowColor = 'rgba(0, 0, 0, 0.2)';
                ctx.shadowBlur = 8 / canvasZoom;
            }
            
            // Pulse animation - always on
            const time = Date.now();
            const baseRadius = OUTLET_RADIUS / canvasZoom;
            
            // Draw 3 expanding pulse circles (30% smaller radius)
            for (let i = 0; i < 3; i++) {
                const delay = i * 400; // 0.4s delay between each
                const cycleTime = (time + delay) % 2000; // 2s cycle
                const progress = cycleTime / 2000;
                
                if (progress < 1) {
                    // Reduced pulse expansion: was baseRadius * 2, now baseRadius * 1.4 (30% less)
                    const pulseRadius = baseRadius + (baseRadius * 1.4 * progress);
                    const pulseAlpha = (1 - progress) * 0.5;
                    
                    ctx.globalAlpha = pulseAlpha;
                    ctx.beginPath();
                    ctx.arc(feature.x, feature.y, pulseRadius, 0, Math.PI * 2);
                    ctx.strokeStyle = '#FF8C00';
                    ctx.lineWidth = 2 / canvasZoom;
                    ctx.stroke();
                }
            }
            
            ctx.globalAlpha = 1.0;
            
            // Draw main circle
            ctx.beginPath();
            ctx.arc(feature.x, feature.y, baseRadius, 0, Math.PI * 2);
            ctx.fillStyle = isSelected ? '#CC7000' : '#FF8C00'; // Orange color
            ctx.fill();
            
            ctx.shadowBlur = 0;
            
            // Draw power icon
            drawPowerIcon(feature.x, feature.y, 14 / canvasZoom, '#FFF');
        }
    });
}

function drawExistingSegments() {
    segments.forEach((segment, index) => {
        const p1 = points.find(p => p.id === segment.startIndex);
        const p2 = points.find(p => p.id === segment.endIndex);
        if (!p1 || !p2) return;
        
        // Check if segment is being edited
        const isEditing = index === editingSegmentIndex;
        
        // Check if segment is selected (single or multi-selection)
        const isSelected = (selectedElement?.type === 'segment' && selectedElement.id === index) ||
            selectedElements.some(el => el.type === 'segment' && el.id === index);
        
        // Editing segment gets special highlight
        if (isEditing) {
            ctx.lineWidth = 7 / canvasZoom;
            ctx.strokeStyle = '#4A90E2'; // Blue highlight for editing
        } else {
            ctx.lineWidth = (isSelected ? 6 : 4) / canvasZoom;
            ctx.strokeStyle = isSelected ? '#D4A574' : '#2C2C2C';
        }
        
        ctx.lineCap = 'round';
        ctx.beginPath();
        ctx.moveTo(p1.x, p1.y);
        ctx.lineTo(p2.x, p2.y);
        ctx.stroke();
        
        if (isSelected && currentTool === 'select' && !isDraggingEndpoint) {
            ctx.fillStyle = '#D4A574';
            ctx.strokeStyle = '#FFF';
            ctx.lineWidth = 2 / canvasZoom;
            
            ctx.beginPath();
            ctx.arc(p1.x, p1.y, 8 / canvasZoom, 0, Math.PI * 2);
            ctx.fill();
            ctx.stroke();
            
            ctx.beginPath();
            ctx.arc(p2.x, p2.y, 8 / canvasZoom, 0, Math.PI * 2);
            ctx.fill();
            ctx.stroke();
        }
        
        if (powerConnection) {
            drawTrackPieceBoundaries(segment, index, p1, p2);
        }
        
        const joints = segmentJointPositions.get(index);
        if (joints && joints.length > 0) {
            joints.forEach(joint => {
                if (joint.isPowerPoint) {
                    ctx.strokeStyle = 'rgba(42, 127, 98, 0.5)';
                    ctx.lineWidth = 3 / canvasZoom;
                } else if (joint.type === 'natural') {
                    ctx.strokeStyle = '#666';
                    ctx.lineWidth = 2 / canvasZoom;
                } else {
                    return;
                }
                
                ctx.beginPath();
                const dx = p2.x - p1.x;
                const dy = p2.y - p1.y;
                const markSize = 5 / canvasZoom;
                if (Math.abs(dx) > Math.abs(dy)) {
                    ctx.moveTo(joint.x, joint.y - markSize);
                    ctx.lineTo(joint.x, joint.y + markSize);
                } else {
                    ctx.moveTo(joint.x - markSize, joint.y);
                    ctx.lineTo(joint.x + markSize, joint.y);
                }
                ctx.stroke();
            });
        }
        
        if (index !== editingSegmentIndex) {
            drawLengthLabel(segment, p1, p2, false);
        }
    });
}

function drawLengthLabel(segmentOrPos, p1, p2, isTemporary) {
    let lengthMeters;
    if (isTemporary) {
        lengthMeters = segmentOrPos;
    } else {
        lengthMeters = segmentOrPos.desiredLengthMeters !== null ? 
            segmentOrPos.desiredLengthMeters : segmentOrPos.calcLengthMeters;
    }
    
    if (lengthMeters === null || lengthMeters === undefined || 
        lengthMeters < FLOAT_TOLERANCE / PIXELS_PER_METER) return;
    
    let text;
    if (isTemporary && !isShiftPressed) {
        // Default mode: snap to 1m increments, show "snapped"
        const snappedMeters = Math.round(lengthMeters);
        const snappedText = window.configuratorTranslations?.snapped || 'snapped';
        text = snappedMeters > 0 ? `${snappedMeters}m (${snappedText})` : '0m';
    } else {
        // Free drawing mode (Shift): show precise length
        text = `${lengthMeters.toFixed(1)}m`;
    }
    
    ctx.font = `500 ${12 / canvasZoom}px sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    
    const midX = (p1.x + p2.x) / 2;
    const midY = (p1.y + p2.y) / 2;
    
    let labelX = midX;
    let labelY = midY;
    
    const textMetrics = ctx.measureText(text);
    const textWidth = textMetrics.width;
    const textHeight = 14 / canvasZoom;
    
    // FIXED: Check for nearby segments to avoid overlap
    let nearbySegmentCount = 0;
    const proximityThreshold = 80 / canvasZoom; // pixels
    
    if (!isTemporary) {
        segments.forEach((seg, idx) => {
            if (segments.indexOf(segmentOrPos) === idx) return; // Skip self
            
            const sp1 = points.find(p => p.id === seg.startIndex);
            const sp2 = points.find(p => p.id === seg.endIndex);
            if (!sp1 || !sp2) return;
            
            const sMidX = (sp1.x + sp2.x) / 2;
            const sMidY = (sp1.y + sp2.y) / 2;
            
            const dist = Math.sqrt(Math.pow(midX - sMidX, 2) + Math.pow(midY - sMidY, 2));
            if (dist < proximityThreshold) {
                nearbySegmentCount++;
            }
        });
    }
    
    // FIXED: Check if this segment has power connection (green dimensions will be on one side)
    let hasPowerOnSegment = false;
    let greenDimensionsBelow = false;
    
    if (!isTemporary && powerConnection) {
        const segmentIndex = segments.indexOf(segmentOrPos);
        if (segmentIndex >= 0) {
            if (powerConnection.targetId === segmentIndex && 
                (powerConnection.type === 'middle' || powerConnection.type === 'joint')) {
                hasPowerOnSegment = true;
                
                // Determine where green dimensions will be placed
                const canvasHeight = BASE_CANVAS_SIZE;
                
                // Green dimensions go below for segments in lower half, above for upper half
                greenDimensionsBelow = midY > canvasHeight / 2;
            }
        }
    }
    
    // FIXED: Increase offset when many segments nearby to avoid crowding
    let offset = 35 / canvasZoom;
    if (nearbySegmentCount >= 2) {
        offset = 55 / canvasZoom; // Larger offset for crowded areas
    }
    
    // FIXED: Place black labels on OPPOSITE side from green dimensions
    if (Math.abs(p1.x - p2.x) > Math.abs(p1.y - p2.y)) {
        // Horizontal segment
        if (hasPowerOnSegment) {
            // Place opposite to green dimensions
            if (greenDimensionsBelow) {
                labelY -= offset;  // Above if green is below
            } else {
                labelY += offset;  // Below if green is above
            }
        } else {
            // No power - alternate based on segment position and nearby segments
            const canvasHeight = BASE_CANVAS_SIZE;
            
            if (midY < canvasHeight / 3) {
                labelY += offset; // Below for top segments
            } else if (midY > 2 * canvasHeight / 3) {
                labelY -= offset; // Above for bottom segments
            } else {
                // Middle area - check for crowding
                labelY -= offset; // Default above
            }
        }
    } else {
        // Vertical segment
        const canvasWidth = BASE_CANVAS_SIZE;
        
        // Alternate left/right based on position
        if (midX < canvasWidth / 2) {
            labelX += offset;  // Right side for left segments
            ctx.textAlign = 'left';
        } else {
            labelX -= offset;  // Left side for right segments
            ctx.textAlign = 'right';
        }
    }
    
    if (!isTemporary) {
        const padding = 4 / canvasZoom;
        
        // Calculate correct box position based on text alignment
        let boxX;
        if (ctx.textAlign === 'center') {
            boxX = labelX - textWidth/2 - padding;
        } else if (ctx.textAlign === 'right') {
            boxX = labelX - textWidth - padding;
        } else { // 'left'
            boxX = labelX - padding;
        }
        
        const boxY = labelY - textHeight/2 - padding;
        const boxW = textWidth + padding * 2;
        const boxH = textHeight + padding * 2;
        
        ctx.fillStyle = 'rgba(255, 255, 255, 0.98)';
        ctx.fillRect(boxX, boxY, boxW, boxH);
        
        ctx.strokeStyle = '#D0D0D0';
        ctx.lineWidth = 0.25 / canvasZoom;
        ctx.strokeRect(boxX, boxY, boxW, boxH);
        
        ctx.fillStyle = '#000';
    } else {
        ctx.fillStyle = '#888';
    }
    
    ctx.fillText(text, labelX, labelY);
    
    if (!isTemporary && segments.indexOf(segmentOrPos) !== -1) {
        const padding = 3 / canvasZoom;
        
        // Calculate correct bounds based on text alignment
        let boundsX;
        if (ctx.textAlign === 'center') {
            boundsX = labelX - textWidth/2 - padding;
        } else if (ctx.textAlign === 'right') {
            boundsX = labelX - textWidth - padding;
        } else { // 'left'
            boundsX = labelX - padding;
        }
        
        const bounds = {
            x: boundsX,
            y: labelY - textHeight / 2 - padding,
            w: textWidth + padding * 2,
            h: textHeight + padding * 2
        };
        segmentOrPos.labelBounds = bounds;
    }
}

function drawCurrentDrawingSegment(currentLengthMeters) {
    if (!currentDrawingStart || !currentDrawingEnd) return;
    
    const intermediatePoints = [];
    for (const point of points) {
        if ('id' in currentDrawingStart && point.id === currentDrawingStart.id) continue;
        
        const distInfo = pointToSegmentDistanceInfo(point, currentDrawingStart, currentDrawingEnd);
        
        if (distInfo.dist < 5 && distInfo.t > 0.01 && distInfo.t < 0.99) {
            const connections = countPointConnections(point.id);
            if (connections > 0) {
                intermediatePoints.push({
                    point: point,
                    t: distInfo.t
                });
            }
        }
    }
    
    intermediatePoints.sort((a, b) => a.t - b.t);
    
    // FIXED: Check for T-junction (endpoint ON a segment) vs X-junction (crossing through)
    let tJunctionPoint = null;
    let intersectionPoint = null;
    
    // Check if endpoint is near/on an existing segment (T-junction)
    for (let i = 0; i < segments.length; i++) {
        const seg = segments[i];
        const p1 = points.find(p => p.id === seg.startIndex);
        const p2 = points.find(p => p.id === seg.endIndex);
        if (!p1 || !p2) continue;
        
        const distInfo = pointToSegmentDistanceInfo(currentDrawingEnd, p1, p2);
        
        if (distInfo.dist < SELECT_TOLERANCE && 
            distance(currentDrawingEnd, p1) > SNAP_TOLERANCE && 
            distance(currentDrawingEnd, p2) > SNAP_TOLERANCE &&
            distInfo.t > 0.1 && distInfo.t < 0.9) {
            tJunctionPoint = distInfo.proj;
            break;
        }
    }
    
    // If no T-junction, check for crossing intersection (X-junction)
    if (!tJunctionPoint) {
        for (let i = 0; i < segments.length; i++) {
            const seg = segments[i];
            const p1 = points.find(p => p.id === seg.startIndex);
            const p2 = points.find(p => p.id === seg.endIndex);
            if (!p1 || !p2) continue;
            
            const intersection = segmentsIntersect(
                currentDrawingStart,
                currentDrawingEnd,
                p1,
                p2
            );
            
            if (intersection) {
                intersectionPoint = intersection;
                break;
            }
        }
    }
    
    ctx.strokeStyle = '#666';
    ctx.lineWidth = 3 / canvasZoom;
    ctx.setLineDash([5 / canvasZoom, 5 / canvasZoom]);
    ctx.lineCap = 'round';
    
    if (intermediatePoints.length > 0) {
        let currentStart = currentDrawingStart;
        
        for (const intermediate of intermediatePoints) {
            ctx.beginPath();
            ctx.moveTo(currentStart.x, currentStart.y);
            ctx.lineTo(intermediate.point.x, intermediate.point.y);
            ctx.stroke();
            
            ctx.fillStyle = 'rgba(42, 127, 98, 0.5)';
            ctx.beginPath();
            ctx.arc(intermediate.point.x, intermediate.point.y, 6, 0, Math.PI * 2);
            ctx.fill();
            
            currentStart = intermediate.point;
        }
        
        ctx.strokeStyle = '#666';
        ctx.beginPath();
        ctx.moveTo(currentStart.x, currentStart.y);
        ctx.lineTo(currentDrawingEnd.x, currentDrawingEnd.y);
        ctx.stroke();
    } else {
        ctx.beginPath();
        ctx.moveTo(currentDrawingStart.x, currentDrawingStart.y);
        ctx.lineTo(currentDrawingEnd.x, currentDrawingEnd.y);
        ctx.stroke();
    }
    
    ctx.setLineDash([]);
    
    // FIXED: Show different previews for T-junction vs X-junction
    if (tJunctionPoint) {
        // T-junction preview (endpoint on track)
        ctx.fillStyle = 'rgba(42, 127, 98, 0.5)';
        ctx.strokeStyle = '#FFF';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(tJunctionPoint.x, tJunctionPoint.y, 7, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
    } else if (intersectionPoint) {
        // X-junction preview (crossing through)
        ctx.fillStyle = '#FF8C00';
        ctx.strokeStyle = '#FFF';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(intersectionPoint.x, intersectionPoint.y, 8, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
    }
    
    drawLengthLabel(currentLengthMeters, currentDrawingStart, currentDrawingEnd, true);
}

function drawTemporaryPowerConnection() {
    if (!powerConnectionStart || !powerConnectionEndCandidate) return;
    
    const isJointSnap = powerConnectionEndCandidate.type === 'joint';
    ctx.strokeStyle = isJointSnap ? 'rgba(42, 127, 98, 0.5)' : '#D4A574';
    ctx.lineWidth = 3 / canvasZoom;
    ctx.setLineDash([4 / canvasZoom, 4 / canvasZoom]);
    ctx.beginPath();
    ctx.moveTo(powerConnectionStart.x, powerConnectionStart.y);
    ctx.lineTo(powerConnectionEndCandidate.x, powerConnectionEndCandidate.y);
    ctx.stroke();
    ctx.setLineDash([]);
}

function drawPowerConnection() {
    if (!powerConnection) return;
    
    const outlet = features.find(f => f.id === powerConnection.outletId);
    if (!outlet) return;
    
    const isSelected = selectedElement?.type === 'power' && selectedElement.id === powerConnection.outletId;
    
    // Draw line from outlet to power connection point
    ctx.strokeStyle = isSelected ? '#D4A574' : 'rgba(42, 127, 98, 0.5)';
    ctx.lineWidth = (isSelected ? 6 : 4) / canvasZoom;
    ctx.beginPath();
    ctx.moveTo(outlet.x, outlet.y);
    ctx.lineTo(powerConnection.pointCoords.x, powerConnection.pointCoords.y);
    ctx.stroke();
    
    // Draw power connection point (green circle)
    ctx.fillStyle = isSelected ? '#D4A574' : 'rgba(42, 127, 98, 0.5)';
    ctx.beginPath();
    ctx.arc(powerConnection.pointCoords.x, powerConnection.pointCoords.y, (isSelected ? 8 : 6) / canvasZoom, 0, Math.PI*2);
    ctx.fill();
    
    // White outline for power connection point
    ctx.strokeStyle = '#FFF';
    ctx.lineWidth = 2 / canvasZoom;
    ctx.beginPath();
    ctx.arc(powerConnection.pointCoords.x, powerConnection.pointCoords.y, (isSelected ? 8 : 6) / canvasZoom, 0, Math.PI*2);
    ctx.stroke();
}

// CRITICAL: Calculate and cache segment directions ONCE after power is set
// This function should be called ONCE after powerConnection is established
// NOT every frame in drawAnimatedPowerFlow()!
function recalculatePowerLineDirections() {
    if (!powerConnection) {
        cachedSegmentOffsetDirections = null;
        cachedTConnectorAnalysis = null;
        cachedGlobalOffsetDirection = null;
        return;
    }
    
    console.log('🔄 Recalculating power line directions (ONCE)...');
    
    // Analyze T-connectors
    const tConnectorAnalysis = new Map();
    points.forEach(point => {
        if (countPointConnections(point.id) === 3) {
            const analysis = analyzeTConnector(point);
            if (analysis) {
                tConnectorAnalysis.set(point.id, analysis);
            }
        }
    });
    
    // Calculate global offset direction for closed loops
    const hasEndpoints = points.some(p => countPointConnections(p.id) === 1);
    const isClosedLoop = !hasEndpoints && segments.length > 0;
    
    let globalOffsetDirection = null;
    if (isClosedLoop) {
        // Calculate signed area and centroid
        let signedArea = 0;
        for (let i = 0; i < points.length; i++) {
            const p1 = points[i];
            const p2 = points[(i + 1) % points.length];
            signedArea += (p1.x * p2.y - p2.x * p1.y);
        }
        signedArea /= 2;
        
        let centroidX = 0, centroidY = 0, pointCount = 0;
        points.forEach(p => {
            centroidX += p.x;
            centroidY += p.y;
            pointCount++;
        });
        centroidX /= pointCount;
        centroidY /= pointCount;
        
        globalOffsetDirection = {
            centroidX: centroidX,
            centroidY: centroidY,
            wantInner: signedArea < 0
        };
        
        console.log('   🔄 Closed loop detected:', signedArea > 0 ? 'CW' : 'CCW');
        console.log('   Lines will be:', globalOffsetDirection.wantInner ? 'INSIDE' : 'OUTSIDE');
    }
    
    // Calculate segment directions
    const segmentDirections = calculateConsistentOffsetDirections(tConnectorAnalysis);
    
    // Cache results
    cachedSegmentOffsetDirections = segmentDirections;
    cachedTConnectorAnalysis = tConnectorAnalysis;
    cachedGlobalOffsetDirection = globalOffsetDirection;
    
    console.log('✅ Power line directions calculated and cached!');
    console.log('   Segments:', segmentDirections.size, '/', segments.length);
    console.log('   T-connectors:', tConnectorAnalysis.size);
}

// Draw animated dashed lines showing power flow along all track segments
function drawAnimatedPowerFlow() {
    if (!powerConnection) return;
    if (segments.length === 0) return;
    
    // Use stored coordinates of power connection point
    const powerPt = powerConnection.pointCoords;
    if (!powerPt) return;
    
    // DISABLE ALL DEBUG LOGS IN THIS FUNCTION - it's called every frame!
    // Only critical errors should be logged (and they should be protected by flags)
    const DEBUG_LOGS_ENABLED = false; // Set to true only for debugging
    
    // Debug only once (not every frame)
    if (!window._powerFlowDebugLogged) {
        console.log('🔌 Power flow animation starting from:', {
            type: powerConnection.type,
            coords: powerPt,
            targetId: powerConnection.targetId
        });
        window._powerFlowDebugLogged = true;
    }
    
    // Detect if power is on a connector that distributes power in multiple directions
    // This includes:
    // 1. T-connector (3 connections) - has built-in feed
    // 2. X-connector (4 connections) - has built-in feed  
    // 3. Straight feed joint (2 connections) - PŘÍMÝ SPOJ S NÁPÁJENÍM (R12263)
    // 4. Middle of track (type='middle') - PŘÍMÝ SPOJ S NÁPÁJENÍM (R12263)
    let powerOnMultiConnector = false;
    let neighborCentroid = null;
    let powerPoint = null;
    
    // For 'middle' type, targetId is segment index, not point ID
    if (powerConnection.type === 'middle') {
        // Power is in the middle of a segment
        powerPoint = null; // No actual point, just coordinates
    } else {
        // For other types, targetId is point ID
        powerPoint = points.find(p => p.id === powerConnection.targetId);
    }
    
    // Check if power is on T/X connector (3+ connections) OR on a straight feed joint
    const isPowerOnJoint = powerConnection.type === 'joint';
    const isPowerOnMiddle = powerConnection.type === 'middle';
    const connectionCount = powerPoint ? countPointConnections(powerPoint.id) : 0;
    
    // Power distributes in multiple directions if:
    // - On T/X connector (3+ connections)
    // - On straight feed joint (2 connections, type='joint')
    // - On middle of track (type='middle')
    if ((powerPoint && connectionCount >= 3) || 
        (isPowerOnJoint && connectionCount === 2) || 
        isPowerOnMiddle) {
        powerOnMultiConnector = true;
        const neighbors = [];
        
        if (powerPoint && !isPowerOnMiddle) {
            // Power on endpoint (T/X connector or straight joint between two segments)
            // Gather all connected segments to determine direction of power flow
            segments.forEach(seg => {
                if (seg.startIndex === powerPoint.id) {
                    const other = points.find(p => p.id === seg.endIndex);
                    if (other) neighbors.push(other);
                } else if (seg.endIndex === powerPoint.id) {
                    const other = points.find(p => p.id === seg.startIndex);
                    if (other) neighbors.push(other);
                }
            });
        } else if ((isPowerOnJoint || isPowerOnMiddle) && powerConnection.targetId !== undefined) {
            // Power on joint within a segment OR in middle of track
            // Power flows in both directions from this point
            const segment = segments[powerConnection.targetId];
            if (segment) {
                const p1 = points.find(p => p.id === segment.startIndex);
                const p2 = points.find(p => p.id === segment.endIndex);
                if (p1) neighbors.push(p1);
                if (p2) neighbors.push(p2);
            }
        }
        
        if (neighbors.length > 0) {
            const sum = neighbors.reduce((acc, p) => ({x: acc.x + p.x, y: acc.y + p.y}), {x:0,y:0});
            neighborCentroid = { x: sum.x / neighbors.length, y: sum.y / neighbors.length };
            
            if (!window._powerFlowNeighborsLogged) {
                console.log('⚡ Power flow neighbors found:', neighbors.length, 'points');
                console.log('  Neighbors:', neighbors.map(n => `(${n.x.toFixed(1)}, ${n.y.toFixed(1)})`));
                console.log('  Centroid:', `(${neighborCentroid.x.toFixed(1)}, ${neighborCentroid.y.toFixed(1)})`);
                window._powerFlowNeighborsLogged = true;
            }
        }
    }
    
    if (!window._powerMultiConnectorLogged) {
        console.log('🔋 Power distribution:', {
            powerOnMultiConnector,
            hasNeighborCentroid: !!neighborCentroid
        });
        window._powerMultiConnectorLogged = true;
    }
    
    ctx.strokeStyle = '#FF8C00';
    ctx.lineWidth = 3 / canvasZoom;
    ctx.setLineDash([12 / canvasZoom, 8 / canvasZoom]);
    ctx.lineDashOffset = -powerFlowOffset;
    
    const offset = 6 / canvasZoom;
    
    // Analyze all T-connectors in the system
    let tConnectorAnalysis = new Map();
    points.forEach(point => {
        if (countPointConnections(point.id) === 3) {
            const analysis = analyzeTConnector(point);
            if (analysis) {
                tConnectorAnalysis.set(point.id, analysis);
                if (!window._tConnectorAnalysisLogged) {
                    console.log('🔧 T-connector at', `(${point.x.toFixed(0)}, ${point.y.toFixed(0)}):`, 
                                'stem direction =', analysis.stemDirection);
                }
            }
        }
    });
    if (tConnectorAnalysis.size > 0 && !window._tConnectorAnalysisLogged) {
        console.log('   Total T-connectors analyzed:', tConnectorAnalysis.size);
        window._tConnectorAnalysisLogged = true;
    }
    
    // Determine if configuration is a closed loop and calculate consistent offset direction
    const hasEndpoints = points.some(p => countPointConnections(p.id) === 1);
    const isClosedLoop = !hasEndpoints && segments.length > 0;
    let globalOffsetDirection = null; // Will be 1 or -1 for closed loops
    let segmentOffsetDirections = new Map(); // Map<segmentIndex, 1|-1> for each segment
    
    if (isClosedLoop) {
        // Calculate signed area to determine polygon orientation (clockwise vs counter-clockwise)
        let signedArea = 0;
        segments.forEach(seg => {
            const p1 = points.find(p => p.id === seg.startIndex);
            const p2 = points.find(p => p.id === seg.endIndex);
            if (p1 && p2) {
                signedArea += (p2.x - p1.x) * (p2.y + p1.y);
            }
        });
        
        // If signedArea > 0, polygon is clockwise (CW)
        // If signedArea < 0, polygon is counter-clockwise (CCW)
        // 
        // Calculate centroid for per-segment direction calculation
        let centroidX = 0, centroidY = 0, pointCount = 0;
        const uniquePoints = new Set();
        segments.forEach(seg => {
            if (!uniquePoints.has(seg.startIndex)) {
                const p = points.find(p => p.id === seg.startIndex);
                if (p) {
                    centroidX += p.x;
                    centroidY += p.y;
                    pointCount++;
                    uniquePoints.add(seg.startIndex);
                }
            }
        });
        centroidX /= pointCount;
        centroidY /= pointCount;
        
        globalOffsetDirection = {
            centroidX: centroidX,
            centroidY: centroidY,
            wantInner: signedArea < 0  // CCW wants inner lines, CW wants outer
        };
        
        if (!window._closedLoopDirectionLogged) {
            console.log('🔄 Closed loop detected! (e.g., square, rectangle)');
            console.log('   Signed area:', signedArea.toFixed(1));
            console.log('   Polygon orientation:', signedArea > 0 ? '↻ Clockwise' : '↺ Counter-clockwise');
            console.log('   Power flow lines will be:', globalOffsetDirection.wantInner ? '📍 Inside the shape' : '📍 Outside the shape');
            console.log('   ✓ Per-segment direction calculation (consistent)');
            window._closedLoopDirectionLogged = true;
        }
    }
    
    // CRITICAL: Use CACHED directions calculated once after power connection
    // DO NOT recalculate every frame - it's expensive and causes instability!
    if (!cachedSegmentOffsetDirections || !cachedTConnectorAnalysis || cachedGlobalOffsetDirection !== globalOffsetDirection) {
        console.warn('⚠️ Cached directions not available! This should only happen on first frame after power connection.');
        console.warn('   Calculating directions now, but this should be done ONCE after power is set!');
        // Emergency fallback - calculate now
        segmentOffsetDirections = calculateConsistentOffsetDirections(tConnectorAnalysis);
        cachedSegmentOffsetDirections = segmentOffsetDirections;
        cachedTConnectorAnalysis = tConnectorAnalysis;
        cachedGlobalOffsetDirection = globalOffsetDirection;
    } else {
        // Use cached values (normal case)
        segmentOffsetDirections = cachedSegmentOffsetDirections;
        // tConnectorAnalysis is already set above, but make sure we use cached one
        tConnectorAnalysis = cachedTConnectorAnalysis;
    }
    
    if (!window._segmentDirectionsLogged && segmentOffsetDirections.size > 0) {
        console.log('✅ Using cached segment directions:', segmentOffsetDirections.size, '/', segments.length, 'segments');
        
        // Show direction for each segment
        const dirCounts = { '1': 0, '-1': 0, 'undefined': 0 };
        segments.forEach((seg, idx) => {
            const dir = segmentOffsetDirections.get(idx);
            if (dir === 1) dirCounts['1']++;
            else if (dir === -1) dirCounts['-1']++;
            else dirCounts['undefined']++;
        });
        console.log('   Final counts: outer=' + dirCounts['1'] + ', inner=' + dirCounts['-1'] + ', undefined=' + dirCounts['undefined']);
        console.log('   System type:', globalOffsetDirection !== null ? 'CLOSED LOOP (' + (globalOffsetDirection.wantInner ? 'inner' : 'outer') + ')' : 'OPEN (octopus propagation)');
        
        window._segmentDirectionsLogged = true;
    }
    
    segments.forEach((seg, segIndex) => {
        let p1 = points.find(p => p.id === seg.startIndex);
        let p2 = points.find(p => p.id === seg.endIndex);
        
        if (p1 && p2) {
            // Check if power is on this specific segment (middle of track)
            const isPowerOnThisSegment = powerConnection.type === 'middle' && 
                                        powerConnection.targetId === segIndex;
            
            if (isPowerOnThisSegment) {
                // Power is on this segment (R12263 - PŘÍMÝ SPOJ S NÁPÁJENÍM)
                // R12263 ALWAYS distributes power in BOTH directions along this segment
                // (it's a straight feed connector that powers both sides)
                
                if (!window._powerSegmentLinesLogged) {
                    console.log('🔥 R12263 (PŘÍMÝ SPOJ S NÁPÁJENÍM) on segment', segIndex);
                    console.log('   Power at:', `(${powerPt.x.toFixed(0)}, ${powerPt.y.toFixed(0)})`);
                    console.log('   Drawing lines in BOTH directions from power point');
                    window._powerSegmentLinesLogged = true;
                }
                
                // CRITICAL FIX: Calculate perpendicular offset based on NORMALIZED direction
                // This must match the normalization in analyzeTConnector() and Step 5!
                const dx = p2.x - p1.x;
                const dy = p2.y - p1.y;
                const segLen = Math.sqrt(dx * dx + dy * dy);
                let basePerpX = 0, basePerpY = 0;
                
                if (segLen > 0) {
                    // Normalize direction: right for horizontal, down for vertical
                    let normDx = dx;
                    let normDy = dy;
                    
                    const isHorizontal = Math.abs(dx) > Math.abs(dy);
                    if (isHorizontal) {
                        // Horizontal: always point right
                        if (normDx < 0) {
                            normDx = -normDx;
                            normDy = -normDy;
                        }
                    } else {
                        // Vertical: always point down
                        if (normDy < 0) {
                            normDx = -normDx;
                            normDy = -normDy;
                        }
                    }
                    
                    // Calculate perpendicular based on NORMALIZED direction
                    basePerpX = normDy / segLen * offset;
                    basePerpY = -normDx / segLen * offset;
                    
                    // For closed loops: calculate direction for THIS segment individually
                    if (isClosedLoop && globalOffsetDirection) {
                        // Calculate normalized perpendicular
                        const normPerpX = dy / segLen;
                        const normPerpY = -dx / segLen;
                        
                        // Calculate segment midpoint
                        const midX = (p1.x + p2.x) / 2;
                        const midY = (p1.y + p2.y) / 2;
                        
                        // Vector from midpoint to centroid
                        const toCentroidX = globalOffsetDirection.centroidX - midX;
                        const toCentroidY = globalOffsetDirection.centroidY - midY;
                        
                        // Dot product
                        const dot = normPerpX * toCentroidX + normPerpY * toCentroidY;
                        
                        // Determine direction for THIS segment
                        const finalDirection = (globalOffsetDirection.wantInner && dot > 0) || 
                                             (!globalOffsetDirection.wantInner && dot < 0) ? 1 : -1;
                        
                        basePerpX *= finalDirection;
                        basePerpY *= finalDirection;
                    } else {
                        // For open systems: use pre-calculated direction
                        const consistentDirection = segmentOffsetDirections.get(segIndex);
                        
                        if (typeof consistentDirection === 'string') {
                            // ⚠️ EXPLICIT SIDE: 'LEFT', 'RIGHT', 'TOP', 'BOTTOM'
                            // Apply absolute offset in the specified direction
                            if (consistentDirection === 'LEFT') {
                                basePerpX = -offset;
                                basePerpY = 0;
                            } else if (consistentDirection === 'RIGHT') {
                                basePerpX = +offset;
                                basePerpY = 0;
                            } else if (consistentDirection === 'TOP') {
                                basePerpX = 0;
                                basePerpY = -offset;
                            } else if (consistentDirection === 'BOTTOM') {
                                basePerpX = 0;
                                basePerpY = +offset;
                            }
                        } else if (typeof consistentDirection === 'number') {
                            // Legacy +1/-1 (for closed loops, L-connectors, etc.)
                            basePerpX *= consistentDirection;
                            basePerpY *= consistentDirection;
                        } else {
                            basePerpX *= -1;
                            basePerpY *= -1;
                        }
                    }
                }
                
                // Determine direction description (log only once)
                if (!window._segmentLogsShown) window._segmentLogsShown = new Set();
                
                if (!window._segmentLogsShown.has(`power-${segIndex}`)) {
                    const dirValue = segmentOffsetDirections.get(segIndex);
                    let dirDesc;
                    if (typeof dirValue === 'string') {
                        dirDesc = `📍 ${dirValue}`;
                    } else if (dirValue === 1) {
                        dirDesc = '📍 OUTER (right)';
                    } else if (dirValue === -1) {
                        dirDesc = '📍 INNER (left)';
                    } else {
                        dirDesc = '❓ UNDEFINED';
                    }
                    
                    console.log(`━━━ Segment ${segIndex} (R12263 - POWER ON SEGMENT) ━━━`);
                    console.log(`    Direction: ${dirDesc}`);
                    console.log(`    From: (${powerPt.x.toFixed(0)}, ${powerPt.y.toFixed(0)})`);
                    console.log(`    To P1: (${p1.x.toFixed(0)}, ${p1.y.toFixed(0)})`);
                    console.log(`    To P2: (${p2.x.toFixed(0)}, ${p2.y.toFixed(0)})`);
                    window._segmentLogsShown.add(`power-${segIndex}`);
                }
                
                // R12263 always draws lines in BOTH directions
                // Line 1: from power point → to p1
                const dx1 = p1.x - powerPt.x;
                const dy1 = p1.y - powerPt.y;
                const len1 = Math.sqrt(dx1 * dx1 + dy1 * dy1);
                
                if (len1 > 0) {
                    ctx.beginPath();
                    ctx.moveTo(powerPt.x + basePerpX, powerPt.y + basePerpY);
                    ctx.lineTo(p1.x + basePerpX, p1.y + basePerpY);
                    ctx.stroke();
                }
                
                // Line 2: from power point → to p2
                const dx2 = p2.x - powerPt.x;
                const dy2 = p2.y - powerPt.y;
                const len2 = Math.sqrt(dx2 * dx2 + dy2 * dy2);
                
                if (len2 > 0) {
                    ctx.beginPath();
                    ctx.moveTo(powerPt.x + basePerpX, powerPt.y + basePerpY);
                    ctx.lineTo(p2.x + basePerpX, p2.y + basePerpY);
                    ctx.stroke();
                }
            } else {
                // Power is NOT on this segment - draw normal line from p1 to p2
                
                // First, get the original segment direction (before swapping for animation)
                const seg = segments[segIndex];
                const origP1 = points.find(p => p.id === seg.startIndex);
                const origP2 = points.find(p => p.id === seg.endIndex);
                const origDx = origP2.x - origP1.x;
                const origDy = origP2.y - origP1.y;
                const origLen = Math.sqrt(origDx * origDx + origDy * origDy);
                
                // Calculate perpendicular based on ACTUAL segment direction (not normalized!)
                // For closed loops, we need the REAL direction to get correct perpendicular
                // Normalization is ONLY needed for T-connectors (which use EXPLICIT SIDE anyway)
                let perpX = 0, perpY = 0;
                if (origLen > 0) {
                    perpX = origDy / origLen * offset;
                    perpY = -origDx / origLen * offset;
                }
                
                // Check if this segment is connected to a T-connector (declare at higher scope for logging)
                let tConnectorAtP1 = tConnectorAnalysis.get(origP1.id);
                let tConnectorAtP2 = tConnectorAnalysis.get(origP2.id);
                
                // Use pre-calculated consistent direction if available
                const consistentDirection = segmentOffsetDirections.get(segIndex);
                if (consistentDirection !== undefined) {
                    // ⚠️ CRITICAL: Check if this is an EXPLICIT SIDE (T-connector segment)
                    if (typeof consistentDirection === 'string') {
                        // EXPLICIT SIDE: 'LEFT', 'RIGHT', 'TOP', 'BOTTOM'
                        // Apply absolute offset in the specified direction (skip dynamic calculation!)
                        if (consistentDirection === 'LEFT') {
                            perpX = -offset;
                            perpY = 0;
                        } else if (consistentDirection === 'RIGHT') {
                            perpX = +offset;
                            perpY = 0;
                        } else if (consistentDirection === 'TOP') {
                            perpX = 0;
                            perpY = -offset;
                        } else if (consistentDirection === 'BOTTOM') {
                            perpX = 0;
                            perpY = +offset;
                        }
                    } else if (typeof consistentDirection === 'number') {
                        // For closed loops and open systems: use pre-calculated direction
                        // (direction was already calculated in calculateConsistentOffsetDirections)
                        perpX *= consistentDirection;
                        perpY *= consistentDirection;
                    }
                } else {
                    // CRITICAL ERROR: Segment has no direction!
                    // This should NEVER happen if calculateConsistentOffsetDirections worked correctly
                    if (!window._noDirectionErrors) window._noDirectionErrors = new Set();
                    if (!window._noDirectionErrors.has(segIndex)) {
                        console.error('❌ CRITICAL: Segment', segIndex, 'has NO direction in drawAnimatedPowerFlow!');
                        console.error('   This means calculateConsistentOffsetDirections failed for this segment!');
                        console.error('   Using -1 (inner) as emergency fallback');
                        window._noDirectionErrors.add(segIndex);
                    }
                    
                    // Emergency fallback: use inner (-1) - don't use old fallback logic!
                    perpX *= -1;
                    perpY *= -1;
                    
                    // Skip old fallback logic - it's wrong and causes mismatches!
                    // Continue to drawing the line with -1 direction
                }
                
                // Log segment info only once per segment
                if (!window._segmentLogsShown) window._segmentLogsShown = new Set();
                
                if (!window._segmentLogsShown.has(`seg-${segIndex}`)) {
                    const p1Connections = countPointConnections(origP1.id);
                    const p2Connections = countPointConnections(origP2.id);
                    let segmentType = 'STRAIGHT';
                    
                    if (p1Connections === 2 || p2Connections === 2) {
                        segmentType = 'L-CORNER';
                    }
                    if (p1Connections === 3 || p2Connections === 3) {
                        segmentType = tConnectorAtP1 || tConnectorAtP2 ? 'T-CONNECTOR' : 'L-CORNER';
                    }
                    if (p1Connections === 4 || p2Connections === 4) {
                        segmentType = 'X-CONNECTOR';
                    }
                    
                    // Determine direction description
                    const dirValue = consistentDirection !== undefined ? consistentDirection : (perpX * origDx + perpY * origDy > 0 ? 1 : -1);
                    let dirDesc;
                    if (typeof dirValue === 'string') {
                        dirDesc = `📍 ${dirValue}`;
                    } else if (dirValue === 1) {
                        dirDesc = '📍 OUTER (right)';
                    } else if (dirValue === -1) {
                        dirDesc = '📍 INNER (left)';
                    } else {
                        dirDesc = '❓ UNDEFINED';
                    }
                    
                    console.log(`━━━ Segment ${segIndex} (${segmentType}) ━━━`);
                    console.log(`    Direction: ${dirDesc} ${consistentDirection !== undefined ? '(pre-calculated)' : '(fallback)'}`);
                    console.log(`    From: (${origP1.x.toFixed(0)}, ${origP1.y.toFixed(0)}) [${p1Connections} connections]`);
                    console.log(`    To:   (${origP2.x.toFixed(0)}, ${origP2.y.toFixed(0)}) [${p2Connections} connections]`);
                    if (tConnectorAtP1 || tConnectorAtP2) {
                        const tAnalysis = tConnectorAtP1 || tConnectorAtP2;
                        const isStem = tAnalysis.stemSegment.segIndex === segIndex;
                        console.log(`    T-connector: ${isStem ? 'STEM (vertical)' : 'BASE (horizontal)'}, stem direction: ${tAnalysis.stemDirection}`);
                    }
                    window._segmentLogsShown.add(`seg-${segIndex}`);
                }
                
                // Now swap points for animation (so flow goes away from power)
                // But keep perpendicular calculated from ORIGINAL segment direction
                const d1 = distance(p1, powerPt);
                const d2 = distance(p2, powerPt);
                if (d2 < d1) {
                    [p1, p2] = [p2, p1];
                }
                
                ctx.beginPath();
                ctx.moveTo(p1.x + perpX, p1.y + perpY);
                ctx.lineTo(p2.x + perpX, p2.y + perpY);
                ctx.stroke();
            }
        }
    });
    
    ctx.setLineDash([]);
    
    // VALIDATION: Check all L-corners for consistent directions (only once)
    if (!window._validationLogged) {
        // DISABLE VALIDATION LOGS - they're called every frame!
        // Only log if DEBUG_LOGS_ENABLED is true
        const VALIDATION_LOGS_ENABLED = false; // Set to true only for debugging
        
        if (VALIDATION_LOGS_ENABLED) {
        console.log('');
        console.log('═══════════ L-CORNER VALIDATION ═══════════');
        }
        let cornerErrorsFound = 0;
        points.forEach(point => {
            const connections = countPointConnections(point.id);
            if (connections === 2) {
                // This is an L-corner
                const connectedSegs = [];
                segments.forEach((seg, idx) => {
                    if (seg.startIndex === point.id || seg.endIndex === point.id) {
                        const dir = segmentOffsetDirections.get(idx);
                        connectedSegs.push({ idx, dir });
                    }
                });
                
                if (connectedSegs.length === 2) {
                    const seg1 = connectedSegs[0];
                    const seg2 = connectedSegs[1];
                    
                    if (seg1.dir !== seg2.dir) {
                        if (VALIDATION_LOGS_ENABLED) {
                        console.error(`❌ L-CORNER ERROR at point ${point.id} (${point.x.toFixed(0)}, ${point.y.toFixed(0)})`);
                        console.error(`   Segment ${seg1.idx}: direction ${seg1.dir === 1 ? 'OUTER (+1)' : seg1.dir === -1 ? 'INNER (-1)' : 'UNDEFINED'}`);
                        console.error(`   Segment ${seg2.idx}: direction ${seg2.dir === 1 ? 'OUTER (+1)' : seg2.dir === -1 ? 'INNER (-1)' : 'UNDEFINED'}`);
                        console.error(`   ⚠️ LINES ARE ON DIFFERENT SIDES - THIS IS WRONG!`);
                        }
                        cornerErrorsFound++;
                    } else {
                        if (VALIDATION_LOGS_ENABLED) {
                        console.log(`✓ L-corner at point ${point.id}: both segments ${seg1.dir === 1 ? 'OUTER' : 'INNER'}`);
                        }
                    }
                }
            }
        });
    
        if (cornerErrorsFound > 0) {
            if (VALIDATION_LOGS_ENABLED) {
            console.error(`🚨 TOTAL L-CORNER ERRORS: ${cornerErrorsFound}`);
            }
        } else {
            if (VALIDATION_LOGS_ENABLED) {
            console.log('✅ All L-corners are consistent!');
        }
        }
        if (VALIDATION_LOGS_ENABLED) {
        console.log('═══════════════════════════════════════════');
        console.log('');
        console.log('═══════════ T-CONNECTOR VALIDATION ═══════════');
        }
        let tConnectorErrorsFound = 0;
        tConnectorAnalysis.forEach((tAnalysis, tPointId) => {
            const tPoint = points.find(p => p.id === tPointId);
            const stemIdx = tAnalysis.stemSegment.segIndex;
            const stemDir = segmentOffsetDirections.get(stemIdx);
            const base1Idx = tAnalysis.baseSegments[0].segIndex;
            const base2Idx = tAnalysis.baseSegments[1].segIndex;
            const base1Dir = segmentOffsetDirections.get(base1Idx);
            const base2Dir = segmentOffsetDirections.get(base2Idx);
            
            if (VALIDATION_LOGS_ENABLED) {
            console.log(`T-connector at point ${tPointId} (${tPoint.x.toFixed(0)}, ${tPoint.y.toFixed(0)})`);
            console.log(`   Type: ${tAnalysis.stemDirection.toUpperCase()} T-spoj`);
            console.log(`   Stem (segment ${stemIdx}): ${stemDir === 1 ? 'OUTER (+1)' : stemDir === -1 ? 'INNER (-1)' : 'UNDEFINED'}`);
            console.log(`   Base1 (segment ${base1Idx}): ${base1Dir === 1 ? 'OUTER (+1)' : base1Dir === -1 ? 'INNER (-1)' : 'UNDEFINED'}`);
            console.log(`   Base2 (segment ${base2Idx}): ${base2Dir === 1 ? 'OUTER (+1)' : base2Dir === -1 ? 'INNER (-1)' : 'UNDEFINED'}`);
            }
            
            // Check rule: 
            // - Base segments: BOTH must have SAME direction (OUTER = opposite to stem)
            // - Stem segment: -1 for RIGHT T-spoj (line on LEFT), +1 for LEFT T-spoj (line on RIGHT)
            const expectedStemDir = tAnalysis.stemDirection === 'right' ? -1 : 1;
            
            // Base segments must have same direction (doesn't matter if +1 or -1, as long as consistent)
            const baseCorrect = (base1Dir === base2Dir && base1Dir !== undefined);
            const stemCorrect = (stemDir === expectedStemDir);
            
            if (baseCorrect && stemCorrect) {
                if (VALIDATION_LOGS_ENABLED) {
                    console.log(`   ✓ T-connector correct: base segments consistent (${base1Dir > 0 ? '+1' : '-1'}), stem ${expectedStemDir > 0 ? '+1' : '-1'}`);
                }
            } else {
                if (VALIDATION_LOGS_ENABLED) {
                    console.error(`   ❌ T-CONNECTOR ERROR!`);
                    if (!baseCorrect) {
                        console.error(`   ⚠️ Base segments should have SAME direction, but got: base1=${base1Dir}, base2=${base2Dir}`);
                    }
                    if (!stemCorrect) {
                        console.error(`   ⚠️ Stem should be ${expectedStemDir > 0 ? '+1' : '-1'} for ${tAnalysis.stemDirection.toUpperCase()} T-spoj, but got: ${stemDir}`);
                    }
                }
                tConnectorErrorsFound++;
            }
        });
    
        if (tConnectorErrorsFound > 0) {
            if (VALIDATION_LOGS_ENABLED) {
            console.error(`🚨 TOTAL T-CONNECTOR ERRORS: ${tConnectorErrorsFound}`);
            }
        } else {
            if (VALIDATION_LOGS_ENABLED) {
            console.log('✅ All T-connectors are correct!');
        }
        }
        if (VALIDATION_LOGS_ENABLED) {
        console.log('═══════════════════════════════════════════');
        console.log('');
        }
        
        window._validationLogged = true;
    }
}

// DEBUG: Draw segment directions and L-connector validation on canvas
function drawDebugSegmentDirections() {
    if (!powerConnection || segments.length === 0) return;
    
    // Calculate segment directions (same as visualization)
    const hasEndpoints = points.some(p => countPointConnections(p.id) === 1);
    const isClosedLoop = !hasEndpoints && segments.length > 0;
    
    // Analyze T-connectors
    const tConnectorAnalysis = new Map();
    points.forEach(point => {
        if (countPointConnections(point.id) === 3) {
            const analysis = analyzeTConnector(point);
            if (analysis) {
                tConnectorAnalysis.set(point.id, analysis);
            }
        }
    });
    
    // Calculate global offset direction for closed loops
    let globalOffsetDirection = null;
    if (isClosedLoop) {
        let signedArea = 0;
        segments.forEach(seg => {
            const p1 = points.find(p => p.id === seg.startIndex);
            const p2 = points.find(p => p.id === seg.endIndex);
            if (p1 && p2) {
                signedArea += (p2.x - p1.x) * (p2.y + p1.y);
            }
        });
        
        let centroidX = 0, centroidY = 0, pointCount = 0;
        const uniquePoints = new Set();
        segments.forEach(seg => {
            if (!uniquePoints.has(seg.startIndex)) {
                const p = points.find(p => p.id === seg.startIndex);
                if (p) {
                    centroidX += p.x;
                    centroidY += p.y;
                    pointCount++;
                    uniquePoints.add(seg.startIndex);
                }
            }
        });
        centroidX /= pointCount;
        centroidY /= pointCount;
        
        globalOffsetDirection = {
            centroidX: centroidX,
            centroidY: centroidY,
            wantInner: signedArea < 0
        };
    }
    
    // Get segment directions
    const segmentDirections = calculateConsistentOffsetDirections();
    
    // Draw centroid for closed loops
    if (isClosedLoop && globalOffsetDirection !== null) {
        ctx.fillStyle = 'rgba(255, 0, 0, 0.3)';
        ctx.beginPath();
        ctx.arc(globalOffsetDirection.centroidX, globalOffsetDirection.centroidY, 8 / canvasZoom, 0, Math.PI * 2);
        ctx.fill();
        
        ctx.strokeStyle = '#FF0000';
        ctx.lineWidth = 2 / canvasZoom;
        ctx.stroke();
        
        // CENTROID label disabled - keep for logic only
        // ctx.fillStyle = '#FF0000';
        // ctx.font = 'bold 10px sans-serif';
        // ctx.textAlign = 'center';
        // ctx.fillText('CENTROID', globalOffsetDirection.centroidX, globalOffsetDirection.centroidY - 12 / canvasZoom);
    }
    
    // Draw direction arrows on each segment - disabled for cleaner UI, keep for logic
    // segments.forEach((seg, segIndex) => {
    //     const p1 = points.find(p => p.id === seg.startIndex);
    //     const p2 = points.find(p => p.id === seg.endIndex);
    //     if (!p1 || !p2) return;
    //     
    //     const direction = segmentDirections.get(segIndex);
    //     if (direction === undefined) return;
    //     
    //     // Calculate midpoint
    //     const midX = (p1.x + p2.x) / 2;
    //     const midY = (p1.y + p2.y) / 2;
    //     
    //     // Calculate perpendicular vector (RIGHT side of segment)
    //     const dx = p2.x - p1.x;
    //     const dy = p2.y - p1.y;
    //     const len = Math.sqrt(dx * dx + dy * dy);
    //     if (len === 0) return;
    //     
    //     const perpX = dy / len;
    //     const perpY = -dx / len;
    //     
    //     // Apply direction
    //     const offsetX = perpX * direction * 20 / canvasZoom;
    //     const offsetY = perpY * direction * 20 / canvasZoom;
    //     
    //     // Draw arrow
    //     const arrowX = midX + offsetX;
    //     const arrowY = midY + offsetY;
    //     
    //     // Arrow color: green for +1 (outer), blue for -1 (inner)
    //     ctx.strokeStyle = direction === 1 ? '#00FF00' : '#0000FF';
    //     ctx.fillStyle = direction === 1 ? '#00FF00' : '#0000FF';
    //     ctx.lineWidth = 2 / canvasZoom;
    //     
    //     // Draw arrow line
    //     ctx.beginPath();
    //     ctx.moveTo(midX, midY);
    //     ctx.lineTo(arrowX, arrowY);
    //     ctx.stroke();
    //     
    //     // Draw arrowhead
    //     const angle = Math.atan2(offsetY, offsetX);
    //     const arrowLength = 8 / canvasZoom;
    //     ctx.beginPath();
    //     ctx.moveTo(arrowX, arrowY);
    //     ctx.lineTo(
    //         arrowX - arrowLength * Math.cos(angle - Math.PI / 6),
    //         arrowY - arrowLength * Math.sin(angle - Math.PI / 6)
    //     );
    //     ctx.lineTo(
    //         arrowX - arrowLength * Math.cos(angle + Math.PI / 6),
    //         arrowY - arrowLength * Math.sin(angle + Math.PI / 6)
    //     );
    //     ctx.closePath();
    //     ctx.fill();
    //     
    //     // Draw direction label
    //     ctx.fillStyle = direction === 1 ? '#00AA00' : '#0000AA';
    //     ctx.font = 'bold 11px sans-serif';
    //     ctx.textAlign = 'center';
    //     ctx.fillText(
    //         direction === 1 ? '+1' : '-1',
    //         arrowX + (direction === 1 ? 15 : -15) / canvasZoom,
    //         arrowY + 5 / canvasZoom
    //     );
    // });
    
    // Draw L-connectors with validation - disabled for cleaner UI, keep for logic
    // points.forEach(point => {
    //     const connectionCount = countPointConnections(point.id);
    //     if (connectionCount !== 2) return; // Only L-connectors
    //     
    //     // Find connected segments
    //     const connectedSegments = [];
    //     segments.forEach((seg, idx) => {
    //         if (seg.startIndex === point.id || seg.endIndex === point.id) {
    //             connectedSegments.push({
    //                 index: idx,
    //                 direction: segmentDirections.get(idx)
    //             });
    //         }
    //     });
    //     
    //     if (connectedSegments.length === 2) {
    //         const dir1 = connectedSegments[0].direction;
    //         const dir2 = connectedSegments[1].direction;
    //         
    //         // Check if directions match
    //         const isMismatch = (dir1 !== undefined && dir2 !== undefined && dir1 !== dir2);
    //         
    //         // Draw corner circle
    //         ctx.fillStyle = isMismatch ? 'rgba(255, 0, 0, 0.5)' : 'rgba(0, 255, 0, 0.3)';
    //         ctx.beginPath();
    //         ctx.arc(point.x, point.y, 12 / canvasZoom, 0, Math.PI * 2);
    //         ctx.fill();
    //         
    //         ctx.strokeStyle = isMismatch ? '#FF0000' : '#00FF00';
    //         ctx.lineWidth = 2 / canvasZoom;
    //         ctx.stroke();
    //         
    //         // Draw direction labels - disabled, keep for logic only
    //         // ctx.fillStyle = isMismatch ? '#FF0000' : '#000000';
    //         // ctx.font = 'bold 10px sans-serif';
    //         // ctx.textAlign = 'center';
    //         
    //         // const label1 = dir1 === undefined ? '?' : (dir1 === 1 ? '+1' : '-1');
    //         // const label2 = dir2 === undefined ? '?' : (dir2 === 1 ? '+1' : '-1');
    //         
    //         // ctx.fillText(
    //         //     `${label1}/${label2}`,
    //         //     point.x,
    //         //     point.y + 20 / canvasZoom
    //         // );
    //         
    //         if (isMismatch) {
    //             // Draw error X
    //             ctx.strokeStyle = '#FF0000';
    //             ctx.lineWidth = 3 / canvasZoom;
    //             ctx.beginPath();
    //             ctx.moveTo(point.x - 8 / canvasZoom, point.y - 8 / canvasZoom);
    //             ctx.lineTo(point.x + 8 / canvasZoom, point.y + 8 / canvasZoom);
    //             ctx.moveTo(point.x + 8 / canvasZoom, point.y - 8 / canvasZoom);
    //             ctx.lineTo(point.x - 8 / canvasZoom, point.y + 8 / canvasZoom);
    //             ctx.stroke();
    //         }
    //     }
    // });
}

// Find path from start point to end point using BFS
function findPathToEndpoint(startPointId, endPointId) {
    if (startPointId === endPointId) return [startPointId];
    
    const visited = new Set();
    const queue = [{pointId: startPointId, path: [startPointId]}];
    visited.add(startPointId);
    
    while (queue.length > 0) {
        const {pointId, path} = queue.shift();
        
        // Check if we reached the endpoint
        if (pointId === endPointId) {
            return path;
        }
        
        // Find all connected segments
        segments.forEach(seg => {
            let nextPointId = null;
            
            if (seg.startIndex === pointId && !visited.has(seg.endIndex)) {
                nextPointId = seg.endIndex;
            } else if (seg.endIndex === pointId && !visited.has(seg.startIndex)) {
                nextPointId = seg.startIndex;
            }
            
            if (nextPointId !== null) {
                visited.add(nextPointId);
                queue.push({
                    pointId: nextPointId,
                    path: [...path, nextPointId]
                });
            }
        });
    }
    
    return null;
}

function drawPoints() {
    ctx.fillStyle = '#000';
    points.forEach(p => {
        const connections = countPointConnections(p.id);
        
        let radius = 4 / canvasZoom;
        if (connections >= 3) radius = 6 / canvasZoom;
        else if (connections === 2) radius = 5 / canvasZoom;
        
        ctx.beginPath();
        ctx.arc(p.x, p.y, radius, 0, Math.PI * 2);
        ctx.fill();
        
        if (connections >= 2) {
            ctx.fillStyle = '#FFF';
            ctx.beginPath();
            ctx.arc(p.x, p.y, radius - (2 / canvasZoom), 0, Math.PI * 2);
            ctx.fill();
            ctx.fillStyle = '#000';
        }
    });
    
    if (isDrawing && currentDrawingStart && 'id' in currentDrawingStart) {
        const existingPoint = points.find(p => p.id === currentDrawingStart.id);
        if (existingPoint) {
            ctx.fillStyle = '#D4A574';
            ctx.beginPath();
            ctx.arc(currentDrawingStart.x, currentDrawingStart.y, 7 / canvasZoom, 0, Math.PI * 2);
            ctx.fill();
        }
    }
}

// FIXED: Show all possible joint positions for power snapping
function drawSnapHighlight() {
    if (window.outletSnapPreview && currentTool === 'outlet') {
        ctx.fillStyle = 'rgba(42, 127, 98, 0.15)';
        ctx.strokeStyle = 'rgba(42, 127, 98, 0.5)';
        ctx.lineWidth = 2 / canvasZoom;
        ctx.setLineDash([3 / canvasZoom, 3 / canvasZoom]);
        ctx.beginPath();
        ctx.arc(window.outletSnapPreview.x, window.outletSnapPreview.y, 
            (OUTLET_RADIUS + 4) / canvasZoom, 0, Math.PI * 2);
        ctx.stroke();
        ctx.setLineDash([]);
    }
    
    if (isConnectingPower) {
        // Highlight corners/endpoints
        points.forEach(p => {
            const connections = countPointConnections(p.id);
            if (connections > 0) {
                let radius = POINT_SNAP_TOLERANCE / canvasZoom;
                if (connections >= 3) radius = (POINT_SNAP_TOLERANCE * 1.2) / canvasZoom;
                
                ctx.fillStyle = 'rgba(42, 127, 98, 0.075)';
                ctx.strokeStyle = 'rgba(42, 127, 98, 0.5)';
                ctx.lineWidth = 1.5 / canvasZoom;
                ctx.setLineDash([2 / canvasZoom, 2 / canvasZoom]);
                ctx.beginPath();
                ctx.arc(p.x, p.y, radius, 0, Math.PI * 2);
                ctx.fill();
                ctx.stroke();
                ctx.setLineDash([]);
            }
        });
        
        // Show ALL possible joint positions including every 1m
        segments.forEach((seg, index) => {
            const p1 = points.find(p => p.id === seg.startIndex);
            const p2 = points.find(p => p.id === seg.endIndex);
            if (!p1 || !p2) return;
            
            const lengthMeters = seg.desiredLengthMeters !== null ? 
                seg.desiredLengthMeters : seg.calcLengthMeters;
            
            const dx = p2.x - p1.x;
            const dy = p2.y - p1.y;
            const segLenPixels = distance(p1, p2);
            
            if (segLenPixels <= FLOAT_TOLERANCE) return;
            
            const unit_dx = dx / segLenPixels;
            const unit_dy = dy / segLenPixels;
            
            const possibleJoints = [];
            
            // Always show joints at every 1m position
            const numFullMeters = Math.floor(lengthMeters);
            for (let i = 1; i <= numFullMeters && i * PIXELS_PER_METER < segLenPixels - FLOAT_TOLERANCE; i++) {
                const distPixels = i * PIXELS_PER_METER;
                possibleJoints.push({
                    x: p1.x + unit_dx * distPixels,
                    y: p1.y + unit_dy * distPixels,
                    distanceFromStart: distPixels
                });
            }
            
            // Additionally, for segments with specific configurations, add those joints
            const segmentPiecesInfo = calculateSegmentPieces(lengthMeters, AVAILABLE_TRACK_LENGTHS);
            
            // For segments with cuts: show BOTH possible arrangements
            if (segmentPiecesInfo.cutNeeded) {
                const cutLength = segmentPiecesInfo.cutNeeded.length;
                const cutDistPixels = cutLength * PIXELS_PER_METER;
                
                // Alternative arrangement: cut first (mark as alternative)
                if (cutDistPixels > FLOAT_TOLERANCE && cutDistPixels < segLenPixels - FLOAT_TOLERANCE) {
                    possibleJoints.push({
                        x: p1.x + unit_dx * cutDistPixels,
                        y: p1.y + unit_dy * cutDistPixels,
                        distanceFromStart: cutDistPixels,
                        isAlternative: true
                    });
                }
            }
            
            // For 3m segments: explicitly show both 1m and 2m options
            if (Math.abs(lengthMeters - 3.0) < 0.05) {
                const oneMeterDist = AVAILABLE_TRACK_LENGTHS[1] * PIXELS_PER_METER;
                const twoMeterDist = AVAILABLE_TRACK_LENGTHS[0] * PIXELS_PER_METER;
                
                // These are already added by the 1m loop above, but mark them as important
                // (no need to add again, just ensure they're there)
            }
            
            // Draw highlights for all possible joints
            possibleJoints.forEach(joint => {
                const isAlt = joint.isAlternative || false;
                ctx.fillStyle = isAlt ? 'rgba(42, 127, 98, 0.05)' : 'rgba(42, 127, 98, 0.075)';
                ctx.strokeStyle = 'rgba(42, 127, 98, 0.5)';
                ctx.lineWidth = (isAlt ? 1 : 1.5) / canvasZoom;
                ctx.setLineDash(isAlt ? [4 / canvasZoom, 4 / canvasZoom] : [2 / canvasZoom, 2 / canvasZoom]);
                ctx.beginPath();
                ctx.arc(joint.x, joint.y, JOINT_SNAP_TOLERANCE / canvasZoom, 0, Math.PI * 2);
                ctx.fill();
                ctx.stroke();
                ctx.setLineDash([]);
            });
        });
        
        // Show general track snap
        if (window.generalTrackSnap && (!powerConnectionEndCandidate || 
            powerConnectionEndCandidate.type === 'middle')) {
            ctx.fillStyle = 'rgba(212, 165, 116, 0.2)';
            ctx.strokeStyle = '#D4A574';
            ctx.lineWidth = 1 / canvasZoom;
            ctx.setLineDash([3 / canvasZoom, 3 / canvasZoom]);
            ctx.beginPath();
            ctx.arc(window.generalTrackSnap.point.x, window.generalTrackSnap.point.y, 
                SELECT_TOLERANCE / canvasZoom, 0, Math.PI * 2);
            ctx.fill();
            ctx.stroke();
            ctx.setLineDash([]);
        }
        
        // Highlight current snap target
        if (powerConnectionEndCandidate) {
            const isJointSnap = powerConnectionEndCandidate.type === 'joint';
            const isEndCornerSnap = powerConnectionEndCandidate.type === 'end' || 
                                   powerConnectionEndCandidate.type === 'corner';
            
            if (isJointSnap || isEndCornerSnap) {
                ctx.fillStyle = 'rgba(42, 127, 98, 0.2)';
                ctx.strokeStyle = 'rgba(42, 127, 98, 0.5)';
                ctx.lineWidth = 2.5 / canvasZoom;
            } else {
                ctx.fillStyle = 'rgba(212, 165, 116, 0.3)';
                ctx.strokeStyle = '#D4A574';
                ctx.lineWidth = 2 / canvasZoom;
            }
            
            ctx.beginPath();
            ctx.arc(powerConnectionEndCandidate.x, powerConnectionEndCandidate.y, 
                POINT_SNAP_TOLERANCE / canvasZoom, 0, Math.PI * 2);
            ctx.fill();
            ctx.stroke();
            
            if (isJointSnap && jointTooltip) {
                const rect = canvas.getBoundingClientRect();
                jointTooltip.style.left = (rect.left + powerConnectionEndCandidate.x + 10) + 'px';
                jointTooltip.style.top = (rect.top + powerConnectionEndCandidate.y - 30) + 'px';
                jointTooltip.classList.add('visible');
            } else if (jointTooltip) {
                jointTooltip.classList.remove('visible');
            }
        }
    } else if (jointTooltip) {
        jointTooltip.classList.remove('visible');
    }
    
    if (isDrawing && currentDrawingEnd) {
        const nearbyPoint = findNearbyEndpoint(currentDrawingEnd);
        if (nearbyPoint) {
            ctx.fillStyle = 'rgba(42, 127, 98, 0.1)';
            ctx.strokeStyle = 'rgba(42, 127, 98, 0.5)';
            ctx.lineWidth = 2;
            ctx.beginPath();
            ctx.arc(nearbyPoint.x, nearbyPoint.y, SNAP_TOLERANCE, 0, Math.PI * 2);
            ctx.fill();
            ctx.stroke();
        }
    }
}

// Component calculation functions
function calculateSegmentPieces(desiredLengthMeters, availableLengths) {
    // Round the input to avoid floating point precision issues
    desiredLengthMeters = roundLength(desiredLengthMeters);
    
    const result = {
        fullPieces: { twoMeter: 0, oneMeter: 0 },
        cutNeeded: null,
        joints: 0
    };
    
    if (desiredLengthMeters <= FLOAT_TOLERANCE) return result;
    
    // Special case for 3m (2m + 1m)
    const TRACK_MATCH_TOLERANCE = 0.1;
    if (Math.abs(desiredLengthMeters - 3.0) < TRACK_MATCH_TOLERANCE) {
        result.fullPieces.twoMeter = 1;
        result.fullPieces.oneMeter = 1;
        result.joints = 1;
        return result;
    }
    
    let remainingNeeded = desiredLengthMeters;
    let piecesUsedCount = 0;
    
    const twoMeterLen = availableLengths[0];
    const oneMeterLen = availableLengths[1];
    
    const numFullTwoMeter = Math.floor(remainingNeeded / twoMeterLen);
    if (numFullTwoMeter > 0) {
        result.fullPieces.twoMeter = numFullTwoMeter;
        remainingNeeded -= numFullTwoMeter * twoMeterLen;
        piecesUsedCount += numFullTwoMeter;
    }
    
    if (remainingNeeded > FLOAT_TOLERANCE) {
        piecesUsedCount++;
        
        // Use larger tolerance for matching track lengths (0.91-1.09m = 1m, 1.91-2.09m = 2m)
        const TRACK_MATCH_TOLERANCE = 0.1;
        
        if (Math.abs(remainingNeeded - oneMeterLen) < TRACK_MATCH_TOLERANCE) {
            result.fullPieces.oneMeter++;
        } else if (Math.abs(remainingNeeded - twoMeterLen) < TRACK_MATCH_TOLERANCE) {
            result.fullPieces.twoMeter++;
        } else {
            // Determine which track to cut from based on length
            const idealCutFrom = (remainingNeeded <= 0.5 + TRACK_MATCH_TOLERANCE) ? 
                oneMeterLen : 
                ((remainingNeeded > oneMeterLen + TRACK_MATCH_TOLERANCE) ? twoMeterLen : oneMeterLen);
            
            result.cutNeeded = {
                length: remainingNeeded,
                idealCutFrom: idealCutFrom
            };
        }
    }
    
    result.joints = Math.max(0, piecesUsedCount - 1);
    return result;
}

function addComponent(list, component, quantity = 1) {
    if (!component) {
        console.error('Attempted to add undefined component');
        return;
    }
    if (!component.code) {
        console.error('Component missing code:', component);
        return;
    }
    const sku = component.code;
    console.log(`Adding component: ${sku} x${quantity}`, component);
    if (!list[sku]) {
        list[sku] = { 
            count: 0,
            code: component.code
        };
    }
    list[sku].count += quantity;
}

function optimizeCuts(cuts, availableTracks) {
    const addedTracks = { twoMeter: 0, oneMeter: 0 };
    if (!cuts || cuts.length === 0) return addedTracks;
    
    cuts.sort((a, b) => b.length - a.length);
    
    const oneFiveCount = cuts.filter(c => Math.abs(c.length - 1.5) < 0.05).length;
    if (oneFiveCount >= 2) {
        const pairsToOptimize = Math.floor(oneFiveCount / 2);
        for (let i = 0; i < pairsToOptimize; i++) {
            const idx1 = cuts.findIndex(c => Math.abs(c.length - 1.5) < 0.05);
            if (idx1 >= 0) cuts.splice(idx1, 1);
            const idx2 = cuts.findIndex(c => Math.abs(c.length - 1.5) < 0.05);
            if (idx2 >= 0) cuts.splice(idx2, 1);
        }
        addedTracks.twoMeter += pairsToOptimize;
        addedTracks.oneMeter += pairsToOptimize;
    }
    
    const twoMeterBins = [];
    const oneMeterBins = [];
    
    for (const cut of cuts) {
        let placed = false;
        
        for (let i = 0; i < twoMeterBins.length; i++) {
            if (twoMeterBins[i] >= cut.length - FLOAT_TOLERANCE) {
                twoMeterBins[i] -= cut.length;
                placed = true;
                break;
            }
        }
        
        if (placed) continue;
        
        if (!placed && cut.length <= availableTracks[1] + FLOAT_TOLERANCE) {
            for (let i = 0; i < oneMeterBins.length; i++) {
                if (oneMeterBins[i] >= cut.length - FLOAT_TOLERANCE) {
                    oneMeterBins[i] -= cut.length;
                    placed = true;
                    break;
                }
            }
        }
        
        if (placed) continue;
        
        if (cut.length <= 0.5 + FLOAT_TOLERANCE) {
            addedTracks.oneMeter++;
            oneMeterBins.push(availableTracks[1] - cut.length);
        } else if (cut.length > availableTracks[1] + FLOAT_TOLERANCE) {
            addedTracks.twoMeter++;
            twoMeterBins.push(availableTracks[0] - cut.length);
        } else {
            if (cut.idealCutFrom === availableTracks[1]) {
                addedTracks.oneMeter++;
                oneMeterBins.push(availableTracks[1] - cut.length);
            } else {
                addedTracks.twoMeter++;
                twoMeterBins.push(availableTracks[0] - cut.length);
            }
        }
    }
    
    return addedTracks;
}

function calculateAndDisplayComponents() {
    componentList = {};
    
    // Reset component selection debug flags (ONLY for calculateAndDisplayComponents)
    // NOTE: Do NOT reset flags used in calculateConsistentOffsetDirections()!
    // Those flags are used in drawAnimatedPowerFlow() which is called every frame!
    window._tComponentSelectionLogged = false;
    
    // Reset T-connector analysis flags (called when configuration changes)
    window._tConnectorDebugLogged = new Set();
    window._step5TConnectorLogged = new Set();
    window._step5StartLogged = false;
    window._powerOnTSkipLogged = false;
    window._step6BFSLogged = false;
    window._step6_5ValidationLogged = false;
    window._step6_5TConnectorLogged = new Set();
    
    // DO NOT reset: _tCorrectionLogged, _feedEndValidationLogged, _recalculationLogged, etc.
    // These are used in calculateConsistentOffsetDirections() which is called every frame!
    
    // Show UI elements when drawing starts (only show, never hide)
    const hasDrawing = segments.length > 0 || features.some(f => f.type === 'outlet');
    
    if (hasDrawing) {
        const canvasHeader = document.querySelector('.canvas-header');
        const resultsArea = document.getElementById('results-area');
        const drawingArea = document.getElementById('drawing-area');
        const secondaryControls = document.querySelector('.secondary-controls');
        
        if (canvasHeader && !canvasHeader.classList.contains('visible')) {
            canvasHeader.classList.add('visible');
        }
        if (resultsArea && !resultsArea.classList.contains('visible')) {
            resultsArea.classList.add('visible');
        }
        if (drawingArea && !drawingArea.classList.contains('drawing-started')) {
            drawingArea.classList.add('drawing-started');
        }
        if (secondaryControls && !secondaryControls.classList.contains('visible')) {
            secondaryControls.classList.add('visible');
        }
    }
    
    if (!powerConnection) {
        resultsList.innerHTML = `<li class="message info">
            ${(segments.length > 0 || features.some(f=>f.type==='outlet')) ? 
            window.TRANSLATIONS.connectPower : 
            window.TRANSLATIONS.startDrawing}
        </li>`;
        totalCostDiv.style.display = 'none';
        return;
    }
    
    const hasEndpoints = points.some(p => countPointConnections(p.id) === 1);
    const isClosedLoop = !hasEndpoints && segments.length > 0;
    
    let totalTwoMeter = 0;
    let totalOneMeter = 0;
    let totalJoints = 0;
    let cutsNeeded = [];
    let innerLConnectors = 0;
    let outerLConnectors = 0;
    let flexConnectorCount = 0;
    let tConnectorCount = 0;
    let xConnectorCount = 0;
    let endCapCount = 0;
    
    segments.forEach((segment, index) => {
        const lengthMeters = segment.desiredLengthMeters !== null ? 
            segment.desiredLengthMeters : segment.calcLengthMeters;
        
        const isPowerSegment = powerConnection && 
            ((powerConnection.type === 'middle' || powerConnection.type === 'joint') && 
             powerConnection.targetId === index);
        
        // FIXED: Check if power is in the MIDDLE (not at a natural joint)
        let powerInMiddle = false;
        if (isPowerSegment && powerConnection.type === 'middle') {
            const p1 = points.find(p => p.id === segment.startIndex);
            const powerDistFromStart = distance(p1, powerConnection.pointCoords);
            const segmentPiecesInfo = calculateSegmentPieces(lengthMeters, AVAILABLE_TRACK_LENGTHS);
            
            // Check if power is NOT at a natural joint
            let isAtNaturalJoint = false;
            const joints = calculateSegmentJointPositions(index);
            for (const joint of joints) {
                if (Math.abs(joint.distanceFromStart - powerDistFromStart) < JOINT_SNAP_TOLERANCE) {
                    isAtNaturalJoint = true;
                    break;
                }
            }
            powerInMiddle = !isAtNaturalJoint;
        }
        
        if (!isPowerSegment) {
            const segmentResult = calculateSegmentPieces(lengthMeters, AVAILABLE_TRACK_LENGTHS);
            totalTwoMeter += segmentResult.fullPieces.twoMeter;
            totalOneMeter += segmentResult.fullPieces.oneMeter;
            totalJoints += segmentResult.joints;
            
            if (segmentResult.cutNeeded) {
                cutsNeeded.push(segmentResult.cutNeeded);
            }
        } else if (powerInMiddle) {
            // FIXED: Power in middle - just add cuts, let optimization calculate tracks
            const p1 = points.find(p => p.id === segment.startIndex);
            const p2 = points.find(p => p.id === segment.endIndex);
            const powerDistFromStart = distance(p1, powerConnection.pointCoords);
            const leftSideMeters = powerDistFromStart / PIXELS_PER_METER;
            const rightSideMeters = (distance(p1, p2) - powerDistFromStart) / PIXELS_PER_METER;
            
            // Add cuts for both sides - optimization will determine best track usage
            if (leftSideMeters > FLOAT_TOLERANCE) {
                const idealCutFrom = leftSideMeters <= 1.0 ? 1 : 2;
                cutsNeeded.push({ length: leftSideMeters, idealCutFrom: idealCutFrom });
            }
            
            if (rightSideMeters > FLOAT_TOLERANCE) {
                const idealCutFrom = rightSideMeters <= 1.0 ? 1 : 2;
                cutsNeeded.push({ length: rightSideMeters, idealCutFrom: idealCutFrom });
            }
        } else {
            // Power at natural joint or in middle
            const segmentResult = calculateSegmentPieces(lengthMeters, AVAILABLE_TRACK_LENGTHS);
            totalTwoMeter += segmentResult.fullPieces.twoMeter;
            totalOneMeter += segmentResult.fullPieces.oneMeter;
            
            // For 'joint' type - power is at a natural joint between tracks
            // For 'middle' type - power is in the middle of segment (uses R12263 feed straight)
            // Only 'joint' type should add regular joints
            if (powerConnection.type === 'joint') {
                totalJoints += segmentResult.joints;
                console.log('  Power at joint - adding', segmentResult.joints, 'joints');
            } else if (powerConnection.type === 'middle') {
                // Don't add joints - R12263 (feed straight) will be added separately
                console.log('  Power in middle - NOT adding joints (using R12263 instead)');
            } else {
                // Other types (shouldn't happen, but fallback)
                totalJoints += segmentResult.joints;
            }
            
            if (segmentResult.cutNeeded) {
                cutsNeeded.push(segmentResult.cutNeeded);
            }
        }
    });
    
    const cornerMap = new Map();
    
    points.forEach(point => {
        const connections = countPointConnections(point.id);
        
        const isPowerEndpoint = powerConnection && 
            powerConnection.type === 'end' && 
            powerConnection.targetId === point.id;
        
        const isPowerCorner = powerConnection && 
            powerConnection.type === 'corner' && 
            powerConnection.targetId === point.id;
        
        if (connections === 1) {
            if (!isPowerEndpoint) {
                endCapCount++;
            }
        } else if (connections === 2) {
            const connectedSegments = [];
            segments.forEach((seg, idx) => {
                if (seg.startIndex === point.id || seg.endIndex === point.id) {
                    connectedSegments.push({seg, idx});
                }
            });
            
            if (connectedSegments.length === 2) {
                const seg1 = connectedSegments[0].seg;
                const seg2 = connectedSegments[1].seg;
                
                const p1 = seg1.startIndex === point.id ? 
                    points.find(p => p.id === seg1.endIndex) : 
                    points.find(p => p.id === seg1.startIndex);
                const p2 = seg2.startIndex === point.id ? 
                    points.find(p => p.id === seg2.endIndex) : 
                    points.find(p => p.id === seg2.startIndex);
                
                if (p1 && p2 && point) {
                    const v1 = { x: p1.x - point.x, y: p1.y - point.y };
                    const v2 = { x: p2.x - point.x, y: p2.y - point.y };
                    
                    const angleDegrees = getAngleBetweenVectors(v1, v2);
                    
                    if (isRightAngle(angleDegrees)) {
                        // 90 degree angle - L connector
                        const n1 = normalize({...v1});
                        const n2 = normalize({...v2});
                        
                        const cross = n1.x * n2.y - n1.y * n2.x;
                        
                        let cornerType = 'straight';
                        if (Math.abs(cross) > 0.1) {
                            cornerType = cross < 0 ? 'type-A' : 'type-B';
                        }
                        
                        cornerMap.set(point.id, {
                            pointId: point.id,
                            point: point,
                            cornerType: cornerType,
                            neighbors: [p1.id, p2.id],
                            polarity: null,
                            isFlexible: false
                        });
                    } else if (isStraightLine(angleDegrees)) {
                        // 180 degree angle - straight line, just a regular joint (not flex!)
                        // Don't add flex connector for straight lines
                        // This is just a continuation of the track
                    } else {
                        // Non-standard angle (not 90° and not 180°) - needs flex connector
                        console.log('⚠️ Flexible angle detected at point', point.id, '- angle:', angleDegrees.toFixed(1), '°');
                        flexConnectorCount++;
                    }
                }
            }
        } else if (connections === 3) {
            tConnectorCount++;
        } else if (connections >= 4) {
            xConnectorCount++;
        }
    });
    
    // ============================================================
    // CRITICAL FIX: Calculate segment offset directions ONCE for ALL components
    // ============================================================
    // This MUST be calculated BEFORE determining L-connector and T-connector types
    // to ensure they all use the SAME directions that match visualization!
    
    // Analyze T-connectors (needed for calculateConsistentOffsetDirections)
    const tConnectorAnalysis = new Map();
    points.forEach(point => {
        if (countPointConnections(point.id) === 3) {
            const analysis = analyzeTConnector(point);
            if (analysis) {
                tConnectorAnalysis.set(point.id, analysis);
            }
        }
    });
    
    // Calculate global offset direction for closed loops
    let globalOffsetDirection = null;
    if (isClosedLoop) {
        let signedArea = 0;
        segments.forEach(seg => {
            const p1 = points.find(p => p.id === seg.startIndex);
            const p2 = points.find(p => p.id === seg.endIndex);
            if (p1 && p2) {
                signedArea += (p2.x - p1.x) * (p2.y + p1.y);
            }
        });
        
        let centroidX = 0, centroidY = 0, pointCount = 0;
        const uniquePoints = new Set();
        segments.forEach(seg => {
            if (!uniquePoints.has(seg.startIndex)) {
                const p = points.find(p => p.id === seg.startIndex);
                if (p) {
                    centroidX += p.x;
                    centroidY += p.y;
                    pointCount++;
                    uniquePoints.add(seg.startIndex);
                }
            }
        });
        centroidX /= pointCount;
        centroidY /= pointCount;
        
        globalOffsetDirection = {
            centroidX: centroidX,
            centroidY: centroidY,
            wantInner: signedArea < 0  // CCW wants inner, CW wants outer
        };
    }
    
    // Calculate segment offset directions (same as visualization!)
    // This is used for BOTH L-connectors AND T-connectors!
    const segmentOffsetDirections = calculateConsistentOffsetDirections();
    
    // ============================================================
    // CRITICAL FIX: Synchronize L-connector types with power line visualization
    // ============================================================
    // L-connector types MUST match power rail offsets:
    // - If both segments use outer offset (+1) → L OUTER
    // - If both segments use inner offset (-1) → L INNER
    // - NEVER mix inner/outer on same corner - physically impossible!
    
        if (cornerMap.size > 0) {
        const corners = Array.from(cornerMap.values());
        
        // Now determine L-connector polarity
        // For closed loops (squares, rectangles): ALL corners should be SAME type
        // For open systems: use cross product for each corner
        
        // Check if this is a P-shape system (square/rectangle WITH T-connector)
        // P-shape can be:
        // 1. Closed loop (no endpoints) with T-connector
        // 2. "Almost closed loop" (1 endpoint = power feed) with T-connector
        const hasTConnector = points.some(p => countPointConnections(p.id) === 3);
        const endpointCount = points.filter(p => countPointConnections(p.id) === 1).length;
        const isPShape = hasTConnector && (isClosedLoop || endpointCount === 1);
        
        if (isClosedLoop && globalOffsetDirection) {
            let uniformPolarity;
            if (isPShape) {
                // P-shape system: ALL L-connectors (corners with 2 connections) must be OUTER (VNEJSI)
                // USER SCHEMA: In P-shape (square/rectangle with T-connector), all remaining L-connectors are external
                uniformPolarity = 'outer';
                console.log('🔧 P-shape detected: square/rectangle WITH T-connector');
                console.log('   → All L-connectors (2-connection corners) set to OUTER (VNEJSI)');
            } else {
                // Simple closed loop (square/rectangle without T-connector):
                // All L-connectors should be same type based on polygon orientation
                // If wantInner (CCW): use INNER connectors
                // If !wantInner (CW): use OUTER connectors
                uniformPolarity = globalOffsetDirection.wantInner ? 'inner' : 'outer';
            }
            
            // Apply polarity to ALL corners in closed loop
            corners.forEach(corner => {
                // Double-check: only apply to L-connectors (2 connections), not T-connectors (3 connections)
                const connections = countPointConnections(corner.pointId);
                if (connections === 2) {
                    corner.polarity = uniformPolarity;
                    cornerMap.get(corner.pointId).polarity = uniformPolarity;
                    
                    if (uniformPolarity === 'inner') {
                        innerLConnectors++;
                    } else {
                        outerLConnectors++;
                    }
                } else {
                    console.warn('⚠️ Skipping corner with', connections, 'connections (not an L-connector)');
                }
            });
            
            if (!window._closedLoopCornersLogged) {
                if (isPShape) {
                    console.log('✅ P-shape system: all L-connectors set to OUTER (VNEJSI)');
                    console.log('   T-connector points:', points.filter(p => countPointConnections(p.id) === 3).map(p => p.id).join(', '));
                    console.log('   L-connector points:', corners.map(c => c.pointId).join(', '));
                    console.log('   Endpoints:', endpointCount, '(power feed)');
                } else {
                    console.log('✅ Closed loop: all L-connectors set to', uniformPolarity.toUpperCase());
                    console.log('   Polygon:', globalOffsetDirection.wantInner ? 'CCW (inner)' : 'CW (outer)');
                }
                console.log('   Total corners:', corners.length);
                window._closedLoopCornersLogged = true;
            }
        } else if (isPShape) {
            // P-shape system (not closed loop, but has T-connector + 1 endpoint):
            // ALL L-connectors must be OUTER (VNEJSI)
            console.log('🔧 P-shape detected (open with T-connector): all L-connectors set to OUTER (VNEJSI)');
            corners.forEach(corner => {
                const connections = countPointConnections(corner.pointId);
                if (connections === 2) {
                    corner.polarity = 'outer';
                    cornerMap.get(corner.pointId).polarity = 'outer';
                    outerLConnectors++;
                }
            });
        } else {
            // Open system (змейки): simple geometric approach
            // Use the signed angle between the two segments
            
            corners.forEach(corner => {
                // Find the two segments connected to this corner
                const connectedSegmentData = [];
                segments.forEach((seg, idx) => {
                    if (seg.startIndex === corner.pointId || seg.endIndex === corner.pointId) {
                        connectedSegmentData.push({ idx, seg });
                    }
                });
                
                if (connectedSegmentData.length === 2) {
                    const seg1 = connectedSegmentData[0].seg;
                    const seg2 = connectedSegmentData[1].seg;
                    
                    const cornerPoint = points.find(p => p.id === corner.pointId);
                    
                    let polarity = 'outer'; // Default
                    
                    if (cornerPoint) {
                        const neighbor1Id = seg1.startIndex === corner.pointId ? seg1.endIndex : seg1.startIndex;
                        const neighbor2Id = seg2.startIndex === corner.pointId ? seg2.endIndex : seg2.startIndex;
                        
                        const neighbor1 = points.find(p => p.id === neighbor1Id);
                        const neighbor2 = points.find(p => p.id === neighbor2Id);
                        
                        if (neighbor1 && neighbor2) {
                            // Two vectors FROM corner TO neighbors
                            const v1 = { x: neighbor1.x - cornerPoint.x, y: neighbor1.y - cornerPoint.y };
                            const v2 = { x: neighbor2.x - cornerPoint.x, y: neighbor2.y - cornerPoint.y };
                            
                            // Cross product (NO SORTING - order matters!)
                            const cross = v1.x * v2.y - v1.y * v2.x;
                            
                            // For L-connectors:
                            // Positive cross product = vectors open CCW = INNER (внутренний)
                            // Negative cross product = vectors open CW = OUTER (внешний)
                            polarity = cross > 0 ? 'inner' : 'outer';
                            
                            console.log(`L-connector at point ${corner.pointId}: cross=${cross.toFixed(2)} → ${polarity.toUpperCase()}`);
                        }
                    }
                    
                    corner.polarity = polarity;
                    cornerMap.get(corner.pointId).polarity = polarity;
                    
                    if (polarity === 'inner') {
                        innerLConnectors++;
                    } else {
                        outerLConnectors++;
                    }
                }
            });
        }
    }
    
    // REMOVED: This logic was WRONG!
    // T-connectors are already handled correctly in calculateConsistentOffsetDirections()
    // which sets base segments to +1 (external) and stem to -1/+1 (internal) based on type.
    // Forcing all L-connectors to outer here would break the correct calculation!
    //
    // The segmentOffsetDirections from calculateConsistentOffsetDirections() already
    // correctly accounts for T-connectors and propagates directions through octopus algorithm.
    // We should trust that calculation, not override it!
    
    let needsFeedInConnector = false;
    
    // Helper function to determine Feed End type based on T-connector
    // Returns 'LEFT', 'RIGHT', or null (if not connected to T-connector)
    function determineFeedEndTypeForTConnector(powerPointId, powerType) {
        if (powerPointId === null || powerPointId === undefined) return null;
        
        // Analyze all T-connectors
        const tConnectorAnalysis = new Map();
        points.forEach(point => {
            if (countPointConnections(point.id) === 3) {
                const analysis = analyzeTConnector(point);
                if (analysis) {
                    tConnectorAnalysis.set(point.id, analysis);
                }
            }
        });
        
        // Case 1: Power on T-point itself (type === 'joint' or 'corner')
        if (powerType === 'joint' || powerType === 'corner') {
            const tAnalysis = tConnectorAnalysis.get(powerPointId);
            if (tAnalysis) {
                // Power is on T-connector point itself
                // USER SCHEMA: Same logic as STEM end
                //   - PRAVY T-spoj → FEED_END_LEFT (Napajeni leva)
                //   - LEVY T-spoj → FEED_END_RIGHT (Napajeni prava)
                if (tAnalysis.stemDirection === 'right') {
                    return 'LEFT';  // PRAVY → Napajeni leva
                } else {
                    return 'RIGHT'; // LEVY → Napajeni prava
                }
            }
        }
        
        // Case 2: Power on end of segment (type === 'end')
        if (powerType === 'end') {
            const endPoint = points.find(p => p.id === powerPointId);
            console.log('🔍 determineFeedEndTypeForTConnector: powerType = end');
            console.log('   powerPointId:', powerPointId);
            console.log('   endPoint:', endPoint);
            
            if (endPoint) {
                const connectedSegment = segments.find(seg => 
                    seg.startIndex === endPoint.id || seg.endIndex === endPoint.id
                );
                
                console.log('   connectedSegment:', connectedSegment);
                
                if (connectedSegment) {
                    const segIdx = segments.indexOf(connectedSegment);
                    console.log('   segIdx:', segIdx);
                    console.log('   Total T-connectors:', tConnectorAnalysis.size);
                    
                    // Check if this segment is a T-connector's STEM or BASE
                    for (const tAnalysis of tConnectorAnalysis.values()) {
                        console.log('   Checking T-connector at point:', tAnalysis.tPoint.id);
                        console.log('      stemSegment.segIndex:', tAnalysis.stemSegment.segIndex);
                        console.log('      baseSegments:', tAnalysis.baseSegments.map(b => b.segIndex));
                        if (tAnalysis.stemSegment.segIndex === segIdx) {
                            // Power on end of STEM
                            // USER SCHEMA: T-SPOJ PRAVY → always NAPAJENI LEVY (R12284/285/286)
                            //              T-SPOJ LEVY → always NAPAJENI PRAVA (R12287/288/289)
                            console.log('🔌 Power on STEM end of T-connector:');
                            console.log('   T-connector at point:', tAnalysis.tPoint.id);
                            console.log('   STEM direction:', tAnalysis.stemDirection);
                            console.log('   T-spoj type:', tAnalysis.stemDirection === 'right' ? 'PRAVY' : 'LEVY');
                            
                            if (tAnalysis.stemDirection === 'right') {
                                console.log('   → PRAVY T-spoj → NAPAJENI LEVY (R12284/285/286)');
                                return 'LEFT';  // PRAVY → Napajeni LEVY
                            } else {
                                console.log('   → LEVY T-spoj → NAPAJENI PRAVA (R12287/288/289)');
                                return 'RIGHT'; // LEVY → Napajeni PRAVA
                            }
                        }
                        
                        // Check if this segment is one of the BASE segments
                        const isBaseSegment = tAnalysis.baseSegments.some(baseSeg => baseSeg.segIndex === segIdx);
                        if (isBaseSegment) {
                            // Power on end of BASE line
                            // BASE has 2 ends, and NAPAJENI depends on which end relative to T-connector
                            // Need to determine if endpoint is "before" or "after" T-connector in base direction
                            
                            const baseIsHorizontal = Math.abs(tAnalysis.baseVector.x) > Math.abs(tAnalysis.baseVector.y);
                            const tPointCoords = points.find(p => p.id === tAnalysis.tPoint.id);
                            const endPointCoords = points.find(p => p.id === powerPointId);
                            
                            // Determine which side of T-connector the endpoint is on
                            let isEndpointBefore; // "before" = in opposite direction of base vector
                            
                            if (baseIsHorizontal) {
                                // HORIZONTAL base: compare X coordinates (left/right of T-connector)
                                isEndpointBefore = (endPointCoords.x < tPointCoords.x);
                            } else {
                                // VERTICAL base: compare Y coordinates (above/below T-connector)
                                isEndpointBefore = (endPointCoords.y < tPointCoords.y);
                            }
                            
                            let feedEndType;
                            
                            // Logic: BASE current flows "away from stem side"
                            // - VERTICAL base + RIGHT stem → current flows LEFT
                            //   - Top end (before): current comes from right → NAPAJENI RIGHT
                            //   - Bottom end (after): current comes from left → NAPAJENI LEFT
                            // - VERTICAL base + LEFT stem → current flows RIGHT
                            //   - Top end (before): current comes from left → NAPAJENI LEFT
                            //   - Bottom end (after): current comes from right → NAPAJENI RIGHT
                            
                            if (baseIsHorizontal) {
                                // HORIZONTAL base
                                if (tAnalysis.stemDirection === 'top') {
                                    feedEndType = isEndpointBefore ? 'RIGHT' : 'LEFT';
                                } else { // bottom
                                    feedEndType = isEndpointBefore ? 'LEFT' : 'RIGHT';
                                }
                            } else {
                                // VERTICAL base
                                if (tAnalysis.stemDirection === 'right') {
                                    feedEndType = isEndpointBefore ? 'RIGHT' : 'LEFT';
                                } else { // left
                                    feedEndType = isEndpointBefore ? 'LEFT' : 'RIGHT';
                                }
                            }
                            
                            console.log('🔌 Power on BASE end of T-connector:');
                            console.log('   T-connector at point:', tAnalysis.tPoint.id, tPointCoords);
                            console.log('   Endpoint at point:', powerPointId, endPointCoords);
                            console.log('   STEM direction:', tAnalysis.stemDirection);
                            console.log('   T-spoj type:', tAnalysis.stemDirection === 'right' || tAnalysis.stemDirection === 'top' ? 'PRAVY' : 'LEVY');
                            console.log('   BASE orientation:', baseIsHorizontal ? 'HORIZONTAL' : 'VERTICAL');
                            console.log('   Endpoint position:', isEndpointBefore ? 'BEFORE T-connector' : 'AFTER T-connector');
                            console.log('   → NAPAJENI', feedEndType, feedEndType === 'LEFT' ? 'LEVA (R12284/285/286)' : 'PRAVA (R12287/288/289)');
                            
                            return feedEndType;
                        }
                    }
                    
                    console.log('⚠️ Power on end, but segment', segIdx, 'is not a T-connector part');
                }
            }
        }
        
        return null; // Not connected to T-connector
    }
    
    if (isClosedLoop) {
        if (powerConnection.type === 'corner') {
            // Power on corner (L-spoj) - check if connected to T-connector
            const feedEndType = determineFeedEndTypeForTConnector(
                powerConnection.targetId, 
                powerConnection.type
            );
            
            if (feedEndType) {
                // Connected to T-connector - use T-connector logic
                if (feedEndType === 'LEFT') {
                    addComponent(componentList, getFeedEndLeft(trackColor));
                } else {
                    addComponent(componentList, getFeedEndRight(trackColor));
                }
            }
            // If not connected to T-connector, no feed component needed (L-spoj doesn't need feed)
        } else if (powerConnection.type === 'joint') {
            // Power on joint (P-spoj or T-spoj)
            // Check if this is a T-connector
            const targetPoint = points.find(p => p.id === powerConnection.targetId);
            const isTConnector = targetPoint && countPointConnections(targetPoint.id) === 3;
            
            if (!isTConnector) {
                // Not a T-connector - check if it needs feed connector
                const feedEndType = determineFeedEndTypeForTConnector(
                    powerConnection.targetId, 
                    powerConnection.type
                );
                
                if (feedEndType) {
                    // Connected to T-connector (P-spoj) - use T-connector logic
                    if (feedEndType === 'LEFT') {
                        addComponent(componentList, getFeedEndLeft(trackColor));
                    } else {
                        addComponent(componentList, getFeedEndRight(trackColor));
                    }
                } else {
                    // Regular P-spoj (not connected to T-connector) - use R12263 (feed straight)
                    needsFeedInConnector = true;
                }
            }
            // If it's a T-connector, skip here - will be added in powerOnMultiConnector block below
        } else {
            needsFeedInConnector = true;
        }
    } else {
        if (powerConnection.type === 'end') {
            // Determine if this is left or right end
            // First check if connected to T-connector (using common function)
            console.log('🔍 Calling determineFeedEndTypeForTConnector...');
            console.log('   targetId:', powerConnection.targetId);
            console.log('   type:', powerConnection.type);
            
            let feedEndType = determineFeedEndTypeForTConnector(
                powerConnection.targetId,
                powerConnection.type
            );
            
            console.log('   Result:', feedEndType);
            
            if (feedEndType === null) {
                // Not connected to T-connector - use simple logic
                const endPoint = points.find(p => p.id === powerConnection.targetId);
                if (endPoint) {
                    const connectedSegment = segments.find(seg => 
                        seg.startIndex === endPoint.id || seg.endIndex === endPoint.id
                    );
                    
                    if (connectedSegment) {
                        const isStart = connectedSegment.startIndex === endPoint.id;
                        feedEndType = isStart ? 'LEFT' : 'RIGHT';
                    } else {
                        feedEndType = 'RIGHT'; // Fallback
                    }
                } else {
                    feedEndType = 'RIGHT'; // Fallback
                }
            }
            
            // Add the correct Feed End component
            console.log('🔌 Adding Feed End component:');
            console.log('   feedEndType:', feedEndType);
            console.log('   Power connection:', powerConnection);
            if (feedEndType === 'LEFT') {
                console.log('   → Adding FEED_END_LEFT (Napajeni leva)');
                addComponent(componentList, getFeedEndLeft(trackColor));
            } else {
                console.log('   → Adding FEED_END_RIGHT (Napajeni prava)');
                addComponent(componentList, getFeedEndRight(trackColor));
            }
        } else if (powerConnection.type === 'corner') {
            // Power on corner (L-spoj) - check if connected to T-connector
            const feedEndType = determineFeedEndTypeForTConnector(
                powerConnection.targetId,
                powerConnection.type
            );
            
            if (feedEndType) {
                // Connected to T-connector - use T-connector logic
                if (feedEndType === 'LEFT') {
                    addComponent(componentList, getFeedEndLeft(trackColor));
                } else {
                    addComponent(componentList, getFeedEndRight(trackColor));
                }
            }
            // If not connected to T-connector, no feed component needed (L-spoj doesn't need feed)
        } else if (powerConnection.type === 'joint') {
            // Power on joint (P-spoj or T-spoj)
            // Check if this is a T-connector
            const targetPoint = points.find(p => p.id === powerConnection.targetId);
            const isTConnector = targetPoint && countPointConnections(targetPoint.id) === 3;
            
            if (!isTConnector) {
                // Not a T-connector - check if it needs feed connector
                const feedEndType = determineFeedEndTypeForTConnector(
                    powerConnection.targetId,
                    powerConnection.type
                );
                
                if (feedEndType) {
                    // Connected to T-connector (P-spoj) - use T-connector logic
                    if (feedEndType === 'LEFT') {
                        addComponent(componentList, getFeedEndLeft(trackColor));
                    } else {
                        addComponent(componentList, getFeedEndRight(trackColor));
                    }
                } else {
                    // Regular P-spoj (not connected to T-connector) - use R12263 (feed straight)
                    needsFeedInConnector = true;
                }
            }
            // If it's a T-connector, skip here - will be added in powerOnMultiConnector block below
        } else {
            needsFeedInConnector = true;
        }
    }
    
    const extraTracks = optimizeCuts(cutsNeeded, AVAILABLE_TRACK_LENGTHS);
    totalTwoMeter += extraTracks.twoMeter;
    totalOneMeter += extraTracks.oneMeter;
    
    // Add tracks with selected color
    if (totalTwoMeter > 0) {
        const track2m = getTrackComponent(2, trackColor);
        if (track2m) {
            addComponent(componentList, track2m, totalTwoMeter);
        }
    }
    if (totalOneMeter > 0) {
        const track1m = getTrackComponent(1, trackColor);
        if (track1m) {
            addComponent(componentList, track1m, totalOneMeter);
        }
    }
    
    // Add suspension kits if enabled (calculate actual count based on track lengths)
    if (suspensionEnabled && segments.length > 0) {
        let totalSuspensions = 0;
        
        // Count suspensions on unique points (no duplicates at connections)
        const uniquePointIds = new Set();
        segments.forEach(seg => {
            uniquePointIds.add(seg.startIndex);
            uniquePointIds.add(seg.endIndex);
        });
        totalSuspensions += uniquePointIds.size; // One suspension at each unique point
        console.log(`Suspensions at points: ${uniquePointIds.size}`);
        
        // Count intermediate suspensions (every meter inside segments)
        segments.forEach(seg => {
            const p1 = points.find(p => p.id === seg.startIndex);
            const p2 = points.find(p => p.id === seg.endIndex);
            if (!p1 || !p2) return;
            
            // Calculate segment length in meters
            const dx = p2.x - p1.x;
            const dy = p2.y - p1.y;
            const lengthInPixels = Math.sqrt(dx * dx + dy * dy);
            const lengthInMeters = lengthInPixels / 80; // PIXELS_PER_METER = 80
            
            // Intermediate suspensions: every meter inside the segment (not at endpoints)
            const intermediateSuspensions = Math.max(0, Math.floor(lengthInMeters) - 1);
            totalSuspensions += intermediateSuspensions;
            
            console.log(`Segment ${seg.startIndex}-${seg.endIndex}: ${lengthInMeters.toFixed(2)}m = ${intermediateSuspensions} intermediate suspensions`);
        });
        
        console.log(`Total suspensions needed: ${totalSuspensions}`);
        
        if (totalSuspensions > 0) {
            const suspensionKit = getSuspensionKit(trackColor);
            if (suspensionKit) {
                addComponent(componentList, suspensionKit, totalSuspensions);
            }
        }
    }
    
    // Detect if power is placed on a T- or X-connector (3 or more connected segments)
    // T and X connectors have built-in power feed capability
    let powerOnMultiConnector = false;
    if (powerConnection) {
        // ONLY check if type is 'joint' AND targetId is a point with >=3 connections
        // Do NOT check for 'middle' type - middle is always on track segment!
        if (powerConnection.type === 'joint' || powerConnection.type === 'corner') {
            const targetPoint = points.find(p => p.id === powerConnection.targetId);
            if (targetPoint && countPointConnections(targetPoint.id) >= 3) {
                powerOnMultiConnector = true;
            }
        }
        // 'middle' type is NEVER on multi-connector - it's always on track segment
    }
    
    if (powerOnMultiConnector) {
        // Power is on T- or X-connector point that already has built-in power support.
        // T-connector itself supports power - NO separate feed component needed!
        // Do NOT add separate straight feed connector (PŘÍMÝ SPOJ S NÁPÁJENÍM / R12263).
        // Only add regular straight connectors for other joints.
        console.log('✅ Power on multi-connector (T/X) - T-connector has built-in power, no separate feed needed');
        
        if (totalJoints > 0) {
            addComponent(componentList, getStraightConnector(trackColor), totalJoints);
        }
    } else if (powerConnection) {
        // Power exists but NOT on multi-connector (T/X)
        console.log('Power connection detected:', {
            type: powerConnection.type,
            needsFeedInConnector: needsFeedInConnector,
            totalJoints: totalJoints
        });
        
        if (powerConnection.type === 'joint' && !needsFeedInConnector) {
            // Power is on a regular straight joint (2 connections).
            // Use straight feed connector (PŘÍMÝ SPOJ S NÁPÁJENÍM / R12263) for power joint.
            // This connector acts as power source and distributes current in both directions.
            console.log('✅ Power on JOINT → Adding FEED STRAIGHT (R12263)');
            if (totalJoints > 0) {
                addComponent(componentList, getStraightConnector(trackColor), totalJoints - 1);
                addComponent(componentList, getFeedStraight(trackColor), 1);
            } else {
                addComponent(componentList, getFeedStraight(trackColor), 1);
            }
        } else if ((powerConnection.type === 'middle' || needsFeedInConnector) && powerConnection.type !== 'end') {
            // Power is in the middle of a segment or needs feed in connector.
            // Add straight feed connector (PŘÍMÝ SPOJ S NÁPÁJENÍM / R12263).
            console.log('✅ Power on MIDDLE of track → Adding FEED STRAIGHT (R12263)');
            addComponent(componentList, getFeedStraight(trackColor), 1);
            if (totalJoints > 0) {
                addComponent(componentList, getStraightConnector(trackColor), totalJoints);
            }
        } else {
            // Power exists but on endpoint or other case - use regular connectors
            console.log('✅ Power on ENDPOINT → using regular connectors (no R12263)');
            if (totalJoints > 0) {
                addComponent(componentList, getStraightConnector(trackColor), totalJoints);
            }
        }
    } else {
        // No power connection - use regular straight connectors
        console.log('No power connection - using regular connectors');
        if (totalJoints > 0) {
            addComponent(componentList, getStraightConnector(trackColor), totalJoints);
        }
    }
    
    if (innerLConnectors > 0)
        addComponent(componentList, getLConnectorInner(trackColor), innerLConnectors);
    if (outerLConnectors > 0)
        addComponent(componentList, getLConnectorOuter(trackColor), outerLConnectors);
    
    if (flexConnectorCount > 0) {
        addComponent(componentList, getFlexConnector(trackColor), flexConnectorCount);
    }
    
    if (tConnectorCount > 0) {
        // CRITICAL FIX: Use the SAME segmentOffsetDirections and tConnectorAnalysis
        // that were calculated above for L-connectors! This ensures T-connector types
        // match visualization!
        
        // tConnectorAnalysis and segmentOffsetDirections are already calculated above
        // (before L-connector calculation). Use those SAME values here!
        
        // Count LEFT vs RIGHT T-connectors
        let leftCount = 0;
        let rightCount = 0;
        tConnectorAnalysis.forEach((analysis) => {
            if (analysis.stemDirection === 'left') {
                leftCount++;
            } else {
                rightCount++;
            }
        });
        
        // Add components based on corrected stem directions
        if (leftCount > 0) {
            addComponent(componentList, getTConnectorLeft(trackColor), leftCount);
        }
        if (rightCount > 0) {
            addComponent(componentList, getTConnectorRight(trackColor), rightCount);
        }
        
        if (!window._tComponentSelectionLogged) {
            console.log('✅ T-connector components selected:', 
                        'LEFT=' + leftCount, 'RIGHT=' + rightCount);
            window._tComponentSelectionLogged = true;
        }
    }
    if (xConnectorCount > 0) {
        addComponent(componentList, getXConnector(trackColor), xConnectorCount);
    }
    if (endCapCount > 0) {
        addComponent(componentList, getEndCap(trackColor), endCapCount);
    }
    
    displayComponentList(cutsNeeded.length > 0 || 
        (powerConnection.type === 'middle' && powerConnection.type !== 'joint'));
}

function displayComponentList(cuttingRequired) {
    resultsList.innerHTML = '';
    
    const sortedNames = Object.keys(componentList).sort((a, b) => a.localeCompare(b));
    
    if (sortedNames.length === 0) {
        resultsList.innerHTML = `<li class="message info">${window.configuratorTranslations.noComponents}</li>`;
        totalCostDiv.style.display = 'none';
        document.getElementById('results-count').textContent = '0';
        return;
    }
    
    let totalCost = 0;
    let totalItems = 0;
    
    sortedNames.forEach(sku => {
        const item = componentList[sku];
        
        // Get Shopify product data
        const shopifyProduct = SHOPIFY_PRODUCTS[item.code];
        console.log(`Processing SKU ${item.code}:`, shopifyProduct);
        console.log(`Image URL for ${item.code}:`, shopifyProduct?.image);
        
        const productImage = shopifyProduct?.image || '';
        const available = shopifyProduct?.available !== false;
        const productUrl = shopifyProduct?.productUrl || '#';
        const productTitle = shopifyProduct?.productTitle || '';
        const variantTitle = shopifyProduct?.title || '';
        const productName = productTitle && variantTitle ? `${productTitle} - ${variantTitle}` : (productTitle || variantTitle || sku);
        
        // Debug missing product data
        if (!shopifyProduct) {
            console.warn(`No Shopify data found for SKU: ${item.code}`);
        } else if (!productTitle && !variantTitle) {
            console.warn(`No title data for SKU: ${item.code}`, shopifyProduct);
        }
        
        // Use Shopify price (no fallback needed since we removed hardcoded prices)
        const unitPrice = shopifyProduct?.price || 0;
        const itemCost = item.count * unitPrice;
        const priceFormatted = shopifyProduct?.priceFormatted || 'N/A';
        
        totalCost += itemCost;
        totalItems += item.count;
        
        const li = document.createElement('li');
        li.className = 'component-item';
        li.dataset.sku = item.code;
        li.dataset.variantId = shopifyProduct?.variantId || '';
        li.dataset.quantity = item.count;
        
        // Make clickable if product URL exists
        if (productUrl !== '#') {
            li.style.cursor = 'pointer';
            li.addEventListener('click', () => {
                window.open(productUrl, '_blank');
            });
        }
        
        // Create image element with fallback
        let imageHtml = '';
        if (productImage && productImage !== '') {
            imageHtml = `<img src="${productImage}" alt="${productName}" class="component-image" onerror="this.parentNode.innerHTML='<div class=&quot;component-image-placeholder&quot;>No Image</div>'">`;
        } else {
            imageHtml = '<div class="component-image-placeholder">No Image</div>';
        }
        
        // Determine color circle for track products
        let colorCircleHtml = '';
        const itemText = (variantTitle || productTitle || productName).toUpperCase();
        if (itemText.includes('BLACK') || itemText.includes('(BLACK)')) {
            colorCircleHtml = '<span class="color-circle-small" style="background-color: #000000; display: inline-block; width: 12px; height: 12px; border-radius: 50%; margin-right: 6px; vertical-align: middle;"></span>';
        } else if (itemText.includes('WHITE') || itemText.includes('(WHITE)')) {
            colorCircleHtml = '<span class="color-circle-small" style="background-color: #FFFFFF; border: 1px solid #E5E5E5; display: inline-block; width: 12px; height: 12px; border-radius: 50%; margin-right: 6px; vertical-align: middle;"></span>';
        } else if (itemText.includes('SILVER') || itemText.includes('(SILVER)')) {
            colorCircleHtml = '<span class="color-circle-small" style="background-color: #C0C0C0; display: inline-block; width: 12px; height: 12px; border-radius: 50%; margin-right: 6px; vertical-align: middle;"></span>';
        }
        
        li.innerHTML = `
            ${imageHtml}
            <div class="component-info">
                <div class="component-name">${productTitle || productName}</div>
            </div>
            ${variantTitle ? `<div class="component-variant">${colorCircleHtml}${variantTitle}</div>` : ''}
            <div class="component-count">${item.count}x</div>
            <div class="component-price">
                <div class="price-value">${priceFormatted}</div>
            </div>
            ${!available ? `<div class="component-out-of-stock">${window.configuratorTranslations.outOfStock}</div>` : ''}
        `;
        
        if (!available) {
            li.style.opacity = '0.6';
            li.title = 'Currently unavailable';
        }
        
        resultsList.appendChild(li);
    });
    
    if (cuttingRequired) {
        const liNote = document.createElement('li');
        liNote.className = 'message warning';
        liNote.innerHTML = `⚠ ${window.configuratorTranslations.trackCuttingWarning}`;
        resultsList.appendChild(liNote);
    }
    
    if (powerConnection && powerConnection.type === 'joint') {
        const liNote = document.createElement('li');
        liNote.className = 'message success';
        liNote.innerHTML = `✓ ${window.configuratorTranslations.powerOptimized}`;
        resultsList.appendChild(liNote);
    } else if (powerConnection && powerConnection.type === 'middle') {
        // Check if it's truly in the middle
        const segment = segments[powerConnection.targetId];
        if (segment) {
            const p1 = points.find(p => p.id === segment.startIndex);
            const powerDistFromStart = distance(p1, powerConnection.pointCoords);
            const joints = calculateSegmentJointPositions(powerConnection.targetId);
            
            let isAtNaturalJoint = false;
            for (const joint of joints) {
                if (Math.abs(joint.distanceFromStart - powerDistFromStart) < JOINT_SNAP_TOLERANCE) {
                    isAtNaturalJoint = true;
                    break;
                }
            }
            
            if (!isAtNaturalJoint) {
                const liNote = document.createElement('li');
                liNote.className = 'message warning';
                liNote.innerHTML = `ℹ ${window.configuratorTranslations.powerMiddle}`;
                resultsList.appendChild(liNote);
            }
        }
    }
    
    // Format total cost with Shopify money format
    let formattedPrice;
    if (window.Shopify && window.Shopify.formatMoney) {
        // Convert to cents for Shopify formatting
        const priceInCents = Math.round(totalCost * 100);
        formattedPrice = window.Shopify.formatMoney(priceInCents);
    } else {
        // Fallback: format with spaces between thousands and currency after number
        let price = totalCost.toFixed(2);
        // Remove .00 for whole numbers
        if (price.endsWith('.00')) {
            price = price.replace('.00', '');
        }
        // Add spaces between thousands
        price = price.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
        formattedPrice = `${price} ${window.CURRENCY_SYMBOL || 'Kč'}`;
    }
    costValueSpan.textContent = formattedPrice;
    totalCostDiv.style.display = 'block';
    
    // Update total items count
    document.getElementById('results-count').textContent = totalItems;
    
    // Check if system is complex and show warning
    checkSystemComplexity();
}

// Check if system is complex and show/hide warning
function checkSystemComplexity() {
    const warningElement = document.getElementById('complexSystemWarning');
    if (!warningElement) return;
    
    // Count L-connectors (corners with 2 connections)
    let lConnectorCount = 0;
    points.forEach(point => {
        const connections = countPointConnections(point.id);
        if (connections === 2) {
            lConnectorCount++;
        }
    });
    
    // Check for T-connectors (3 connections) or X-connectors (4 connections)
    let hasTOrXConnector = false;
    points.forEach(point => {
        const connections = countPointConnections(point.id);
        if (connections >= 3) {
            hasTOrXConnector = true;
        }
    });
    
    // System is complex if:
    // - 2 or more L-connectors
    // - OR has at least one T or X connector
    const isComplex = (lConnectorCount >= 2) || hasTOrXConnector;
    
    // Show/hide warning
    warningElement.style.display = isComplex ? 'flex' : 'none';
    
    console.log(`System complexity check: L-connectors=${lConnectorCount}, T/X-connectors=${hasTOrXConnector}, Complex=${isComplex}`);
}

// Function to show inline input for editing segment length
function showInlineLengthInput(segmentIndex) {
    if (segmentIndex < 0 || segmentIndex >= segments.length) return;
    
    const segment = segments[segmentIndex];
    const p1 = points.find(p => p.id === segment.startIndex);
    const p2 = points.find(p => p.id === segment.endIndex);
    if (!p1 || !p2) return;
    
    const currentLength = segment.desiredLengthMeters !== null ? 
        segment.desiredLengthMeters : segment.calcLengthMeters;
    
    // Get label position in canvas coordinates
    const bounds = segment.labelBounds;
    if (!bounds) return;
    
    const rect = canvas.getBoundingClientRect();
    const canvasAspect = canvas.width / canvas.height;
    const rectAspect = rect.width / rect.height;
    
    let displayWidth, displayHeight;
    let offsetX = 0, offsetY = 0;
    
    if (rectAspect > canvasAspect) {
        displayHeight = rect.height;
        displayWidth = displayHeight * canvasAspect;
        offsetX = (rect.width - displayWidth) / 2;
    } else {
        displayWidth = rect.width;
        displayHeight = displayWidth / canvasAspect;
        offsetY = (rect.height - displayHeight) / 2;
    }
    
    const scaleX = displayWidth / canvas.width;
    const scaleY = displayHeight / canvas.height;
    
    // Convert canvas coordinates to container-relative coordinates
    const centerX = BASE_CANVAS_SIZE / 2;
    const centerY = BASE_CANVAS_SIZE / 2;
    
    const transformedX = centerX + (bounds.x + bounds.w/2 - centerX) * canvasZoom + canvasPanX * canvasZoom;
    const transformedY = centerY + (bounds.y + bounds.h/2 - centerY) * canvasZoom + canvasPanY * canvasZoom;
    
    // Position relative to canvas (not page)
    const relativeX = offsetX + transformedX * scaleX;
    const relativeY = offsetY + transformedY * scaleY;
    
    editingSegmentIndex = segmentIndex;
    selectedElement = { type: 'segment', id: segmentIndex };
    
    inlineLengthInput.value = currentLength.toFixed(1);
    inlineLengthInput.style.left = `${relativeX - 30}px`;
    inlineLengthInput.style.top = `${relativeY - 15}px`;
    inlineLengthInput.style.display = 'block';
    inlineLengthInput.focus();
    inlineLengthInput.select();
}

// Hide inline input
function hideInlineLengthInput() {
    inlineLengthInput.style.display = 'none';
    editingSegmentIndex = null;
}

// Handle inline input submit
inlineLengthInput.addEventListener('keydown', (event) => {
    if (event.key === 'Enter') {
        event.preventDefault();
        const newLength = parseFloat(inlineLengthInput.value);
        
        if (!isNaN(newLength) && newLength > 0 && editingSegmentIndex !== null) {
            const segment = segments[editingSegmentIndex];
            if (segment) {
                saveState('Edit segment length');
                segment.desiredLengthMeters = newLength;
                
                // Smart anchor point selection
                const p1 = points.find(p => p.id === segment.startIndex);
                const p2 = points.find(p => p.id === segment.endIndex);
                
                if (p1 && p2) {
                    const currentDist = distance(p1, p2);
                    const newPixels = newLength * PIXELS_PER_METER;
                    const ratio = newPixels / currentDist;
                    
                    // Determine which point to keep fixed based on priority
                    const p1Priority = getPointAnchorPriority(segment.startIndex);
                    const p2Priority = getPointAnchorPriority(segment.endIndex);
                    
                    if (p1Priority > p2Priority) {
                        // Keep p1 fixed, move p2
                        p2.x = p1.x + (p2.x - p1.x) * ratio;
                        p2.y = p1.y + (p2.y - p1.y) * ratio;
                    } else if (p2Priority > p1Priority) {
                        // Keep p2 fixed, move p1
                        p1.x = p2.x + (p1.x - p2.x) * ratio;
                        p1.y = p2.y + (p1.y - p2.y) * ratio;
                    } else {
                        // Equal priority - keep p1 fixed, move p2 (default behavior)
                        p2.x = p1.x + (p2.x - p1.x) * ratio;
                        p2.y = p1.y + (p2.y - p1.y) * ratio;
                    }
                    
                    segment.calcLengthMeters = roundLength(newLength);
                }
                
                drawAll();
                calculateAndDisplayComponents();
            }
        }
        
        hideInlineLengthInput();
    } else if (event.key === 'Escape') {
        event.preventDefault();
        hideInlineLengthInput();
    }
});

inlineLengthInput.addEventListener('blur', () => {
    hideInlineLengthInput();
});

// Event handlers and initialization code continues...
// (Keeping the rest of the code from the original file)
canvas.addEventListener('mousedown', (event) => {
    if (event.button !== 0) {
        event.preventDefault();
        return;
    }
    
    const clickPos = getMousePos(canvas, event);
    
    // Handle space + drag for panning
    if (isSpacePressed) {
        isPanning = true;
        panStartX = event.clientX;
        panStartY = event.clientY;
        panStartCanvasX = canvasPanX;
        panStartCanvasY = canvasPanY;
        canvas.style.cursor = 'grabbing';
        return;
    }
    
    if (editingSegmentIndex !== null) return;
    
    let interacted = false;
    
    if (currentTool === 'draw' && !isDrawing) {
        for (let i = 0; i < segments.length; i++) {
            const seg = segments[i];
            const p1 = points.find(p => p.id === seg.startIndex);
            const p2 = points.find(p => p.id === seg.endIndex);
            if (!p1 || !p2) continue;
            
            const distInfo = pointToSegmentDistanceInfo(clickPos, p1, p2);
            
            if (distInfo.dist < SELECT_TOLERANCE && 
                distance(clickPos, p1) > SNAP_TOLERANCE && 
                distance(clickPos, p2) > SNAP_TOLERANCE &&
                distInfo.t > 0.1 && distInfo.t < 0.9) {
                
                let existingJunction = null;
                for (const point of points) {
                    if (distance(point, distInfo.proj) < 5) {
                        existingJunction = point;
                        break;
                    }
                }
                
                if (existingJunction) {
                    isDrawing = true;
                    currentDrawingStart = { ...existingJunction };
                    currentDrawingEnd = { ...existingJunction };
                } else {
                    const newPointId = pointCounter++;
                    const newPoint = {
                        x: distInfo.proj.x,
                        y: distInfo.proj.y,
                        id: newPointId
                    };
                    points.push(newPoint);
                    
                    const originalEndIndex = seg.endIndex;
                    const dist1 = distance(p1, newPoint) / PIXELS_PER_METER;
                    const dist2 = distance(newPoint, p2) / PIXELS_PER_METER;
                    
                    seg.endIndex = newPointId;
                    seg.calcLengthMeters = roundLength(dist1);
                    seg.desiredLengthMeters = null;
                    
                    segments.push({
                        startIndex: newPointId,
                        endIndex: originalEndIndex,
                        desiredLengthMeters: null,
                        calcLengthMeters: roundLength(dist2),
                        labelBounds: null
                    });
                    
                    isDrawing = true;
                    currentDrawingStart = { ...newPoint };
                    currentDrawingEnd = { ...newPoint };
                    
                    saveState('Create T-junction');
                }
                
                interacted = true;
                break;
            }
        }
        
        if (interacted) {
            drawAll();
            calculateAndDisplayComponents();
            return;
        }
    }
    
    if (currentTool === 'select' && selectedElement?.type === 'segment') {
        const seg = segments[selectedElement.id];
        const p1 = points.find(p => p.id === seg.startIndex);
        const p2 = points.find(p => p.id === seg.endIndex);
        
        if (p1 && p2) {
            if (distance(clickPos, p1) < 10) {
                isDraggingEndpoint = true;
                draggingSegmentIndex = selectedElement.id;
                draggingPointId = seg.startIndex;
                dragStartPos = { ...p1 };
                interacted = true;
            }
            else if (distance(clickPos, p2) < 10) {
                isDraggingEndpoint = true;
                draggingSegmentIndex = selectedElement.id;
                draggingPointId = seg.endIndex;
                dragStartPos = { ...p2 };
                interacted = true;
            }
        }
    }
    
    if (!interacted && currentTool === 'connect_power') {
        // New logic: one click to place power outlet
        // Use ghost preview position if available, otherwise click position
        const placePos = powerGhostPreview || clickPos;
        
        // Create outlet at the position
        const newOutletId = pointCounter++;
        features.push({
            x: placePos.x,
            y: placePos.y,
            id: newOutletId,
            type: 'outlet'
        });
        
        // Find nearest track connection point for power
        let bestSnap = { dist: Infinity, point: null, type: null, targetId: null };
        
        // Check endpoints (prioritize endpoints with connections === 1, i.e., actual ends)
        for (const point of points) {
            const dist = distance(placePos, point);
            const connections = countPointConnections(point.id);
            
            if (connections === 1 && dist < OUTLET_SNAP_DISTANCE * 2 && dist < bestSnap.dist) {
                // This is an actual end point
                bestSnap = {
                    dist: dist,
                    point: { ...point },
                    type: 'end',
                    targetId: point.id
                };
            } else if (connections === 2 && dist < OUTLET_SNAP_DISTANCE * 2 && dist < bestSnap.dist) {
                // This is a corner
                bestSnap = {
                    dist: dist,
                    point: { ...point },
                    type: 'corner',
                    targetId: point.id
                };
            } else if (connections >= 3 && dist < OUTLET_SNAP_DISTANCE * 2 && dist < bestSnap.dist) {
                // T or X connector - treat as joint that can feed power directly
                bestSnap = {
                    dist: dist,
                    point: { ...point },
                    type: 'joint', // reuse joint logic
                    targetId: point.id, // point id (not segment index)
                    jointInfo: { ...point }
                };
            }
        }
        
        // Check track joints
        segments.forEach((seg, index) => {
            const joints = segmentJointPositions.get(index);
            if (joints) {
                joints.forEach(joint => {
                    const dist = distance(placePos, joint);
                    if (dist < bestSnap.dist) {
                        bestSnap = {
                            dist: dist,
                            point: joint,
                            type: 'joint',
                            targetId: index,
                            jointInfo: joint
                        };
                    }
                });
            }
        });
        
        // Check track middle (always check, compare with best distance)
        segments.forEach((seg, index) => {
            const p1 = points.find(p => p.id === seg.startIndex);
            const p2 = points.find(p => p.id === seg.endIndex);
            if (!p1 || !p2) return;
            
            const distInfo = pointToSegmentDistanceInfo(placePos, p1, p2);
            // Check if close to track and not too close to endpoints
            if (distInfo.dist < OUTLET_SNAP_DISTANCE && 
                distance(placePos, p1) > 20 && 
                distance(placePos, p2) > 20) {
                // Prefer track middle over distant endpoints
                if (distInfo.dist < bestSnap.dist || bestSnap.dist > OUTLET_SNAP_DISTANCE) {
                    bestSnap = {
                        dist: distInfo.dist,
                        point: distInfo.proj,
                        type: 'middle',
                        targetId: index
                    };
                }
            }
        });
        
        // Create power connection if snap point found
        if (bestSnap.point) {
            powerConnection = {
                outletId: newOutletId,
                type: bestSnap.type,
                targetId: bestSnap.targetId,
                pointCoords: bestSnap.point,
                jointInfo: bestSnap.jointInfo
            };
            
            // Reset debug flags for new power connection
            window._powerFlowDebugLogged = false;
            window._powerFlowNeighborsLogged = false;
            window._powerMultiConnectorLogged = false;
            window._powerSegmentLinesLogged = false;
            window._tConnectorAnalysisLogged = false;
            window._segmentDirectionsLogged = false;
            window._cornerMismatchLogged = false;
            window._dominantDirectionLogged = false;
            window._octopusPropagationLogged = false;
            window._tCorrectionLogged = false;
            window._feedEndTConnectorLogged = false;
            window._feedEndValidationLogged = false;
            window._recalculationLogged = false;
            window._recalculationCompleteLogged = false;
            window._tBaseDirectionLogged = false;
            window._closedLoopDirectionLogged = false;
            window._closedLoopDirectionSetLogged = false;
            window._powerSegmentInitLogged = false;
            window._octopusSkipLogged = false;
            window._perpDebugCount = 0;
            
            // CRITICAL: Calculate power line directions ONCE after power is set
            // This ensures stable, consistent directions for ALL T-connectors!
            recalculatePowerLineDirections();
        }
        
        setCurrentTool('select');
        saveState('Place power outlet');
        drawAll();
        calculateAndDisplayComponents();
        updatePowerButtonState();
        interacted = true;
        return;
    }
    
    if (!interacted && currentTool === 'select') {
        for (let i = 0; i < segments.length; i++) {
            const seg = segments[i];
            const p1 = points.find(p => p.id === seg.startIndex);
            const p2 = points.find(p => p.id === seg.endIndex);
            if (!p1 || !p2) continue;
            
            const distInfo = pointToSegmentDistanceInfo(clickPos, p1, p2);
            if (distInfo.dist < SELECT_TOLERANCE) {
                selectedElement = { type: 'segment', id: i };
                selectionHint.classList.add('visible');
                interacted = true;
                break;
            }
        }
        
        if (!interacted && powerConnection) {
            const outlet = features.find(f => f.id === powerConnection.outletId);
            if (outlet) {
                const distInfo = pointToSegmentDistanceInfo(clickPos, outlet, powerConnection.pointCoords);
                if (distInfo.dist < SELECT_TOLERANCE) {
                    selectedElement = { type: 'power', id: powerConnection.outletId };
                    selectionHint.classList.add('visible');
                    interacted = true;
                }
            }
        }
        
        if (!interacted) {
            for (let i = 0; i < features.length; i++) {
                const feature = features[i];
                if (feature.type === 'outlet') {
                    if (distance(clickPos, feature) < OUTLET_RADIUS + SELECT_TOLERANCE) {
                        selectedElement = { type: 'feature', id: feature.id };
                        selectionHint.classList.add('visible');
                        interacted = true;
                        break;
                    }
                }
            }
        }
    }
    
    if (!interacted) {
        selectedElement = null;
        selectionHint.classList.remove('visible');
        
        // Start box selection in select mode (but not when space is pressed for panning)
        if (currentTool === 'select' && !isSpacePressed) {
            isBoxSelecting = true;
            selectionBoxStart = clickPos;
            selectionBoxEnd = clickPos;
            selectedElements = []; // Clear previous multi-selection
        }
        
        if (currentTool === 'outlet') {
            let outletPos = { ...clickPos };
            
            // No boundary restrictions for infinite canvas
            
            let tooCloseToOutlet = false;
            for (const feature of features) {
                if (feature.type === 'outlet' && distance(outletPos, feature) < OUTLET_RADIUS * 2) {
                    tooCloseToOutlet = true;
                    break;
                }
            }
            
            if (!tooCloseToOutlet) {
                const newOutletId = pointCounter++;
                features.push({
                    x: outletPos.x,
                    y: outletPos.y,
                    id: newOutletId,
                    type: 'outlet'
                });
                
                saveState('Place outlet');
            }
        } else if (currentTool === 'draw') {
            let tjunctionCreated = false;
            
            for (let i = 0; i < segments.length; i++) {
                const seg = segments[i];
                const p1 = points.find(p => p.id === seg.startIndex);
                const p2 = points.find(p => p.id === seg.endIndex);
                if (!p1 || !p2) continue;
                
                const distInfo = pointToSegmentDistanceInfo(clickPos, p1, p2);
                
                if (distInfo.dist < SELECT_TOLERANCE && 
                    distance(clickPos, p1) > SNAP_TOLERANCE && 
                    distance(clickPos, p2) > SNAP_TOLERANCE &&
                    distInfo.t > 0.1 && distInfo.t < 0.9) {
                    
                    const newPointId = pointCounter++;
                    const newPoint = {
                        x: distInfo.proj.x,
                        y: distInfo.proj.y,
                        id: newPointId
                    };
                    points.push(newPoint);
                    
                    const originalEndIndex = seg.endIndex;
                    const dist1 = distance(p1, newPoint) / PIXELS_PER_METER;
                    const dist2 = distance(newPoint, p2) / PIXELS_PER_METER;
                    
                    seg.endIndex = newPointId;
                    seg.calcLengthMeters = roundLength(dist1);
                    seg.desiredLengthMeters = null;
                    
                    segments.push({
                        startIndex: newPointId,
                        endIndex: originalEndIndex,
                        desiredLengthMeters: null,
                        calcLengthMeters: roundLength(dist2),
                        labelBounds: null
                    });
                    
                    isDrawing = true;
                    currentDrawingStart = { ...newPoint };
                    currentDrawingEnd = { ...newPoint };
                    
                    saveState('Create T-junction start');
                    tjunctionCreated = true;
                    break;
                }
            }
            
            if (!tjunctionCreated) {
                isDrawing = true;
                const snappedPoint = findNearbyEndpoint(clickPos);
                
                if (snappedPoint) {
                    currentDrawingStart = { ...snappedPoint };
                } else {
                    currentDrawingStart = { ...clickPos };
                }
                currentDrawingEnd = { ...currentDrawingStart };
                
                const modeInd = document.getElementById('modeIndicator');
                if (modeInd) {
                    if (isShiftPressed) {
                        modeInd.textContent = window.configuratorTranslations.drawModeFree;
                        modeInd.style.background = '#8B4513';
                    } else {
                        modeInd.textContent = window.configuratorTranslations.drawMode1m;
                        modeInd.style.background = '#D4A574';
                    }
                }
            }
        }
    }
    
    drawAll();
    calculateAndDisplayComponents();
    event.preventDefault();
});

// FIXED: Power connection mousemove with proper joint snapping for all arrangements
canvas.addEventListener('mousemove', (event) => {
    // Handle canvas panning with space key (before getMousePos for performance)
    if (isPanning) {
        const deltaX = event.clientX - panStartX;
        const deltaY = event.clientY - panStartY;
        canvasPanX = panStartCanvasX + deltaX;
        canvasPanY = panStartCanvasY + deltaY;
        drawAll();
        return;
    }
    
    const currentPos = getMousePos(canvas, event);
    lastMousePos = currentPos; // Save for custom cursor drawing
    
    // Handle box selection in select mode
    if (isBoxSelecting && selectionBoxStart) {
        selectionBoxEnd = currentPos;
        drawAll();
        return;
    }
    
    if (isDraggingEndpoint && draggingSegmentIndex !== null) {
        const segment = segments[draggingSegmentIndex];
        const otherPointId = draggingPointId === segment.startIndex ? 
            segment.endIndex : segment.startIndex;
        const otherPoint = points.find(p => p.id === otherPointId);
        
        if (otherPoint) {
            let newPos = isCtrlPressed ? 
                currentPos :
                snapPointOrthogonal(currentPos, otherPoint);
            
            if (isShiftPressed) {
                newPos = snapToMeterIncrements(otherPoint, newPos);
            }
            
            const dragPoint = points.find(p => p.id === draggingPointId);
            if (dragPoint) {
                // Calculate guide lines for the new position
                const tempPoint = { ...dragPoint, x: newPos.x, y: newPos.y };
                activeGuideLines = calculateGuideLines(tempPoint);
                
                // Snap to guide lines if close
                const SNAP_TO_GUIDE = 5; // pixels
                activeGuideLines.forEach(guide => {
                    if (guide.type === 'horizontal' && Math.abs(newPos.y - guide.y) < SNAP_TO_GUIDE) {
                        newPos.y = guide.y;
                    }
                    if (guide.type === 'vertical' && Math.abs(newPos.x - guide.x) < SNAP_TO_GUIDE) {
                        newPos.x = guide.x;
                    }
                });
                
                dragPoint.x = newPos.x;
                dragPoint.y = newPos.y;
                
                segment.calcLengthMeters = roundLength(distance(otherPoint, newPos) / PIXELS_PER_METER);
                segment.desiredLengthMeters = segment.calcLengthMeters;
            }
        }
        
        drawAll();
        calculateAndDisplayComponents();
    } else if (currentTool === 'connect_power') {
        // Show ghost preview when near track (priority: endpoints > track middle)
        let ghostPos = null;
        let minDist = Infinity;
        
        // Check endpoints first (priority)
        for (const point of points) {
            const dist = distance(currentPos, point);
            const connections = countPointConnections(point.id);
            if (connections > 0 && dist < OUTLET_SNAP_DISTANCE && dist < minDist) {
                ghostPos = { ...point };
                minDist = dist;
            }
        }
        
        // Always check track segments (not just if no endpoint)
        for (let i = 0; i < segments.length; i++) {
            const seg = segments[i];
            const p1 = points.find(p => p.id === seg.startIndex);
            const p2 = points.find(p => p.id === seg.endIndex);
            if (!p1 || !p2) continue;
            
            const distInfo = pointToSegmentDistanceInfo(currentPos, p1, p2);
            // Check if close to track and not too close to endpoints
            if (distInfo.dist < OUTLET_SNAP_DISTANCE && 
                distance(currentPos, p1) > 20 && 
                distance(currentPos, p2) > 20) {
                // Prefer track middle over distant endpoints
                if (distInfo.dist < minDist || minDist > OUTLET_SNAP_DISTANCE) {
                    ghostPos = distInfo.proj;
                    minDist = distInfo.dist;
                }
            }
        }
        
        powerGhostPreview = ghostPos;
        
        drawAll();
    } else if (isDrawing && currentDrawingStart && currentTool === 'draw') {
        currentDrawingEnd = isCtrlPressed ? 
            currentPos : 
            snapPointOrthogonal(currentPos, currentDrawingStart);
        
        const nearbyEndpoint = findNearbyEndpoint(currentPos);
        if (nearbyEndpoint) {
            currentDrawingEnd = { ...nearbyEndpoint };
        } else if (!isShiftPressed) {
            // Default: snap to meter increments
            currentDrawingEnd = snapToMeterIncrements(currentDrawingStart, currentDrawingEnd);
        }
        // If Shift is pressed: free drawing (no snap)
        
        drawAll();
    } else if (currentTool === 'select' && !isDraggingEndpoint) {
        if (selectedElement?.type === 'segment') {
            const seg = segments[selectedElement.id];
            const p1 = points.find(p => p.id === seg.startIndex);
            const p2 = points.find(p => p.id === seg.endIndex);
            
            if (p1 && p2) {
                if (distance(currentPos, p1) < 10 || distance(currentPos, p2) < 10) {
                    canvas.style.cursor = 'move';
                } else {
                    canvas.style.cursor = 'pointer';
                }
            }
        }
    }
});

canvas.addEventListener('mouseup', (event) => {
    // Handle panning release
    if (isPanning) {
        isPanning = false;
        updateCursor();
        return;
    }
    
    // Handle box selection finish
    if (isBoxSelecting) {
        isBoxSelecting = false;
        
        if (selectionBoxStart && selectionBoxEnd) {
            // Calculate selection box bounds
            const minX = Math.min(selectionBoxStart.x, selectionBoxEnd.x);
            const maxX = Math.max(selectionBoxStart.x, selectionBoxEnd.x);
            const minY = Math.min(selectionBoxStart.y, selectionBoxEnd.y);
            const maxY = Math.max(selectionBoxStart.y, selectionBoxEnd.y);
            
            // Select all segments that are fully or partially inside the box
            selectedElements = [];
            for (let i = 0; i < segments.length; i++) {
                const seg = segments[i];
                const p1 = points.find(p => p.id === seg.startIndex);
                const p2 = points.find(p => p.id === seg.endIndex);
                if (!p1 || !p2) continue;
                
                // Check if segment intersects with selection box
                const segMinX = Math.min(p1.x, p2.x);
                const segMaxX = Math.max(p1.x, p2.x);
                const segMinY = Math.min(p1.y, p2.y);
                const segMaxY = Math.max(p1.y, p2.y);
                
                // Check if any part of segment is inside selection box
                if (segMaxX >= minX && segMinX <= maxX && 
                    segMaxY >= minY && segMinY <= maxY) {
                    selectedElements.push({ type: 'segment', id: i });
                }
            }
            
            // If multiple segments selected, show them all as selected
            if (selectedElements.length > 0) {
                selectedElement = selectedElements[0]; // Set first as primary
                selectionHint.classList.add('visible');
            }
        }
        
        selectionBoxStart = null;
        selectionBoxEnd = null;
        drawAll();
        return;
    }
    
    if (isDraggingEndpoint) {
        isDraggingEndpoint = false;
        draggingSegmentIndex = null;
        draggingPointId = null;
        dragStartPos = null;
        activeGuideLines = []; // Clear guide lines
        saveState('Resize segment');
        drawAll();
        calculateAndDisplayComponents();
        return;
    }
    
    // Removed old isConnectingPower drag logic - now handled in mousedown
    
    if (isDrawing && currentTool === 'draw') {
        if (!currentDrawingStart || !currentDrawingEnd) {
            isDrawing = false;
            return;
        }
        
        // Use the already calculated currentDrawingEnd from mousemove
        // This ensures consistency with what the user sees during drawing
        let finalEndPointPos = { ...currentDrawingEnd };
        
        const segmentLengthPixels = distance(currentDrawingStart, finalEndPointPos);
        
        if (segmentLengthPixels >= MIN_SEGMENT_PIXELS) {
            let startPoint = null;
            if ('id' in currentDrawingStart) {
                startPoint = points.find(p => p.id === currentDrawingStart.id);
            }
            
            if (!startPoint) {
                startPoint = { ...currentDrawingStart, id: pointCounter++ };
                points.push(startPoint);
            }
            
            let finalEndPoint = findNearbyEndpoint(finalEndPointPos);
            
            if (!finalEndPoint) {
                finalEndPoint = { ...finalEndPointPos, id: pointCounter++ };
                points.push(finalEndPoint);
            }
            
            if (startPoint.id !== finalEndPoint.id) {
                const segmentExists = segments.some(seg => 
                    (seg.startIndex === startPoint.id && seg.endIndex === finalEndPoint.id) ||
                    (seg.startIndex === finalEndPoint.id && seg.endIndex === startPoint.id)
                );
                
                if (!segmentExists) {
                    const intermediatePoints = [];
                    
                    for (const point of points) {
                        if (point.id === startPoint.id || point.id === finalEndPoint.id) continue;
                        
                        const distInfo = pointToSegmentDistanceInfo(point, startPoint, finalEndPoint);
                        
                        if (distInfo.dist < 5 && distInfo.t > 0.01 && distInfo.t < 0.99) {
                            const connections = countPointConnections(point.id);
                            if (connections > 0) {
                                intermediatePoints.push({
                                    point: point,
                                    t: distInfo.t
                                });
                            }
                        }
                    }
                    
                    intermediatePoints.sort((a, b) => a.t - b.t);
                    
                    if (intermediatePoints.length > 0) {
                        let currentStart = startPoint;
                        
                        for (const intermediate of intermediatePoints) {
                            const dist = distance(currentStart, intermediate.point) / PIXELS_PER_METER;
                            
                            segments.push({
                                startIndex: currentStart.id,
                                endIndex: intermediate.point.id,
                                desiredLengthMeters: null,
                                calcLengthMeters: roundLength(dist),
                                labelBounds: null
                            });
                            
                            currentStart = intermediate.point;
                        }
                        
                        const finalDist = distance(currentStart, finalEndPoint) / PIXELS_PER_METER;
                        segments.push({
                            startIndex: currentStart.id,
                            endIndex: finalEndPoint.id,
                            desiredLengthMeters: null,
                            calcLengthMeters: roundLength(finalDist),
                            labelBounds: null
                        });
                        
                    } else {
                        // FIXED: Check if endpoint is ON an existing segment (T-junction)
                        let tJunctionCreated = false;
                        
                        for (let i = 0; i < segments.length; i++) {
                            const existingSeg = segments[i];
                            const existingP1 = points.find(p => p.id === existingSeg.startIndex);
                            const existingP2 = points.find(p => p.id === existingSeg.endIndex);
                            
                            if (!existingP1 || !existingP2) continue;
                            
                            // Check if endpoint is near this segment
                            const distInfo = pointToSegmentDistanceInfo(finalEndPoint, existingP1, existingP2);
                            
                            if (distInfo.dist < SELECT_TOLERANCE && 
                                distance(finalEndPoint, existingP1) > SNAP_TOLERANCE && 
                                distance(finalEndPoint, existingP2) > SNAP_TOLERANCE &&
                                distInfo.t > 0.1 && distInfo.t < 0.9) {
                                
                                // Snap endpoint to the segment projection
                                finalEndPoint.x = distInfo.proj.x;
                                finalEndPoint.y = distInfo.proj.y;
                                
                                // Check if there's already a junction point here
                                let existingJunction = null;
                                for (const point of points) {
                                    if (point.id === finalEndPoint.id) continue;
                                    if (distance(point, distInfo.proj) < 5) {
                                        existingJunction = point;
                                        break;
                                    }
                                }
                                
                                if (existingJunction) {
                                    // Use existing junction
                                    const junctionIndex = points.findIndex(p => p.id === finalEndPoint.id);
                                    if (junctionIndex !== -1) {
                                        points.splice(junctionIndex, 1);
                                    }
                                    finalEndPoint = existingJunction;
                                } else {
                                    // Split the existing segment at this point
                                    const dist1 = distance(existingP1, finalEndPoint) / PIXELS_PER_METER;
                                    const dist2 = distance(finalEndPoint, existingP2) / PIXELS_PER_METER;
                                    
                                    existingSeg.endIndex = finalEndPoint.id;
                                    existingSeg.calcLengthMeters = dist1;
                                    existingSeg.desiredLengthMeters = null;
                                    
                                    segments.push({
                                        startIndex: finalEndPoint.id,
                                        endIndex: existingP2.id,
                                        desiredLengthMeters: null,
                                        calcLengthMeters: roundLength(dist2),
                                        labelBounds: null
                                    });
                                }
                                
                                tJunctionCreated = true;
                                break;
                            }
                        }
                        
                        // Create the new segment
                        segments.push({
                            startIndex: startPoint.id,
                            endIndex: finalEndPoint.id,
                            desiredLengthMeters: null,
                            calcLengthMeters: roundLength(distance(startPoint, finalEndPoint) / PIXELS_PER_METER),
                            labelBounds: null
                        });
                        
                        // FIXED: Check for intersections with existing segments (X-junction creation)
                        // Only if T-junction was not created
                        if (!tJunctionCreated) {
                            const newSegmentIndex = segments.length - 1;
                            const newSegment = segments[newSegmentIndex];
                            const newSegmentStartPoint = startPoint;
                            const newSegmentEndPoint = finalEndPoint;
                            
                            for (let i = 0; i < segments.length - 1; i++) {
                                const existingSeg = segments[i];
                                const existingP1 = points.find(p => p.id === existingSeg.startIndex);
                                const existingP2 = points.find(p => p.id === existingSeg.endIndex);
                                
                                if (!existingP1 || !existingP2) continue;
                                
                                // Check if the new segment intersects this existing segment
                                const intersection = segmentsIntersect(
                                    newSegmentStartPoint, 
                                    newSegmentEndPoint,
                                    existingP1,
                                    existingP2
                                );
                                
                                if (intersection) {
                                    // Remove the just-added segment
                                    segments.splice(newSegmentIndex, 1);
                                    
                                    // Create intersection point
                                    const intersectionPointId = pointCounter++;
                                    const intersectionPoint = {
                                        x: intersection.x,
                                        y: intersection.y,
                                        id: intersectionPointId
                                    };
                                    points.push(intersectionPoint);
                                    
                                    // Split existing segment at intersection
                                    const existingDist1 = distance(existingP1, intersectionPoint) / PIXELS_PER_METER;
                                    const existingDist2 = distance(intersectionPoint, existingP2) / PIXELS_PER_METER;
                                    
                                    existingSeg.endIndex = intersectionPointId;
                                    existingSeg.calcLengthMeters = roundLength(existingDist1);
                                    existingSeg.desiredLengthMeters = null;
                                    
                                    segments.push({
                                        startIndex: intersectionPointId,
                                        endIndex: existingP2.id,
                                        desiredLengthMeters: null,
                                        calcLengthMeters: roundLength(existingDist2),
                                        labelBounds: null
                                    });
                                    
                                    // Split new segment at intersection
                                    const newDist1 = distance(newSegmentStartPoint, intersectionPoint) / PIXELS_PER_METER;
                                    const newDist2 = distance(intersectionPoint, newSegmentEndPoint) / PIXELS_PER_METER;
                                    
                                    segments.push({
                                        startIndex: newSegmentStartPoint.id,
                                        endIndex: intersectionPointId,
                                        desiredLengthMeters: null,
                                        calcLengthMeters: roundLength(newDist1),
                                        labelBounds: null
                                    });
                                    
                                    segments.push({
                                        startIndex: intersectionPointId,
                                        endIndex: newSegmentEndPoint.id,
                                        desiredLengthMeters: null,
                                        calcLengthMeters: roundLength(newDist2),
                                        labelBounds: null
                                    });
                                    
                                    break; // Only process first intersection
                                }
                            }
                        }
                    }
                    
                    saveState('Draw segment');
                    
                    // CRITICAL: Recalculate power line directions if power exists
                    // User might have added segment that creates/modifies T-connector
                    if (powerConnection) {
                        recalculatePowerLineDirections();
                    }
                }
            }
        }
        
        isDrawing = false;
        currentDrawingStart = null;
        currentDrawingEnd = null;
        
        if (currentTool === 'draw') {
            const modeInd = document.getElementById('modeIndicator');
            if (modeInd) {
                if (isShiftPressed) {
                    modeInd.textContent = window.configuratorTranslations.drawModeFree;
                    modeInd.style.background = '#8B4513';
                } else {
                    modeInd.textContent = window.configuratorTranslations.drawMode1m;
                    modeInd.style.background = '#D4A574';
                }
            }
        }
        
        drawAll();
        calculateAndDisplayComponents();
    }
});

canvas.addEventListener('mouseleave', (event) => {
    if (jointTooltip) {
        jointTooltip.classList.remove('visible');
    }
    
    if (window.outletSnapPreview) {
        window.outletSnapPreview = null;
        drawAll();
    }
    
    if (window.generalTrackSnap) {
        window.generalTrackSnap = null;
    }
    
    if (isDraggingEndpoint) {
        isDraggingEndpoint = false;
        draggingSegmentIndex = null;
        draggingPointId = null;
        dragStartPos = null;
        activeGuideLines = []; // Clear guide lines
        drawAll();
    }
    if (isDrawing) {
        isDrawing = false;
        currentDrawingStart = null;
        currentDrawingEnd = null;
        drawAll();
    }
    if (isConnectingPower) {
        isConnectingPower = false;
        powerConnectionStart = null;
        powerConnectionEndCandidate = null;
        drawAll();
    }
    
    isShiftPressed = false;
    isCtrlPressed = false;
    isAltPressed = false;
});

canvas.addEventListener('contextmenu', (event) => {
    event.preventDefault();
    return false;
});

// Double click to edit length label
canvas.addEventListener('dblclick', (event) => {
    // Allow editing in both draw and select modes
    if (currentTool !== 'select' && currentTool !== 'draw') return;
    if (editingSegmentIndex !== null) return;
    
    const clickPos = getMousePos(canvas, event);
    
    // Check if double-clicking on length label to edit
    for (let i = 0; i < segments.length; i++) {
        const seg = segments[i];
        if (seg.labelBounds) {
            const bounds = seg.labelBounds;
            if (clickPos.x >= bounds.x && clickPos.x <= bounds.x + bounds.w &&
                clickPos.y >= bounds.y && clickPos.y <= bounds.y + bounds.h) {
                // Double-clicked on label - show inline input
                event.preventDefault();
                showInlineLengthInput(i);
                return;
            }
        }
    }
});

const toolDrawButton = document.getElementById('toolDrawButton');
const toolSelectButton = document.getElementById('toolSelectButton');
const toolOutletButton = document.getElementById('toolOutletButton');
const toolConnectPowerButton = document.getElementById('toolConnectPowerButton');
const undoButton = document.getElementById('undoButton');

toolDrawButton.addEventListener('click', () => setCurrentTool('draw'));
toolSelectButton.addEventListener('click', () => setCurrentTool('select'));
toolOutletButton.addEventListener('click', () => setCurrentTool('outlet'));
toolConnectPowerButton.addEventListener('click', () => {
    // Check if power is already connected
    const isPowerConnected = powerConnection && powerConnection.segmentIndex !== null;
    
    if (isPowerConnected) {
        // Remove the power outlet feature (orange point)
        if (powerConnection.outletId !== undefined) {
            features = features.filter(f => f.id !== powerConnection.outletId);
        }
        
        // Disconnect power and return to drawing mode
        powerConnection = null;
        saveState('Power disconnected');
        setCurrentTool('draw');
        calculateAndDisplayComponents();
    } else {
        // Enter power connection mode
        setCurrentTool('connect_power');
    }
});
undoButton.addEventListener('click', () => undo());

// Zoom functionality
const zoomInButton = document.getElementById('zoomInButton');
const zoomOutButton = document.getElementById('zoomOutButton');
const zoomIndicator = document.getElementById('zoomIndicator');

function updateZoomIndicator() {
    if (zoomIndicator) {
        const zoomPercent = Math.round(canvasZoom * 100);
        zoomIndicator.textContent = `${zoomPercent}%`;
    }
}

function zoomIn() {
    canvasZoom = Math.min(canvasZoom * 1.2, 3);
    updateZoomIndicator();
    drawAll();
}

function zoomOut() {
    canvasZoom = Math.max(canvasZoom / 1.2, 0.5);
    updateZoomIndicator();
    drawAll();
}

if (zoomInButton) {
    zoomInButton.addEventListener('click', zoomIn);
}
if (zoomOutButton) {
    zoomOutButton.addEventListener('click', zoomOut);
}

// Mouse wheel zoom
canvas.addEventListener('wheel', (event) => {
    event.preventDefault();
    
    const delta = event.deltaY;
    const zoomFactor = 1.1;
    
    if (delta < 0) {
        // Scroll up - zoom in
        canvasZoom = Math.min(canvasZoom * zoomFactor, 3);
    } else {
        // Scroll down - zoom out
        canvasZoom = Math.max(canvasZoom / zoomFactor, 0.5);
    }
    
    updateZoomIndicator();
    drawAll();
}, { passive: false });

// Initialize zoom indicator
updateZoomIndicator();

// Update canvas cursor based on current state
function updateCursor() {
    if (isSpacePressed && !isPanning) {
        canvas.style.cursor = 'grab';
    } else if (isPanning) {
        canvas.style.cursor = 'grabbing';
    } else if (currentTool === 'draw') {
        canvas.style.cursor = 'crosshair';
    } else if (currentTool === 'select') {
        canvas.style.cursor = 'default';
    } else if (currentTool === 'outlet') {
        canvas.style.cursor = 'crosshair';
    } else if (currentTool === 'connect_power') {
        canvas.style.cursor = 'none'; // Hide cursor, we draw custom one
    } else {
        canvas.style.cursor = 'default';
    }
}

function handleKeyDown(event) {
    // Check if typing in input field
    const isTyping = event.target.tagName === 'INPUT' || event.target.tagName === 'TEXTAREA';
    
    // Prevent page scroll on Space (but allow in input fields)
    if (event.code === 'Space' && !isTyping) {
        event.preventDefault();
    }
    
    // Ctrl/Cmd + Z: Undo
    if ((event.ctrlKey || event.metaKey) && event.key === 'z' && !event.shiftKey && !isTyping) {
        event.preventDefault();
        undo();
        return;
    }
    
    // Ctrl/Cmd + Y: Redo
    if ((event.ctrlKey || event.metaKey) && event.key === 'y' && !isTyping) {
        event.preventDefault();
        redo();
        return;
    }
    
    // Ctrl/Cmd + C: Copy configuration link
    if ((event.ctrlKey || event.metaKey) && event.key === 'c' && !isTyping) {
        event.preventDefault();
        copyConfigLink();
        return;
    }
    
    // Don't handle other shortcuts when typing
    if (isTyping) return;
    
    // Check if power is connected (disable D and S shortcuts)
    const isPowerConnected = powerConnection && powerConnection.segmentIndex !== null;
    
    // D: Switch to Draw mode (disabled when power is connected)
    if (event.key === 'd' || event.key === 'D') {
        event.preventDefault();
        if (!isPowerConnected) {
            document.getElementById('toolDrawButton')?.click();
        }
        return;
    }
    
    // S: Switch to Select mode (disabled when power is connected)
    if (event.key === 's' || event.key === 'S') {
        event.preventDefault();
        if (!isPowerConnected) {
            document.getElementById('toolSelectButton')?.click();
        }
        return;
    }
    
    // O: Switch to Outlet mode (disabled when power is connected)
    if (event.key === 'o' || event.key === 'O') {
        event.preventDefault();
        if (!isPowerConnected) {
            document.getElementById('toolOutletButton')?.click();
        }
        return;
    }
    
    // P: Switch to Power mode
    if (event.key === 'p' || event.key === 'P') {
        event.preventDefault();
        document.getElementById('toolConnectPowerButton')?.click();
        return;
    }
    
    // Escape: Cancel current action
    if (event.key === 'Escape') {
        event.preventDefault();
        if (isDrawing) {
            cancelDrawing();
        } else if (selectedElement || selectedElements.length > 0) {
            selectedElement = null;
            selectedElements = [];
            selectionHint.classList.remove('visible');
            drawAll();
        }
        return;
    }
    
    // +, =: Zoom in
    if (event.key === '+' || event.key === '=') {
        event.preventDefault();
        document.getElementById('zoomInButton')?.click();
        return;
    }
    
    // -: Zoom out
    if (event.key === '-' || event.key === '_') {
        event.preventDefault();
        document.getElementById('zoomOutButton')?.click();
        return;
    }
    
    // 2: Switch to 2D view
    if (event.key === '2') {
        event.preventDefault();
        setView('2D');
        return;
    }
    
    // 3: Switch to 3D view
    if (event.key === '3') {
        event.preventDefault();
        setView('3D');
        return;
    }
    
    // F: Toggle fullscreen
    if (event.key === 'f' || event.key === 'F') {
        event.preventDefault();
        toggleFullscreen();
        return;
    }
    
    // Space key for canvas panning
    if (event.code === 'Space' && !event.repeat && !isBoxSelecting && !isDrawing && !isDraggingEndpoint) {
        isSpacePressed = true;
        updateCursor();
        return;
    }
    
    if (event.key === 'Shift' && !event.repeat) {
        isShiftPressed = true;
        
        if (currentTool === 'draw') {
            const modeInd = document.getElementById('modeIndicator');
            if (modeInd) {
                const ctrlText = isCtrlPressed ? ' + FREE ANGLE' : '';
                modeInd.textContent = window.configuratorTranslations.drawModeFree + ctrlText;
                modeInd.style.background = '#8B4513';
            }
            if (isDrawing) {
                drawAll();
            }
        } else if (currentTool === 'select' && isDraggingEndpoint) {
            drawAll();
        }
    }
    
    if (event.key === 'Control' && !event.repeat) {
        isCtrlPressed = true;
        
        if (currentTool === 'draw' && isDrawing) {
            const modeInd = document.getElementById('modeIndicator');
            if (modeInd) {
                const shiftText = isShiftPressed ? ' + FREE' : ' + 1M SNAP';
                modeInd.textContent = window.configuratorTranslations.drawModeFree + shiftText;
                modeInd.style.background = '#8B4513';
            }
            drawAll();
        }
    }
    
    if (event.key === 'Alt' && !event.repeat) {
        isAltPressed = true;
        
        if (currentTool === 'draw' && isDrawing) {
            drawAll();
        } else if (currentTool === 'select' && isDraggingEndpoint) {
            drawAll();
        }
    }
    
    if (selectedElement === null || editingSegmentIndex !== null) return;
    
    if (event.key === 'Delete' || event.key === 'Backspace') {
        event.preventDefault();
        
        // Delete multiple selected segments if any
        if (selectedElements.length > 0) {
            saveState('Delete multiple segments');
            
            // Sort indices in descending order to avoid index shifting during deletion
            const segmentIndices = selectedElements
                .filter(el => el.type === 'segment')
                .map(el => el.id)
                .sort((a, b) => b - a);
            
            // Collect all points that will be affected
            const affectedPoints = new Set();
            
            segmentIndices.forEach(segmentIndex => {
                if (segmentIndex >= 0 && segmentIndex < segments.length) {
                    const seg = segments[segmentIndex];
                    affectedPoints.add(seg.startIndex);
                    affectedPoints.add(seg.endIndex);
                    segments.splice(segmentIndex, 1);
                }
            });
            
            // Remove orphaned points
            affectedPoints.forEach(pointId => {
                const connections = countPointConnections(pointId);
                if (connections === 0) {
                    const pointIdx = points.findIndex(p => p.id === pointId);
                    if (pointIdx !== -1) {
                        points.splice(pointIdx, 1);
                    }
                }
            });
            
            selectedElements = [];
            selectedElement = null;
            selectionHint.classList.remove('visible');
            drawAll();
            calculateAndDisplayComponents();
        } else if (selectedElement) {
            if (selectedElement.type === 'segment') {
                deleteSelectedSegment();
            } else if (selectedElement.type === 'feature') {
                const featureId = selectedElement.id;
                const featureIndex = features.findIndex(f => f.id === featureId);
                if (featureIndex !== -1) {
                    saveState('Delete outlet');
                    features.splice(featureIndex, 1);
                    
                    if (powerConnection && powerConnection.outletId === featureId) {
                        powerConnection = null;
                        // Clear cached directions when power is removed
                        cachedSegmentOffsetDirections = null;
                        cachedTConnectorAnalysis = null;
                        cachedGlobalOffsetDirection = null;
                    }
                    
                    selectedElement = null;
                    selectionHint.classList.remove('visible');
                    drawAll();
                    calculateAndDisplayComponents();
                }
            } else if (selectedElement.type === 'power') {
                saveState('Delete power connection');
                powerConnection = null;
                // Clear cached directions when power is removed
                cachedSegmentOffsetDirections = null;
                cachedTConnectorAnalysis = null;
                cachedGlobalOffsetDirection = null;
                selectedElement = null;
                selectionHint.classList.remove('visible');
                drawAll();
                calculateAndDisplayComponents();
            }
        }
    }
}

function handleKeyUp(event) {
    // Space key release
    if (event.code === 'Space' && !event.repeat) {
        isSpacePressed = false;
        isPanning = false;
        updateCursor();
    }
    
    if (event.key === 'Shift' && !event.repeat) {
        isShiftPressed = false;
        
        if (jointTooltip) {
            jointTooltip.classList.remove('visible');
        }
        
        if (currentTool === 'draw') {
            const modeInd = document.getElementById('modeIndicator');
            if (modeInd) {
                const ctrlText = isCtrlPressed ? ' + FREE ANGLE' : '';
                modeInd.textContent = window.configuratorTranslations.drawMode1m + ctrlText;
                modeInd.style.background = isCtrlPressed ? '#8B4513' : '#D4A574';
            }
            if (isDrawing) {
                drawAll();
            }
        } else if (currentTool === 'select' && isDraggingEndpoint) {
            drawAll();
        }
    }
    
    if (event.key === 'Control' && !event.repeat) {
        isCtrlPressed = false;
        
        if (currentTool === 'draw') {
            const modeInd = document.getElementById('modeIndicator');
            if (modeInd) {
                if (isShiftPressed) {
                    modeInd.textContent = window.configuratorTranslations.drawModeFree;
                    modeInd.style.background = '#8B4513';
                } else {
                    modeInd.textContent = window.configuratorTranslations.drawMode1m;
                    modeInd.style.background = '#D4A574';
                }
            }
            if (isDrawing) {
                drawAll();
            }
        }
    }
    
    if (event.key === 'Alt' && !event.repeat) {
        isAltPressed = false;
        
        if (currentTool === 'draw' && isDrawing) {
            drawAll();
        } else if (currentTool === 'select' && isDraggingEndpoint) {
            drawAll();
        }
    }
}

function saveState(description = '') {
    const state = {
        points: JSON.parse(JSON.stringify(points)),
        segments: JSON.parse(JSON.stringify(segments)),
        features: JSON.parse(JSON.stringify(features)),
        powerConnection: powerConnection ? JSON.parse(JSON.stringify(powerConnection)) : null,
        pointCounter: pointCounter,
        description: description,
        timestamp: Date.now()
    };
    
    historyStack = historyStack.slice(0, historyIndex + 1);
    historyStack.push(state);
    
    if (historyStack.length > MAX_HISTORY) {
        historyStack.shift();
    } else {
        historyIndex++;
    }
}

function undo() {
    if (historyIndex > 0) {
        historyIndex--;
        const state = historyStack[historyIndex];
        if (state) {
            restoreState(state);
        }
    }
}

function redo() {
    if (historyIndex < historyStack.length - 1) {
        historyIndex++;
        const state = historyStack[historyIndex];
        if (state) {
            restoreState(state);
        }
    }
}

function cancelDrawing() {
    if (isDrawing) {
        isDrawing = false;
        currentDrawingStart = null;
        currentDrawingEnd = null;
        const modeInd = document.getElementById('modeIndicator');
        if (modeInd) {
            modeInd.style.display = 'none';
        }
        drawAll();
    }
}

function restoreState(state) {
    if (!state) return;
    
    points = JSON.parse(JSON.stringify(state.points));
    segments = JSON.parse(JSON.stringify(state.segments));
    window.segments = segments; // Update global reference
    features = JSON.parse(JSON.stringify(state.features));
    powerConnection = state.powerConnection ? JSON.parse(JSON.stringify(state.powerConnection)) : null;
    pointCounter = state.pointCounter;
    
    isDrawing = false;
    currentDrawingStart = null;
    currentDrawingEnd = null;
    editingSegmentIndex = null;
    selectedElement = null;
    isConnectingPower = false;
    powerConnectionStart = null;
    powerConnectionEndCandidate = null;
    isDraggingEndpoint = false;
    draggingSegmentIndex = null;
    draggingPointId = null;
    isCtrlPressed = false;
    isAltPressed = false;
    
    if (selectionHint) selectionHint.classList.remove('visible');
    if (jointTooltip) jointTooltip.classList.remove('visible');
    hideInlineInput();
    
    // CRITICAL: Recalculate power line directions if power connection exists after restore
    if (powerConnection) {
        recalculatePowerLineDirections();
    }
    
    drawAll();
    calculateAndDisplayComponents();
}

function deleteSelectedSegment() {
    if (!selectedElement || selectedElement.type !== 'segment') return;
    
    const segmentIndex = selectedElement.id;
    if (segmentIndex < 0 || segmentIndex >= segments.length) return;
    
    saveState('Delete segment');
    
    const deletedSegment = segments[segmentIndex];
    const deletedStartId = deletedSegment.startIndex;
    const deletedEndId = deletedSegment.endIndex;
    
    segments.splice(segmentIndex, 1);
    
    [deletedStartId, deletedEndId].forEach(pointId => {
        const connections = countPointConnections(pointId);
        
        if (connections === 0) {
            const pointIdx = points.findIndex(p => p.id === pointId);
            if (pointIdx !== -1) {
                points.splice(pointIdx, 1);
            }
        }
    });
    
    selectedElement = null;
    selectionHint.classList.remove('visible');
    
    drawAll();
    calculateAndDisplayComponents();
}

function setCurrentTool(toolName) {
    currentTool = toolName;
    isDrawing = false;
    isConnectingPower = false;
    powerConnectionStart = null;
    powerConnectionEndCandidate = null;
    selectionHint.classList.remove('visible');
    if (jointTooltip) jointTooltip.classList.remove('visible');
    if (window.outletSnapPreview) window.outletSnapPreview = null;
    
    const shiftState = isShiftPressed || false;
    const ctrlState = isCtrlPressed || false;
    
    // Update tool tabs active state
    const toolTabs = document.querySelectorAll('.tool-tab');
    toolTabs.forEach(tab => {
        tab.classList.remove('active');
    });
    
    if (toolName === 'select') {
        document.getElementById('toolSelectButton').classList.add('active');
    } else if (toolName === 'draw') {
        document.getElementById('toolDrawButton').classList.add('active');
    } else if (toolName === 'outlet') {
        document.getElementById('toolOutletButton').classList.add('active');
    } else if (toolName === 'connect_power') {
        document.getElementById('toolConnectPowerButton').classList.add('active');
    }
    
    // Update power button text and hide/show draw/select buttons
    updatePowerButtonState();
    
    // Set cursor based on tool
    if (toolName === 'select') {
        canvas.style.cursor = 'pointer';
    } else if (toolName === 'draw') {
        canvas.style.cursor = 'crosshair';
    } else if (toolName === 'outlet') {
        canvas.style.cursor = 'copy';
    } else if (toolName === 'connect_power') {
        canvas.style.cursor = 'none'; // Hide cursor, we draw custom one
    }
    
    selectedElement = null;
    drawAll();
}

function updatePowerButtonState() {
    const drawBtn = document.getElementById('toolDrawButton');
    const selectBtn = document.getElementById('toolSelectButton');
    const powerBtn = document.getElementById('toolConnectPowerButton');
    const powerBtnText = powerBtn ? powerBtn.querySelector('span') : null;
    const undoBtn = document.getElementById('undoButton');
    const clearBtn = document.getElementById('clearButton');
    
    // Check if power is connected
    const isPowerConnected = powerConnection && powerConnection.segmentIndex !== null;
    
    if (isPowerConnected) {
        // Hide draw, select, undo and clear buttons
        if (drawBtn) drawBtn.style.display = 'none';
        if (selectBtn) selectBtn.style.display = 'none';
        if (undoBtn) undoBtn.style.display = 'none';
        if (clearBtn) clearBtn.style.display = 'none';
        
        // Change power button text to disconnect and add special styling class
        if (powerBtn) powerBtn.classList.add('power-connected');
        if (powerBtnText) {
            const translations = window.configuratorTranslations || {};
            powerBtnText.textContent = translations.disconnect_power || 'Disconnect power and return to drawing';
        }
    } else {
        // Show draw, select, undo and clear buttons
        if (drawBtn) drawBtn.style.display = '';
        if (selectBtn) selectBtn.style.display = '';
        if (undoBtn) undoBtn.style.display = '';
        if (clearBtn) clearBtn.style.display = '';
        
        // Change power button text back to connect and remove special styling class
        if (powerBtn) powerBtn.classList.remove('power-connected');
        if (powerBtnText) {
            const translations = window.configuratorTranslations || {};
            powerBtnText.textContent = translations.connect_power || 'Connect power';
        }
    }
}

function hideInlineInput() {
    inlineLengthInput.style.display = 'none';
    editingSegmentIndex = null;
}

function exportPDF() {
    alert('PDF export functionality would be implemented here');
}

function saveConfiguration() {
    const config = {
        points: points,
        segments: segments,
        features: features,
        powerConnection: powerConnection,
        trackColor: trackColor,
        suspensionEnabled: suspensionEnabled,
        timestamp: new Date().toISOString()
    };
    const dataStr = JSON.stringify(config);
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
    const exportFileDefaultName = 'rendl-track-config.json';
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
}

function generatePDFContent() {
    // Generate a text-based configuration summary
    let summary = 'RENDL TRACK CONFIGURATION\\n';
    summary += '='.repeat(50) + '\\n\\n';
    
    // Configuration details
    summary += `Date: ${new Date().toLocaleString()}\\n`;
    summary += `Track Color: ${trackColor.toUpperCase()}\\n`;
    summary += `Suspension: ${suspensionEnabled ? 'Yes' : 'No'}\\n\\n`;
    
    // Track layout info
    summary += `Total Segments: ${segments.length}\\n`;
    let totalLength = 0;
    segments.forEach(seg => {
        const len = seg.desiredLengthMeters || seg.calcLengthMeters;
        totalLength += len;
    });
    summary += `Total Track Length: ${totalLength.toFixed(1)}m\\n\\n`;
    
    // Power connection
    if (powerConnection) {
        summary += `Power Connection: ${powerConnection.type.toUpperCase()}\\n\\n`;
    }
    
    // Component list
    summary += 'COMPONENTS REQUIRED:\\n';
    summary += '-'.repeat(50) + '\\n';
    
    const sortedSkus = Object.keys(componentList).sort();
    let totalCost = 0;
    
    sortedSkus.forEach(sku => {
        const item = componentList[sku];
        const shopifyProduct = SHOPIFY_PRODUCTS[item.code];
        const productTitle = shopifyProduct?.productTitle || '';
        const variantTitle = shopifyProduct?.title || '';
        const productName = productTitle && variantTitle ? `${productTitle} - ${variantTitle}` : (productTitle || variantTitle || sku);
        const unitPrice = shopifyProduct?.price || 0;
        const itemCost = item.count * unitPrice;
        const priceFormatted = shopifyProduct?.priceFormatted || 'N/A';
        
        totalCost += itemCost;
        summary += `${item.count}x ${productName} (${item.code})\\n`;
        summary += `   ${priceFormatted}\\n`;
    });
    
    summary += '-'.repeat(50) + '\\n';
    // Format total cost with Shopify money format
    let formattedPrice;
    if (window.Shopify && window.Shopify.formatMoney) {
        // Convert to cents for Shopify formatting
        const priceInCents = Math.round(totalCost * 100);
        formattedPrice = window.Shopify.formatMoney(priceInCents);
    } else {
        // Fallback: format with spaces between thousands and currency after number
        let price = totalCost.toFixed(2);
        // Remove .00 for whole numbers
        if (price.endsWith('.00')) {
            price = price.replace('.00', '');
        }
        // Add spaces between thousands
        price = price.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
        formattedPrice = `${price} ${window.CURRENCY_SYMBOL || 'Kč'}`;
    }
    summary += `TOTAL COST: ${formattedPrice}\\n`;
    
    return summary;
}

// Verification Modal Functions
function showCartVerificationModal() {
    const modal = document.getElementById('verificationModal');
    const colorLabel = document.getElementById('verifyColorLabel');
    
    // Update color label to show current color with translation
    const colorUpper = trackColor.toUpperCase();
    const translations = window.configuratorTranslations || {};
    const colorCheckText = translations.modal_verify?.check_color || 'I am certain "__COLOR__" is the correct color I want for my track system.';
    console.log('Color check text template:', colorCheckText);
    console.log('Current track color:', colorUpper);
    const formattedText = colorCheckText.replace('__COLOR__', colorUpper);
    console.log('Formatted text:', formattedText);
    colorLabel.innerHTML = formattedText;
    
    // Reset checkboxes
    document.getElementById('verifyConfig').checked = false;
    document.getElementById('verifyColor').checked = false;
    document.getElementById('verifyPower').checked = false;
    
    // Reset button states
    const addToCartBtn = document.getElementById('modalAddToCartBtn');
    const addAndViewBtn = document.getElementById('modalAddAndViewBtn');
    
    if (addToCartBtn) {
        addToCartBtn.textContent = window.configuratorTranslations.addToCart;
        addToCartBtn.disabled = true;
    }
    if (addAndViewBtn) {
        addAndViewBtn.textContent = window.configuratorTranslations.addAndView;
        addAndViewBtn.style.pointerEvents = 'none';
        addAndViewBtn.style.opacity = '0.5';
    }
    
    updateAddToCartButton();
    
    modal.classList.add('visible');
}

function closeVerificationModal() {
    const modal = document.getElementById('verificationModal');
    modal.classList.remove('visible');
}

function showSendForVerificationModal() {
    const modal = document.getElementById('sendVerificationModal');
    modal.classList.add('visible');
}

function closeSendVerificationModal() {
    const modal = document.getElementById('sendVerificationModal');
    modal.classList.remove('visible');
}

function copyConfigLinkAndCloseSendModal() {
    console.log('copyConfigLinkAndCloseSendModal called');
    
    if (segments.length === 0 && features.length === 0) {
        alert(window.TRANSLATIONS.startDrawing || 'Please create a configuration first.');
        return;
    }
    
    const shareUrl = generateConfigLink();
    
    if (!shareUrl) {
        console.error('Failed to generate share URL');
        return;
    }
    
    console.log('Attempting to copy URL to clipboard from modal...');
    
    // Copy to clipboard
    navigator.clipboard.writeText(shareUrl).then(() => {
        const btnModal = document.getElementById('copyLinkBtnModal');
        const textElementModal = document.getElementById('copyLinkTextModal');
        
        const copiedText = window.configuratorTranslations?.copied || 'Copied!';
        
        // Update modal button
        if (btnModal && textElementModal) {
            textElementModal.textContent = copiedText;
            btnModal.style.background = '#2A7F62';
            btnModal.style.borderColor = '#2A7F62';
            btnModal.style.color = '#FFFFFF';
            
            const svg = btnModal.querySelector('svg path');
            if (svg) {
                svg.setAttribute('stroke', '#FFFFFF');
            }
            
            // Reset after 2 seconds and close modal
            setTimeout(() => {
                const originalText = window.configuratorTranslations?.modal_send?.copy_link_btn || 'Copy link';
                textElementModal.textContent = originalText;
                btnModal.style.background = '';
                btnModal.style.borderColor = '';
                btnModal.style.color = '';
                
                const svg = btnModal.querySelector('svg path');
                if (svg) {
                    svg.setAttribute('stroke', 'currentColor');
                }
                
                // Close modal
                closeSendVerificationModal();
            }, 2000);
        }
        
        console.log('Configuration link copied to clipboard successfully from modal');
    }).catch(err => {
        console.error('Failed to copy configuration link from modal:', err);
        alert('Failed to copy link to clipboard. Please try again.');
    });
}

// Link sharing functions
function generateConfigLink() {
    console.log('generateConfigLink called');
    console.log('LZString available:', typeof LZString !== 'undefined');
    
    const config = {
        points: points,
        segments: segments,
        features: features,
        powerConnection: powerConnection,
        pointCounter: pointCounter,
        trackColor: trackColor,
        suspensionEnabled: suspensionEnabled,
        canvasZoom: canvasZoom,
        canvasPanX: canvasPanX,
        canvasPanY: canvasPanY
    };
    
    const jsonString = JSON.stringify(config);
    console.log('Config data:', config);
    console.log('JSON string length before compression:', jsonString.length);
    
    // Check if LZString is available
    if (typeof LZString === 'undefined') {
        console.error('LZString library not loaded!');
        alert('Configuration sharing is not available. Please reload the page.');
        return null;
    }
    
    // Use LZ-String compression for shorter URLs
    const compressedData = LZString.compressToEncodedURIComponent(jsonString);
    console.log('Compressed string length:', compressedData.length);
    console.log('Compression ratio:', ((1 - compressedData.length / jsonString.length) * 100).toFixed(1) + '%');
    
    const currentUrl = window.location.href.split('?')[0];
    const shareUrl = `${currentUrl}?config=${compressedData}`;
    
    console.log('Generated share URL length:', shareUrl.length);
    
    return shareUrl;
}

function copyConfigLink() {
    console.log('copyConfigLink called');
    console.log('Segments:', segments.length, 'Features:', features.length);
    
    if (segments.length === 0 && features.length === 0) {
        alert(window.TRANSLATIONS.startDrawing || 'Please create a configuration first.');
        return;
    }
    
    const shareUrl = generateConfigLink();
    
    if (!shareUrl) {
        console.error('Failed to generate share URL');
        return;
    }
    
    console.log('Attempting to copy URL to clipboard...');
    
    // Copy to clipboard
    navigator.clipboard.writeText(shareUrl).then(() => {
        // Visual feedback for both buttons
        const btn = document.getElementById('copyLinkBtn');
        const btnResults = document.getElementById('copyLinkBtnResults');
        const textElement = document.getElementById('copyLinkText');
        const textElementResults = document.getElementById('copyLinkTextResults');
        const originalText = textElement ? textElement.textContent : '';
        const originalTitle = btn ? btn.title : '';
        
        // Change text to "Copied!"
        const copiedText = window.configuratorTranslations?.copied || 'Copied!';
        
        // Update main button (header)
        if (btn && textElement) {
            textElement.textContent = copiedText;
            btn.title = copiedText;
            btn.style.background = '#2A7F62';
            btn.style.borderColor = '#2A7F62';
            btn.style.color = '#FFFFFF';
            
            const svg = btn.querySelector('svg');
            const img = btn.querySelector('img');
            if (svg) {
                const paths = svg.querySelectorAll('path');
                paths.forEach(path => {
                    path.setAttribute('stroke', '#FFFFFF');
                });
            }
            if (img) {
                img.style.filter = 'brightness(0) invert(1)';
            }
        }
        
        // Update results button
        if (btnResults && textElementResults) {
            textElementResults.textContent = copiedText;
            btnResults.style.background = '#2A7F62';
            btnResults.style.borderColor = '#2A7F62';
            btnResults.style.color = '#FFFFFF';
            
            const svg = btnResults.querySelector('svg');
            if (svg) {
                const paths = svg.querySelectorAll('path');
                paths.forEach(path => {
                    path.setAttribute('stroke', '#FFFFFF');
                });
            }
        }
        
        // Reset after 2 seconds
        setTimeout(() => {
            // Reset main button
            if (btn && textElement) {
                textElement.textContent = originalText;
                btn.title = originalTitle;
                btn.style.background = '';
                btn.style.borderColor = '';
                btn.style.color = '';
                
                const svg = btn.querySelector('svg');
                const img = btn.querySelector('img');
                if (svg) {
                    const paths = svg.querySelectorAll('path');
                    paths.forEach(path => {
                        path.setAttribute('stroke', '#000000');
                    });
                }
                if (img) {
                    img.style.filter = '';
                }
            }
            
            // Reset results button
            if (btnResults && textElementResults) {
                textElementResults.textContent = originalText;
                btnResults.style.background = '';
                btnResults.style.borderColor = '';
                btnResults.style.color = '';
                
                const svg = btnResults.querySelector('svg');
                if (svg) {
                    const paths = svg.querySelectorAll('path');
                    paths.forEach(path => {
                        path.setAttribute('stroke', 'currentColor');
                    });
                }
            }
        }, 2000);
        
        console.log('Configuration link copied to clipboard:', shareUrl);
    }).catch(err => {
        console.error('Failed to copy link:', err);
        // Fallback: show the link in a prompt
        prompt('Copy this link:', shareUrl);
    });
}

function showInsertLinkModal() {
    const modal = document.getElementById('insertLinkModal');
    const input = document.getElementById('insertLinkInput');
    const errorMsg = document.getElementById('linkErrorMessage');
    
    // Reset modal
    if (input) input.value = '';
    if (errorMsg) errorMsg.style.display = 'none';
    
    if (modal) modal.classList.add('visible');
}

function closeInsertLinkModal() {
    const modal = document.getElementById('insertLinkModal');
    if (modal) modal.classList.remove('visible');
}

function loadConfigFromLink() {
    console.log('loadConfigFromLink called');
    const input = document.getElementById('insertLinkInput');
    const errorMsg = document.getElementById('linkErrorMessage');
    
    if (!input || !input.value.trim()) {
        console.log('No input value');
        if (errorMsg) {
            errorMsg.textContent = window.configuratorTranslations.pasteLink;
            errorMsg.style.display = 'block';
        }
        return;
    }
    
    try {
        const urlString = input.value.trim();
        console.log('Processing URL:', urlString);
        
        const url = new URL(urlString);
        const configParam = url.searchParams.get('config');
        
        console.log('Config param length:', configParam ? configParam.length : 0);
        
        if (!configParam) {
            throw new Error('No configuration found in URL');
        }
        
        // Check if LZString is available
        if (typeof LZString === 'undefined') {
            throw new Error('LZString library not loaded');
        }
        
        console.log('Decompressing data...');
        
        // Decompress using LZ-String
        const jsonString = LZString.decompressFromEncodedURIComponent(configParam);
        
        console.log('Decompressed string length:', jsonString ? jsonString.length : 0);
        
        if (!jsonString) {
            throw new Error('Decompression failed - the link may be corrupted');
        }
        
        console.log('Parsing JSON...');
        const config = JSON.parse(jsonString);
        
        console.log('Parsed config:', config);
        
        // Validate configuration - check that we have at least segments array
        if (!config.segments || !Array.isArray(config.segments)) {
            throw new Error('Invalid configuration format - missing segments array');
        }
        
        console.log('Loading configuration...');
        
        // Load configuration
        loadConfiguration(config);
        
        // Close modal
        closeInsertLinkModal();
        
        // Show success message
        console.log('Configuration loaded successfully from link');
        
    } catch (error) {
        console.error('Error loading configuration from link:', error);
        console.error('Error details:', error.message, error.stack);
        if (errorMsg) {
            errorMsg.textContent = `Invalid configuration link: ${error.message}`;
            errorMsg.style.display = 'block';
        }
    }
}

function loadConfiguration(config) {
    console.log('loadConfiguration: Starting to load config', config);
    
    // Clear current configuration
    points = [];
    segments = [];
    window.segments = segments; // Update global reference
    features = [];
    powerConnection = null;
    
    // Load points first (needed for segments)
    if (config.points && Array.isArray(config.points)) {
        points = config.points;
        console.log('loadConfiguration: Loaded points:', points.length);
    }
    
    // Load point counter
    if (config.pointCounter !== undefined) {
        pointCounter = config.pointCounter;
        console.log('loadConfiguration: Loaded pointCounter:', pointCounter);
    }
    
    // Load segments
    if (config.segments && Array.isArray(config.segments)) {
        segments = config.segments;
        window.segments = segments; // Update global reference
        console.log('loadConfiguration: Loaded segments:', segments.length);
    }
    
    // Load features (outlets)
    if (config.features && Array.isArray(config.features)) {
        features = config.features;
        console.log('loadConfiguration: Loaded features:', features.length);
    }
    
    // Load power connection
    if (config.powerConnection) {
        powerConnection = config.powerConnection;
        console.log('loadConfiguration: Loaded powerConnection');
    }
    
    // Load track color
    if (config.trackColor) {
        trackColor = config.trackColor;
        
        // Update global state
        if (window.configuratorState) {
            window.configuratorState.selectedColor = trackColor;
        }
        
        const colorRadio = document.getElementById(`color${config.trackColor.charAt(0).toUpperCase() + config.trackColor.slice(1)}`);
        if (colorRadio) {
            colorRadio.checked = true;
        }
    }
    
    // Load suspension
    if (config.suspensionEnabled !== undefined) {
        suspensionEnabled = config.suspensionEnabled;
        const suspensionToggle = document.getElementById('suspensionToggle');
        const suspensionLabel = document.getElementById('suspensionLabel');
        const suspensionStatus = document.getElementById('suspensionStatus');
        if (suspensionToggle) {
            suspensionToggle.checked = suspensionEnabled;
        }
        if (suspensionLabel) {
            suspensionLabel.textContent = suspensionEnabled 
                ? (window.TRANSLATIONS.on || 'ON') 
                : (window.TRANSLATIONS.off || 'OFF');
        }
        if (suspensionStatus) {
            suspensionStatus.textContent = `(${suspensionEnabled 
                ? (window.TRANSLATIONS.on || 'ON') 
                : (window.TRANSLATIONS.off || 'OFF')})`;
        }
    }
    
    // Load zoom and pan if available
    if (config.canvasZoom !== undefined) {
        canvasZoom = config.canvasZoom;
        updateZoomIndicator();
    }
    if (config.canvasPanX !== undefined) {
        canvasPanX = config.canvasPanX;
    }
    if (config.canvasPanY !== undefined) {
        canvasPanY = config.canvasPanY;
    }
    
    // Update history
    saveState('Loaded configuration from link');
    
    console.log('loadConfiguration: Before redraw - points:', points.length, 'segments:', segments.length, 'features:', features.length);
    
    // Recalculate and redraw
    updateAllSegmentJoints();
    
    // CRITICAL: Recalculate power line directions if power connection exists
    if (powerConnection) {
        console.log('loadConfiguration: Recalculating power line directions...');
        recalculatePowerLineDirections();
    }
    
    calculateAndDisplayComponents();
    updatePowerButtonState();
    drawAll();
    
    console.log('loadConfiguration: Configuration loaded and redrawn successfully');
    
    // Update 3D view if active
    if (is3DView) {
        update3DFromCanvas();
    }
}

// Check for configuration in URL on page load
function loadConfigFromURL() {
    try {
        console.log('loadConfigFromURL: Checking URL for configuration...');
        const urlParams = new URLSearchParams(window.location.search);
        const configParam = urlParams.get('config');
        
        if (configParam) {
            console.log('loadConfigFromURL: Found configuration in URL');
            console.log('loadConfigFromURL: Config param length:', configParam.length);
            
            // Check if LZString is available
            if (typeof LZString === 'undefined') {
                throw new Error('LZString library not loaded');
            }
            
            console.log('loadConfigFromURL: Decompressing data...');
            
            // Decompress using LZ-String
            const jsonString = LZString.decompressFromEncodedURIComponent(configParam);
            
            console.log('loadConfigFromURL: Decompressed string length:', jsonString ? jsonString.length : 0);
            
            if (!jsonString) {
                throw new Error('Decompression failed - the link may be corrupted');
            }
            
            console.log('loadConfigFromURL: Parsing JSON...');
            const config = JSON.parse(jsonString);
            
            console.log('loadConfigFromURL: Parsed config:', config);
            
            loadConfiguration(config);
            
            console.log('loadConfigFromURL: Configuration loaded successfully from URL');
        } else {
            console.log('loadConfigFromURL: No configuration found in URL');
        }
    } catch (error) {
        console.error('loadConfigFromURL: Error loading configuration from URL:', error);
        console.error('loadConfigFromURL: Error details:', error.message, error.stack);
        alert(`Could not load configuration from URL: ${error.message}`);
    }
}

function updateAddToCartButton() {
    const configChecked = document.getElementById('verifyConfig').checked;
    const colorChecked = document.getElementById('verifyColor').checked;
    const powerChecked = document.getElementById('verifyPower').checked;
    const addToCartBtn = document.getElementById('modalAddToCartBtn');
    const addAndViewBtn = document.getElementById('modalAddAndViewBtn');
    
    const allChecked = configChecked && colorChecked && powerChecked;
    
    if (addToCartBtn) {
        addToCartBtn.disabled = !allChecked;
    }
    if (addAndViewBtn) {
        if (allChecked) {
            addAndViewBtn.style.pointerEvents = 'auto';
            addAndViewBtn.style.opacity = '1';
        } else {
            addAndViewBtn.style.pointerEvents = 'none';
            addAndViewBtn.style.opacity = '0.5';
        }
    }
}

async function proceedToCart(event) {
    // Check if this is from the "Add and View" button
    const shouldOpenCatalog = event && event.target && event.target.id === 'modalAddAndViewBtn';
    
    if (event && event.preventDefault) {
        event.preventDefault();
    }
    
    closeVerificationModal();
    
    // Show loading state on both buttons
    const addToCartBtn = document.getElementById('modalAddToCartBtn');
    const addAndViewBtn = document.getElementById('modalAddAndViewBtn');
    const originalText = addToCartBtn ? addToCartBtn.textContent : 'Add to Cart';
    const originalViewText = addAndViewBtn ? addAndViewBtn.textContent : 'Add and View Lamps Catalog';
    
    if (addToCartBtn) {
        addToCartBtn.textContent = window.configuratorTranslations.adding;
        addToCartBtn.disabled = true;
    }
    if (addAndViewBtn) {
        addAndViewBtn.textContent = window.configuratorTranslations.adding;
        addAndViewBtn.style.pointerEvents = 'none';
    }
    
    try {
        // Collect all components to add to cart
        const items = [];
        const componentElements = document.querySelectorAll('.component-item[data-variant-id]');
        
        componentElements.forEach(element => {
            const variantId = element.dataset.variantId;
            const quantity = parseInt(element.dataset.quantity);
            
            if (variantId && quantity > 0) {
                items.push({
                    variantId: parseInt(variantId),
                    quantity: quantity
                });
            }
        });
        
        if (items.length === 0) {
            throw new Error('No items to add to cart');
        }
        
        // Add to Shopify cart
        const cartResponse = await addToShopifyCart(items);
        
        // Show cart notification popup (same as in product-form.liquid)
        const cartNotification = document.getElementById('cart-notification');
        if (cartNotification) {
            // Update product information in popup with first item
            const firstItem = items[0];
            const cartNotificationProduct = document.getElementById('cart-notification-product');
            
            if (cartNotificationProduct && firstItem) {
                // Update image
                const imageElement = cartNotificationProduct.querySelector('img');
                if (imageElement) {
                    imageElement.src = firstItem.image || '';
                    imageElement.alt = firstItem.title || '';
                }
                
                // Update product name
                const nameElement = cartNotificationProduct.querySelector('.cart-notification-product__name');
                if (nameElement) {
                    nameElement.textContent = firstItem.title || '';
                }
            }
            
            // Refresh cart to update counters
            fetch(window.Shopify.routes.root + 'cart.js')
                .then(response => response.json())
                .then(cart => {
                    // Update view cart button
                    const viewCartButton = document.getElementById('cart-notification-button');
                    if (viewCartButton) {
                        const lang = document.documentElement.getAttribute('lang') || 'en';
                        let viewCartText = 'View cart';
                        
                        if (lang.startsWith('cs')) viewCartText = 'Zobrazit košík';
                        else if (lang.startsWith('sk')) viewCartText = 'Zobraziť košík';
                        else if (lang.startsWith('de')) viewCartText = 'Warenkorb anzeigen';
                        else if (lang.startsWith('fr')) viewCartText = 'Voir le panier';
                        else if (lang.startsWith('it')) viewCartText = 'Visualizza carrello';
                        else if (lang.startsWith('es')) viewCartText = 'Ver carrito';
                        else if (lang.startsWith('nl')) viewCartText = 'Winkelwagen bekijken';
                        else if (lang.startsWith('pl')) viewCartText = 'Zobacz koszyk';
                        else if (lang.startsWith('hu')) viewCartText = 'Kosár megtekintése';
                        else if (lang.startsWith('ro')) viewCartText = 'Vezi coșul';
                        else if (lang.startsWith('bg')) viewCartText = 'Виж количката';
                        else if (lang.startsWith('hr')) viewCartText = 'Pogledaj košaricu';
                        else if (lang.startsWith('fi')) viewCartText = 'Näytä ostoskori';
                        else if (lang.startsWith('el')) viewCartText = 'Προβολή καλαθιού';
                        else if (lang.startsWith('pt-PT')) viewCartText = 'Ver carrinho';
                        else if (lang.startsWith('sl')) viewCartText = 'Poglej košarico';
                        else if (lang.startsWith('sv')) viewCartText = 'Visa varukorg';
                        
                        viewCartButton.textContent = `${viewCartText} (${cart.item_count})`;
                    }
                    
                    // Show popup
                    cartNotification.classList.add('active');
                    cartNotification.removeAttribute('hidden');
                    
                    // Update all cart counters
                    const headerCartCounters = document.querySelectorAll('.cart-count-bubble, .cart-count, .header__cart-count, .site-header__cart-count');
                    headerCartCounters.forEach(counter => {
                        counter.textContent = cart.item_count;
                        if (cart.item_count > 0 && counter.hasAttribute('hidden')) {
                            counter.removeAttribute('hidden');
                        }
                    });
                });
        }
        
        // Success! Handle based on which button was clicked
        if (shouldOpenCatalog) {
            // Open catalog in new tab and stay on configurator
            const catalogUrl = addAndViewBtn.getAttribute('href') || '/collections/all';
            setTimeout(() => {
                window.open(catalogUrl, '_blank');
            }, 500);
            
            // Reset buttons
            if (addToCartBtn) {
                addToCartBtn.textContent = originalText;
                addToCartBtn.disabled = false;
            }
            if (addAndViewBtn) {
                addAndViewBtn.textContent = originalViewText;
                addAndViewBtn.style.pointerEvents = 'auto';
            }
        } else {
            // Just add to cart and stay on page
            // Reset buttons
            if (addToCartBtn) {
                addToCartBtn.textContent = originalText;
                addToCartBtn.disabled = false;
            }
            if (addAndViewBtn) {
                addAndViewBtn.textContent = originalViewText;
                addAndViewBtn.style.pointerEvents = 'auto';
            }
        }
        
    } catch (error) {
        console.error('Error adding to cart:', error);
        alert('Error adding items to cart. Please try again or contact support.');
        
        // Reset button states
        if (addToCartBtn) {
            addToCartBtn.textContent = originalText;
            addToCartBtn.disabled = false;
        }
        if (addAndViewBtn) {
            addAndViewBtn.textContent = originalViewText;
            addAndViewBtn.style.pointerEvents = 'auto';
        }
    }
}

function requestQuote() {
    sendForVerification();
}

function addToCart() {
    showCartVerificationModal();
}

// Add event listeners for checkboxes
window.addEventListener('DOMContentLoaded', () => {
    document.getElementById('verifyConfig').addEventListener('change', updateAddToCartButton);
    document.getElementById('verifyColor').addEventListener('change', updateAddToCartButton);
    document.getElementById('verifyPower').addEventListener('change', updateAddToCartButton);
    
    // Close modal when clicking outside
    document.getElementById('verificationModal').addEventListener('click', (e) => {
        if (e.target.id === 'verificationModal') {
            closeVerificationModal();
        }
    });
    
    // Close send verification modal when clicking outside
    document.getElementById('sendVerificationModal').addEventListener('click', (e) => {
        if (e.target.id === 'sendVerificationModal') {
            closeSendVerificationModal();
        }
    });
});

let clearAllClickCount = 0;
let clearAllTimer = null;

window.clearAll = function() {
    if (clearAllClickCount === 0) {
        clearAllClickCount = 1;
        const btn = document.getElementById('clearButton');
        const originalText = btn.textContent;
        btn.textContent = `⚠ ${window.configuratorTranslations.confirmClear}`;
        btn.style.backgroundColor = '#DC3545';
        btn.style.color = '#FFF';
        
        clearAllTimer = setTimeout(() => {
            clearAllClickCount = 0;
            btn.textContent = originalText;
            btn.style.backgroundColor = '';
            btn.style.color = '';
        }, 3000);
        
        return;
    }
    
    clearAllClickCount = 0;
    clearTimeout(clearAllTimer);
    
    const btn = document.getElementById('clearButton');
    btn.textContent = window.configuratorTranslations.clearAll;
    btn.style.backgroundColor = '';
    btn.style.color = '';
    
    points = [];
    segments = [];
    window.segments = segments; // Update global reference
    features = [];
    componentList = {};
    pointCounter = 0;
    historyIndex = -1;
    historyStack = [];
    powerConnection = null;
    isDrawing = false;
    currentDrawingStart = null;
    currentDrawingEnd = null;
    editingSegmentIndex = null;
    selectedElement = null;
    isConnectingPower = false;
    powerConnectionStart = null;
    powerConnectionEndCandidate = null;
    isDraggingEndpoint = false;
    draggingSegmentIndex = null;
    draggingPointId = null;
    dragStartPos = null;
    isShiftPressed = false;
    isCtrlPressed = false;
    isAltPressed = false;
    segmentJointPositions.clear();
    window.outletSnapPreview = null;
    
    drawAll();
    calculateAndDisplayComponents(); // This will hide UI elements automatically
};

// Save configuration to JSON file
window.saveConfiguration = function() {
    const config = {
        version: '1.0',
        timestamp: new Date().toISOString(),
        data: {
            points: points,
            segments: segments,
            features: features,
            powerConnection: powerConnection,
            pointCounter: pointCounter,
            canvasZoom: canvasZoom,
            canvasPanX: canvasPanX,
            canvasPanY: canvasPanY
        }
    };
    
    const json = JSON.stringify(config, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    
    const a = document.createElement('a');
    a.href = url;
    a.download = `track-configuration-${new Date().getTime()}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    // Close dropdown menu
    document.getElementById('dropdownMenu').classList.remove('show');
    
    console.log('Configuration saved successfully');
};

// Load configuration from JSON file
// Load configuration from file (JSON file upload)
window.loadConfigurationFromFile = function(file) {
    if (!file) return;
    
    console.log('Loading configuration from file:', file.name);
    
    const reader = new FileReader();
    reader.onload = function(e) {
        try {
            const config = JSON.parse(e.target.result);
            
            if (!config.version || !config.data) {
                throw new Error('Invalid configuration file format');
            }
            
            // Restore data
            points = config.data.points || [];
            segments = config.data.segments || [];
            window.segments = segments; // Update global reference
            features = config.data.features || [];
            powerConnection = config.data.powerConnection || null;
            pointCounter = config.data.pointCounter || 0;
            canvasZoom = config.data.canvasZoom || 1;
            canvasPanX = config.data.canvasPanX || 0;
            canvasPanY = config.data.canvasPanY || 0;
            
            // Reset state
            historyIndex = -1;
            historyStack = [];
            selectedElement = null;
            editingSegmentIndex = null;
            isDrawing = false;
            currentDrawingStart = null;
            currentDrawingEnd = null;
            isDraggingEndpoint = false;
            draggingSegmentIndex = null;
            draggingPointId = null;
            dragStartPos = null;
            isConnectingPower = false;
            powerConnectionStart = null;
            powerConnectionEndCandidate = null;
            selectedElements.clear();
            segmentJointPositions.clear();
            
            // Redraw and recalculate
            drawAll();
            calculateAndDisplayComponents();
            
            console.log('Configuration loaded successfully from file');
            alert('Configuration loaded successfully!');
            
        } catch (error) {
            console.error('Error loading configuration from file:', error);
            alert('Error loading configuration file. Please check the file format.');
        }
    };
    
    reader.onerror = function() {
        console.error('Error reading file');
        alert('Error reading file. Please try again.');
    };
    
    reader.readAsText(file);
    
    // Close dropdown menu if open
    const dropdownMenu = document.getElementById('dropdownMenu');
    if (dropdownMenu) {
        dropdownMenu.classList.remove('show');
    }
};

// 3D Scene variables
let scene3D, camera3D, renderer3D, controls3D;
let trackGroup3D;

function init3DView() {
    const container = document.getElementById('threeD-container');
    if (!container || scene3D) return; // Already initialized
    
    // Create scene
    scene3D = new THREE.Scene();
    scene3D.background = new THREE.Color(0xf5f5f5);
    
    // Create camera
    camera3D = new THREE.PerspectiveCamera(
        50,
        container.clientWidth / container.clientHeight,
        0.1,
        1000
    );
    camera3D.position.set(5, 5, 5);
    camera3D.lookAt(0, 0, 0);
    
    // Create renderer
    renderer3D = new THREE.WebGLRenderer({ antialias: true });
    renderer3D.setSize(container.clientWidth, container.clientHeight);
    renderer3D.setPixelRatio(window.devicePixelRatio);
    renderer3D.shadowMap.enabled = true;
    renderer3D.shadowMap.type = THREE.PCFSoftShadowMap;
    container.appendChild(renderer3D.domElement);
    
    // Add lights - increased intensity for better visibility
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.8);
    scene3D.add(ambientLight);
    
    const directionalLight = new THREE.DirectionalLight(0xffffff, 1.0);
    directionalLight.position.set(5, 10, 5);
    directionalLight.castShadow = true;
    directionalLight.shadow.camera.near = 0.1;
    directionalLight.shadow.camera.far = 50;
    directionalLight.shadow.camera.left = -10;
    directionalLight.shadow.camera.right = 10;
    directionalLight.shadow.camera.top = 10;
    directionalLight.shadow.camera.bottom = -10;
    scene3D.add(directionalLight);
    
    // Add second directional light from opposite side
    const directionalLight2 = new THREE.DirectionalLight(0xffffff, 0.5);
    directionalLight2.position.set(-5, 8, -5);
    scene3D.add(directionalLight2);
    
    // Add grid - larger size for bigger configurations
    const gridHelper = new THREE.GridHelper(40, 40, 0xcccccc, 0xe0e0e0);
    gridHelper.position.y = -0.01;
    scene3D.add(gridHelper);
    
    // Add axes helper
    const axesHelper = new THREE.AxesHelper(2);
    scene3D.add(axesHelper);
    
    // Create group for tracks
    trackGroup3D = new THREE.Group();
    scene3D.add(trackGroup3D);
    
    // Add OrbitControls if available
    if (THREE.OrbitControls) {
        controls3D = new THREE.OrbitControls(camera3D, renderer3D.domElement);
        controls3D.enableDamping = true;
        controls3D.dampingFactor = 0.05;
        controls3D.screenSpacePanning = false;
        controls3D.minDistance = 2;
        controls3D.maxDistance = 50;
        controls3D.maxPolarAngle = Math.PI / 2;
    }
    
    // Handle window resize
    window.addEventListener('resize', () => {
        if (!is3DView || !renderer3D) return;
        const container = document.getElementById('threeD-container');
        camera3D.aspect = container.clientWidth / container.clientHeight;
        camera3D.updateProjectionMatrix();
        renderer3D.setSize(container.clientWidth, container.clientHeight);
    });
    
    // Start animation loop
    animate3D();
    
    console.log('3D View initialized');
}

function animate3D() {
    if (!renderer3D || !scene3D) return;
    
    requestAnimationFrame(animate3D);
    
    if (controls3D) {
        controls3D.update();
    }
    
    renderer3D.render(scene3D, camera3D);
}

function update3DFromCanvas() {
    if (!scene3D || !trackGroup3D) {
        init3DView();
        if (!trackGroup3D) return;
    }
    
    console.log('=== Updating 3D view ===');
    console.log('Points:', points.length);
    console.log('Segments:', segments.length);
    console.log('Features:', features.length);
    console.log('PowerConnection:', powerConnection);
    console.log('BASE_CANVAS_SIZE:', BASE_CANVAS_SIZE);
    
    if (segments.length === 0) {
        console.warn('No segments to render in 3D!');
    }
    
    // Clear existing tracks
    while (trackGroup3D.children.length > 0) {
        const child = trackGroup3D.children[0];
        if (child.geometry) child.geometry.dispose();
        if (child.material) child.material.dispose();
        trackGroup3D.remove(child);
    }
    
    // Get track color
    const trackColorInput = document.querySelector('input[name="trackColor"]:checked');
    const colorValue = trackColorInput ? trackColorInput.value : 'black';
    const trackColor = colorValue === 'white' ? 0xFFFFFF : 
                       colorValue === 'silver' ? 0xC0C0C0 : 0x2C2C2C;
    
    console.log('Track color:', colorValue, trackColor);
    
    // Track dimensions (in meters) - increased significantly for better visibility
    const trackWidth = 0.24;  // 240mm width (3x bigger)
    const trackHeight = 0.12; // 120mm height (3x bigger)
    const trackElevation = 5.0; // Height from ground (5 meters - ceiling system)
    const scale = 0.1; // Scale from canvas units to 3D meters
    
    // Create material for tracks
    const trackMaterial = new THREE.MeshStandardMaterial({
        color: trackColor,
        metalness: 0.7,
        roughness: 0.3,
        wireframe: false, // Ensure solid rendering
        side: THREE.DoubleSide
    });
    
    // Draw each segment as a 3D track
    segments.forEach(seg => {
        const p1 = points.find(p => p.id === seg.startIndex);
        const p2 = points.find(p => p.id === seg.endIndex);
        if (!p1 || !p2) return;
        
        // Convert 2D coordinates to 3D
        const start = new THREE.Vector3(
            (p1.x - BASE_CANVAS_SIZE / 2) * scale,
            trackElevation,
            (p1.y - BASE_CANVAS_SIZE / 2) * scale
        );
        const end = new THREE.Vector3(
            (p2.x - BASE_CANVAS_SIZE / 2) * scale,
            trackElevation,
            (p2.y - BASE_CANVAS_SIZE / 2) * scale
        );
        
        // Calculate segment length and direction
        const direction = new THREE.Vector3().subVectors(end, start);
        const length = direction.length();
        const center = new THREE.Vector3().addVectors(start, end).multiplyScalar(0.5);
        
        // Create horizontal track geometry (lying on XZ plane)
        const geometry = new THREE.BoxGeometry(length, trackHeight, trackWidth);
        const track = new THREE.Mesh(geometry, trackMaterial);
        
        // Position at center
        track.position.copy(center);
        
        // Orient track to point from start to end (horizontal rotation around Y axis)
        const angle = Math.atan2(direction.z, direction.x);
        track.rotation.y = -angle;
        
        track.castShadow = true;
        track.receiveShadow = true;
        
        trackGroup3D.add(track);
        
        // Suspensions will be added separately after all segments to avoid duplicates
    });
    
    // Add suspension cables if enabled (from ceiling to track)
    // Process all segments together to avoid duplicates at connection points
    if (suspensionEnabled && segments.length > 0) {
        const suspensionPositions = new Map(); // key: "x,y,z", value: position
        
        segments.forEach(seg => {
            const p1 = points.find(p => p.id === seg.startIndex);
            const p2 = points.find(p => p.id === seg.endIndex);
            if (!p1 || !p2) return;
            
            // Calculate segment in 3D space
            const x1 = (p1.x - BASE_CANVAS_SIZE / 2) * scale;
            const z1 = (p1.y - BASE_CANVAS_SIZE / 2) * scale;
            const x2 = (p2.x - BASE_CANVAS_SIZE / 2) * scale;
            const z2 = (p2.y - BASE_CANVAS_SIZE / 2) * scale;
            
            const start = new THREE.Vector3(x1, trackElevation, z1);
            const end = new THREE.Vector3(x2, trackElevation, z2);
            
            // Calculate segment length in meters
            const dx = p2.x - p1.x;
            const dy = p2.y - p1.y;
            const lengthInPixels = Math.sqrt(dx * dx + dy * dy);
            const lengthInMeters = lengthInPixels / 80; // PIXELS_PER_METER = 80
            
            // Add suspensions at start and end points
            const startKey = `${start.x.toFixed(3)},${start.y.toFixed(3)},${start.z.toFixed(3)}`;
            const endKey = `${end.x.toFixed(3)},${end.y.toFixed(3)},${end.z.toFixed(3)}`;
            suspensionPositions.set(startKey, start);
            suspensionPositions.set(endKey, end);
            
            // Add intermediate suspensions every meter
            const numIntermediateSuspensions = Math.max(0, Math.floor(lengthInMeters) - 1);
            for (let i = 1; i <= numIntermediateSuspensions; i++) {
                const t = i / Math.floor(lengthInMeters); // Position at each meter
                const susPos = new THREE.Vector3().lerpVectors(start, end, t);
                const key = `${susPos.x.toFixed(3)},${susPos.y.toFixed(3)},${susPos.z.toFixed(3)}`;
                suspensionPositions.set(key, susPos);
            }
            
            console.log(`3D: Segment ${seg.startIndex}-${seg.endIndex}: ${lengthInMeters.toFixed(2)}m = ${numIntermediateSuspensions} intermediate + 2 endpoint suspensions`);
        });
        
        console.log(`Total 3D suspensions (no duplicates): ${suspensionPositions.size}`);
        
        // Suspension material - thin wire/cable appearance
        const suspensionMaterial = new THREE.MeshStandardMaterial({
            color: trackColor, // Match track color
            metalness: 0.8,
            roughness: 0.2
        });
        
        // Create suspension geometry for all unique positions
        suspensionPositions.forEach((susPos, key) => {
                    
                    // Suspension cable parameters
                    const cableRadius = trackWidth * 0.04; // Very thin cable
                    const ceilingHeight = trackElevation + 2.0; // 2 meters above track to ceiling
                    const cableLength = ceilingHeight;
                    
                    // Create vertical cable from track to ceiling
                    const cableGeometry = new THREE.CylinderGeometry(cableRadius, cableRadius, cableLength, 8);
                    const cable = new THREE.Mesh(cableGeometry, suspensionMaterial);
                    
                    // Position cable above track center
                    cable.position.set(
                        susPos.x,
                        susPos.y + cableLength / 2, // Half cable length above track
                        susPos.z
                    );
                    
                    cable.castShadow = true;
                    trackGroup3D.add(cable);
                    
                    // Ceiling mount - cone shape with rounded top
                    const coneHeight = trackHeight * 1.2;
                    const coneBaseRadius = trackWidth * 0.3;
                    const coneTopRadius = cableRadius * 1.5;
                    const coneGeometry = new THREE.ConeGeometry(coneBaseRadius, coneHeight, 12);
                    const cone = new THREE.Mesh(coneGeometry, suspensionMaterial);
                    // Position at top of cable, pointing down
                    cone.position.set(susPos.x, susPos.y + cableLength - coneHeight / 2, susPos.z);
                    cone.rotation.x = Math.PI; // Flip upside down
                    cone.castShadow = true;
                    trackGroup3D.add(cone);
                    
                    // Add rounded top to cone (small sphere)
                    const topSphereGeometry = new THREE.SphereGeometry(coneTopRadius, 8, 8);
                    const topSphere = new THREE.Mesh(topSphereGeometry, suspensionMaterial);
                    topSphere.position.set(susPos.x, susPos.y + cableLength, susPos.z);
                    topSphere.castShadow = true;
                    trackGroup3D.add(topSphere);
                    
                    // Track mount - L-shaped bracket (angle shape)
                    // Vertical part of L
                    const bracketVertHeight = trackHeight * 0.8;
                    const bracketThickness = trackWidth * 0.15;
                    const bracketWidth = trackWidth * 0.4;
                    
                    const vertPartGeometry = new THREE.BoxGeometry(bracketThickness, bracketVertHeight, bracketWidth);
                    const vertPart = new THREE.Mesh(vertPartGeometry, suspensionMaterial);
                    vertPart.position.set(susPos.x, susPos.y - bracketVertHeight / 2, susPos.z);
                    vertPart.castShadow = true;
                    trackGroup3D.add(vertPart);
                    
                    // Horizontal part of L (angle)
                    const horizPartLength = trackWidth * 0.6;
                    const horizPartGeometry = new THREE.BoxGeometry(horizPartLength, bracketThickness, bracketWidth);
                    const horizPart = new THREE.Mesh(horizPartGeometry, suspensionMaterial);
                    horizPart.position.set(
                        susPos.x + horizPartLength / 2 - bracketThickness / 2,
                        susPos.y - bracketVertHeight + bracketThickness / 2,
                        susPos.z
                    );
                    horizPart.castShadow = true;
                    trackGroup3D.add(horizPart);
        });
    }
    
    segments.forEach(seg => {
        const p1 = points.find(p => p.id === seg.startIndex);
        const p2 = points.find(p => p.id === seg.endIndex);
        if (!p1 || !p2) return;
        
        // Calculate segment in 3D space
        const x1 = (p1.x - BASE_CANVAS_SIZE / 2) * scale;
        const z1 = (p1.y - BASE_CANVAS_SIZE / 2) * scale;
        const x2 = (p2.x - BASE_CANVAS_SIZE / 2) * scale;
        const z2 = (p2.y - BASE_CANVAS_SIZE / 2) * scale;
        
        const start = new THREE.Vector3(x1, trackElevation, z1);
        const end = new THREE.Vector3(x2, trackElevation, z2);
        const direction = new THREE.Vector3().subVectors(end, start);
        const length = direction.length();
        
        // Calculate track angle (needed for lamp positioning)
        const angle = Math.atan2(direction.z, direction.x);
        
        // Calculate number of lamps based on track length
        // Convert 3D units to real meters: 1 meter = PIXELS_PER_METER * scale 3D units
        const lengthInMeters = length / (80 * scale); // PIXELS_PER_METER = 80
        let numLamps;
        if (lengthInMeters < 1) {
            numLamps = 0; // No lamps for tracks < 1m
        } else if (lengthInMeters <= 2) {
            numLamps = 2; // 2 lamps for tracks 1-2m
        } else {
            // 2 lamps per meter for tracks > 2m
            numLamps = Math.round(lengthInMeters * 2);
        }
        
        console.log(`Track: ${length.toFixed(2)} 3D units = ${lengthInMeters.toFixed(2)}m, lamps: ${numLamps}`);
        
        // Lamp shade material - more transparent (0.4 instead of 0.6)
        const lampMaterial = new THREE.MeshStandardMaterial({
            color: 0x999999,
            metalness: 0.3,
            roughness: 0.4,
            transparent: true,
            opacity: 0.4,
            side: THREE.DoubleSide
        });
        
        // Arm material - transparent like lamp shade
        const armMaterial = new THREE.MeshStandardMaterial({
            color: 0x999999,
            metalness: 0.3,
            roughness: 0.4,
            transparent: true,
            opacity: 0.4
        });
        
        // Mounting plate material - transparent
        const mountingPlateMaterial = new THREE.MeshStandardMaterial({
            color: trackColor,
            metalness: 0.6,
            roughness: 0.3,
            transparent: true,
            opacity: 0.5
        });
        
        // Light bulb material - emissive disk (semi-transparent)
        const bulbMaterial = new THREE.MeshStandardMaterial({
            color: 0xFFFFAA,
            emissive: 0xFFFF88,
            emissiveIntensity: 0.8,
            metalness: 0.0,
            roughness: 0.2,
            transparent: true,
            opacity: 0.6
        });
        
        if (numLamps > 0) {
            for (let i = 0; i < numLamps; i++) {
                const t = (i + 0.5) / numLamps; // Position along track (0 to 1)
                const lampPos = new THREE.Vector3().lerpVectors(start, end, t);
                
                // Mounting plate on track (small rectangular box)
                const plateWidth = trackWidth * 0.6;
                const plateHeight = trackHeight * 0.3;
                const plateDepth = trackWidth * 0.6;
                const plateGeometry = new THREE.BoxGeometry(plateWidth, plateHeight, plateDepth);
                const mountingPlate = new THREE.Mesh(plateGeometry, mountingPlateMaterial);
                mountingPlate.position.set(lampPos.x, lampPos.y - plateHeight / 2, lampPos.z);
                mountingPlate.castShadow = true;
                trackGroup3D.add(mountingPlate);
                
                // Lamp mounting bracket (rectangular box BETWEEN track and arm) - transparent
                const bracketWidth = trackWidth * 0.5;
                const bracketHeight = trackHeight * 0.4;
                const bracketDepth = trackWidth * 0.5;
                const bracketGeometry = new THREE.BoxGeometry(bracketWidth, bracketHeight, bracketDepth);
                const lampBracket = new THREE.Mesh(bracketGeometry, mountingPlateMaterial);
                // Position RIGHT UNDER mounting plate (between track and arm)
                const bracketYPos = lampPos.y - plateHeight - bracketHeight / 2;
                lampBracket.position.set(lampPos.x, bracketYPos, lampPos.z);
                lampBracket.castShadow = true;
                trackGroup3D.add(lampBracket);
                
                // Mounting arm (thin cylinder going down) - transparent
                const armLength = trackHeight * 4.5;
                const armGeometry = new THREE.CylinderGeometry(trackWidth * 0.15, trackWidth * 0.15, armLength, 8);
                const arm = new THREE.Mesh(armGeometry, armMaterial);
                // Arm starts AFTER bracket
                arm.position.set(lampPos.x, lampPos.y - plateHeight - bracketHeight - armLength / 2, lampPos.z);
                arm.castShadow = true;
                trackGroup3D.add(arm);
                
                // Lamp shade (cylinder, not cone) - transparent
                const lampRadius = trackWidth * 1.2;
                const lampHeight = trackHeight * 3.6;
                const lampGeometry = new THREE.CylinderGeometry(lampRadius, lampRadius, lampHeight, 16); // Same radius = cylinder
                const lamp = new THREE.Mesh(lampGeometry, lampMaterial);
                
                // Position below arm (arm starts after bracket, so subtract both)
                const lampYPos = lampPos.y - plateHeight - bracketHeight - armLength - lampHeight / 2;
                lamp.position.set(lampPos.x, lampYPos, lampPos.z);
                
                // Rotate lamp perpendicular to track direction (alternating sides)
                const perpAngle = angle + (i % 2 === 0 ? Math.PI / 2 : -Math.PI / 2);
                const tiltDown = Math.PI / 6; // 30 degrees down
                
                // Apply rotation: first tilt down, then rotate around track
                lamp.rotation.set(tiltDown, perpAngle, 0);
                
                lamp.castShadow = true;
                trackGroup3D.add(lamp);
                
                // Light bulb (disk at the end/bottom of lamp cylinder)
                const bulbRadius = lampRadius * 0.7;
                const bulbThickness = trackHeight * 0.2;
                const bulbGeometry = new THREE.CylinderGeometry(bulbRadius, bulbRadius, bulbThickness, 16);
                const bulb = new THREE.Mesh(bulbGeometry, bulbMaterial);
                
                // Simplified position calculation - at the very bottom of lamp
                // Just offset downward from lamp center along lamp's tilted direction
                const lampBottomOffset = lampHeight / 2;
                
                // Create rotation matrix to transform offset vector
                const rotationMatrix = new THREE.Matrix4();
                rotationMatrix.makeRotationFromEuler(new THREE.Euler(tiltDown, perpAngle, 0));
                
                // Offset vector in lamp's local space (down = negative Y)
                const localOffset = new THREE.Vector3(0, -lampBottomOffset, 0);
                localOffset.applyMatrix4(rotationMatrix);
                
                bulb.position.set(
                    lamp.position.x + localOffset.x,
                    lamp.position.y + localOffset.y,
                    lamp.position.z + localOffset.z
                );
                
                // Same rotation as lamp
                bulb.rotation.copy(lamp.rotation);
                
                trackGroup3D.add(bulb);
            }
        }
    });
    
    // Add connectors at junction points
    const addedPoints = new Set();
    const connectorMaterial = new THREE.MeshStandardMaterial({
        color: trackColor,
        metalness: 0.8,
        roughness: 0.2
    });
    
    // End cap material (slightly different for visibility)
    const endCapMaterial = new THREE.MeshStandardMaterial({
        color: trackColor,
        metalness: 0.7,
        roughness: 0.3
    });
    
    points.forEach(point => {
        const pointKey = `${point.x}_${point.y}`;
        if (!addedPoints.has(pointKey)) {
            addedPoints.add(pointKey);
            
            // Count how many segments connect to this point
            const connectedSegments = segments.filter(seg => 
                seg.startIndex === point.id || seg.endIndex === point.id
            );
            const numConnections = connectedSegments.length;
            
            const pointX = (point.x - BASE_CANVAS_SIZE / 2) * scale;
            const pointZ = (point.y - BASE_CANVAS_SIZE / 2) * scale;
            
            if (numConnections === 1) {
                // End cap (KONCOVÁ KRYTKA) - small square cap
                const capSize = trackWidth * 0.9;
                const capGeometry = new THREE.BoxGeometry(capSize, trackHeight * 0.9, capSize);
                const cap = new THREE.Mesh(capGeometry, endCapMaterial);
                cap.position.set(pointX, trackElevation, pointZ);
                cap.castShadow = true;
                cap.receiveShadow = true;
                trackGroup3D.add(cap);
                
            } else if (numConnections === 2) {
                // Straight connector (PŘÍMÝ SPOJ) - small rectangular piece
                const connectorLength = trackWidth * 1.4;
                const connectorGeometry = new THREE.BoxGeometry(
                    connectorLength,
                    trackHeight * 1.1,
                    trackWidth * 1.1
                );
                const connector = new THREE.Mesh(connectorGeometry, connectorMaterial);
                connector.position.set(pointX, trackElevation, pointZ);
                
                // Rotate to align with tracks if they're in line
                const seg1 = connectedSegments[0];
                const p1 = points.find(p => p.id === (seg1.startIndex === point.id ? seg1.endIndex : seg1.startIndex));
                if (p1) {
                    const angle = Math.atan2(p1.y - point.y, p1.x - point.x);
                    connector.rotation.y = -angle;
                }
                
                connector.castShadow = true;
                connector.receiveShadow = true;
                trackGroup3D.add(connector);
                
            } else if (numConnections === 3) {
                // T-connector - slightly larger junction
                const tSize = trackWidth * 1.5;
                const tGeometry = new THREE.BoxGeometry(tSize, trackHeight * 1.2, tSize);
                const tConnector = new THREE.Mesh(tGeometry, connectorMaterial);
                tConnector.position.set(pointX, trackElevation, pointZ);
                tConnector.castShadow = true;
                tConnector.receiveShadow = true;
                trackGroup3D.add(tConnector);
                
            } else if (numConnections >= 4) {
                // X-connector (X SPOJ PRO 1-OKRUH. LIŠTU) - cross-shaped
                const xSize = trackWidth * 1.6;
                const xGeometry = new THREE.BoxGeometry(xSize, trackHeight * 1.3, xSize);
                const xConnector = new THREE.Mesh(xGeometry, connectorMaterial);
                xConnector.position.set(pointX, trackElevation, pointZ);
                xConnector.castShadow = true;
                xConnector.receiveShadow = true;
                trackGroup3D.add(xConnector);
                
                // Add decorative cross detail
                const crossArmGeometry = new THREE.BoxGeometry(xSize * 1.1, trackHeight * 0.3, trackWidth * 0.6);
                const crossArm1 = new THREE.Mesh(crossArmGeometry, connectorMaterial);
                crossArm1.position.set(pointX, trackElevation + trackHeight * 0.5, pointZ);
                crossArm1.rotation.y = Math.PI / 4;
                trackGroup3D.add(crossArm1);
                
                const crossArm2 = new THREE.Mesh(crossArmGeometry, connectorMaterial);
                crossArm2.position.set(pointX, trackElevation + trackHeight * 0.5, pointZ);
                crossArm2.rotation.y = -Math.PI / 4;
                trackGroup3D.add(crossArm2);
            }
        }
    });
    
    // Add outlets (features)
    const outletMaterial = new THREE.MeshStandardMaterial({
        color: 0xFFD700, // Gold color for outlets
        metalness: 0.6,
        roughness: 0.4,
        emissive: 0xFFAA00,
        emissiveIntensity: 0.2,
        wireframe: false
    });
    
    features.forEach(feature => {
        if (feature.type === 'outlet') {
            console.log('Adding outlet:', feature);
            
            // Find the closest segment (track) to this outlet
            let closestSegment = null;
            let closestDistance = Infinity;
            let closestPointOnSegment = null;
            
            segments.forEach(seg => {
                const p1 = points.find(p => p.id === seg.startIndex);
                const p2 = points.find(p => p.id === seg.endIndex);
                if (!p1 || !p2) return;
                
                // Calculate closest point on segment to outlet
                const outletPos = { x: feature.x, y: feature.y };
                const dx = p2.x - p1.x;
                const dy = p2.y - p1.y;
                const lengthSquared = dx * dx + dy * dy;
                
                let t = ((outletPos.x - p1.x) * dx + (outletPos.y - p1.y) * dy) / lengthSquared;
                t = Math.max(0, Math.min(1, t)); // Clamp to segment
                
                const closestX = p1.x + t * dx;
                const closestY = p1.y + t * dy;
                const dist = Math.sqrt((outletPos.x - closestX) ** 2 + (outletPos.y - closestY) ** 2);
                
                if (dist < closestDistance) {
                    closestDistance = dist;
                    closestSegment = seg;
                    closestPointOnSegment = { x: closestX, y: closestY };
                }
            });
            
            if (!closestPointOnSegment) {
                console.warn('No track found for outlet');
                return;
            }
            
            // 3D positions
            const outletX = (feature.x - BASE_CANVAS_SIZE / 2) * scale;
            const outletZ = (feature.y - BASE_CANVAS_SIZE / 2) * scale;
            const outletY = trackElevation - trackHeight * 2; // Hang below track
            
            const trackX = (closestPointOnSegment.x - BASE_CANVAS_SIZE / 2) * scale;
            const trackZ = (closestPointOnSegment.y - BASE_CANVAS_SIZE / 2) * scale;
            const trackY = trackElevation;
            
            console.log(`Outlet at (${outletX.toFixed(2)}, ${outletY.toFixed(2)}, ${outletZ.toFixed(2)})`);
            console.log(`Track at (${trackX.toFixed(2)}, ${trackY.toFixed(2)}, ${trackZ.toFixed(2)})`);
            
            // Create wavy cable from track to outlet using CatmullRomCurve3
            const cableStart = new THREE.Vector3(trackX, trackY, trackZ);
            const cableEnd = new THREE.Vector3(outletX, outletY, outletZ);
            
            // Add control points for wave effect
            const midX = (trackX + outletX) / 2;
            const midY = (trackY + outletY) / 2;
            const midZ = (trackZ + outletZ) / 2;
            
            // Create slight wave/sag in the cable
            const sag = trackHeight * 1.5;
            const wavyPoints = [
                cableStart,
                new THREE.Vector3(trackX, trackY - sag * 0.3, trackZ),
                new THREE.Vector3(midX, midY - sag, midZ),
                new THREE.Vector3(outletX, outletY + sag * 0.3, outletZ),
                cableEnd
            ];
            
            const curve = new THREE.CatmullRomCurve3(wavyPoints);
            const tubeGeometry = new THREE.TubeGeometry(curve, 20, trackWidth * 0.04, 8, false);
            
            const cableMaterial = new THREE.MeshStandardMaterial({
                color: 0x1a1a1a, // Very dark gray (cable)
                metalness: 0.1,
                roughness: 0.9
            });
            
            const cable = new THREE.Mesh(tubeGeometry, cableMaterial);
            cable.castShadow = true;
            trackGroup3D.add(cable);
            
            // Create outlet box at the end
            const outletSize = trackWidth * 0.8;
            const outletHeight = outletSize * 0.5;
            const outletGeometry = new THREE.BoxGeometry(outletSize, outletHeight, outletSize * 0.7);
            const outlet = new THREE.Mesh(outletGeometry, outletMaterial);
            outlet.position.set(outletX, outletY, outletZ);
            outlet.castShadow = true;
            trackGroup3D.add(outlet);
        }
    });
    
    // Add power connection visualization (L-shaped connector)
    if (powerConnection) {
        const powerMaterial = new THREE.MeshStandardMaterial({
            color: trackColor, // Match track color
            metalness: 0.7,
            roughness: 0.3,
            emissive: 0x004400, // Slight green glow
            emissiveIntensity: 0.2
        });
        
        const powerX = (powerConnection.x - BASE_CANVAS_SIZE / 2) * scale;
        const powerZ = (powerConnection.y - BASE_CANVAS_SIZE / 2) * scale;
        
        // L-shaped power connector (two boxes forming an L)
        // Horizontal part (on track)
        const powerHorizontalGeometry = new THREE.BoxGeometry(
            trackWidth * 0.8,
            trackHeight * 0.8,
            trackWidth * 1.2
        );
        const powerHorizontal = new THREE.Mesh(powerHorizontalGeometry, powerMaterial);
        powerHorizontal.position.set(powerX, trackElevation, powerZ);
        powerHorizontal.castShadow = true;
        trackGroup3D.add(powerHorizontal);
        
        // Vertical part (going up)
        const powerVerticalHeight = trackHeight * 2.5;
        const powerVerticalGeometry = new THREE.BoxGeometry(
            trackWidth * 0.6,
            powerVerticalHeight,
            trackWidth * 0.6
        );
        const powerVertical = new THREE.Mesh(powerVerticalGeometry, powerMaterial);
        powerVertical.position.set(
            powerX,
            trackElevation + trackHeight / 2 + powerVerticalHeight / 2,
            powerZ
        );
        powerVertical.castShadow = true;
        trackGroup3D.add(powerVertical);
        
        // Cable entry point at top (small cylinder)
        const cableEntryGeometry = new THREE.CylinderGeometry(
            trackWidth * 0.25,
            trackWidth * 0.25,
            trackHeight * 0.5,
            8
        );
        const cableEntry = new THREE.Mesh(cableEntryGeometry, powerMaterial);
        cableEntry.position.set(
            powerX,
            trackElevation + trackHeight / 2 + powerVerticalHeight + trackHeight * 0.25,
            powerZ
        );
        cableEntry.castShadow = true;
        trackGroup3D.add(cableEntry);
    }
    
    console.log('3D scene updated with', segments.length, 'segments and', features.length, 'features');
}

function autoFocusCamera() {
    if (!camera3D || !controls3D) return;
    if (segments.length === 0 && features.length === 0) return;
    
    const scale = 0.1;
    
    // Calculate bounding box of all objects
    let minX = Infinity, maxX = -Infinity;
    let minZ = Infinity, maxZ = -Infinity;
    
    points.forEach(p => {
        const x = (p.x - BASE_CANVAS_SIZE / 2) * scale;
        const z = (p.y - BASE_CANVAS_SIZE / 2) * scale;
        minX = Math.min(minX, x);
        maxX = Math.max(maxX, x);
        minZ = Math.min(minZ, z);
        maxZ = Math.max(maxZ, z);
    });
    
    features.forEach(f => {
        const x = (f.x - BASE_CANVAS_SIZE / 2) * scale;
        const z = (f.y - BASE_CANVAS_SIZE / 2) * scale;
        minX = Math.min(minX, x);
        maxX = Math.max(maxX, x);
        minZ = Math.min(minZ, z);
        maxZ = Math.max(maxZ, z);
    });
    
    // Calculate center and size
    const centerX = (minX + maxX) / 2;
    const centerZ = (minZ + maxZ) / 2;
    const sizeX = maxX - minX;
    const sizeZ = maxZ - minZ;
    const maxSize = Math.max(sizeX, sizeZ, 1); // Minimum 1 meter
    
    // Position camera to see everything
    const distance = maxSize * 2;
    camera3D.position.set(centerX + distance, distance * 0.8, centerZ + distance);
    camera3D.lookAt(centerX, 0, centerZ);
    
    controls3D.target.set(centerX, 0, centerZ);
    controls3D.update();
    
    console.log('Camera focused on:', {centerX, centerZ, distance});
}

function setView(viewType) {
    const wasIn3D = is3DView;
    is3DView = viewType === '3D';
    
    document.getElementById('view2DBtn').classList.toggle('active', !is3DView);
    document.getElementById('view3DBtn').classList.toggle('active', is3DView);
    
    document.getElementById('canvas-container').style.display = is3DView ? 'none' : 'block';
    document.getElementById('threeD-container').style.display = is3DView ? 'block' : 'none';
    
    const toolSwitcher = document.querySelector('.tool-switcher');
    if (toolSwitcher) {
        toolSwitcher.classList.toggle('hidden-3d', is3DView);
    }
    
    const secondaryControls = document.querySelector('.secondary-controls');
    if (secondaryControls) {
        secondaryControls.classList.toggle('hidden-3d', is3DView);
    }
    
    // Save camera position when leaving 3D view
    if (wasIn3D && !is3DView && camera3D && controls3D) {
        savedCameraPosition = camera3D.position.clone();
        savedCameraTarget = controls3D.target.clone();
        console.log('Camera position saved');
    }
    
    // Initialize and update 3D view when switching to it
    if (is3DView) {
        const isFirstTime = !scene3D;
        
        if (isFirstTime) {
            init3DView();
        }
        
        update3DFromCanvas();
        
        // Restore saved camera position or auto-focus on first time
        if (savedCameraPosition && savedCameraTarget && !isFirstTime) {
            camera3D.position.copy(savedCameraPosition);
            controls3D.target.copy(savedCameraTarget);
            controls3D.update();
            console.log('Camera position restored');
        } else if (isFirstTime) {
            autoFocusCamera();
        }
        
        // Trigger resize to ensure proper rendering
        setTimeout(() => {
            const container = document.getElementById('threeD-container');
            if (container && renderer3D) {
                renderer3D.setSize(container.clientWidth, container.clientHeight);
                camera3D.aspect = container.clientWidth / container.clientHeight;
                camera3D.updateProjectionMatrix();
            }
        }, 100);
    }
}

// Toggle fullscreen mode
function toggleFullscreen() {
    const container = document.querySelector('.main-container');
    
    if (!document.fullscreenElement && 
        !document.webkitFullscreenElement && 
        !document.mozFullScreenElement && 
        !document.msFullscreenElement) {
        
        // Enter fullscreen
        if (container.requestFullscreen) {
            container.requestFullscreen();
        } else if (container.webkitRequestFullscreen) { // Safari
            container.webkitRequestFullscreen();
        } else if (container.mozRequestFullScreen) { // Firefox
            container.mozRequestFullScreen();
        } else if (container.msRequestFullscreen) { // IE/Edge
            container.msRequestFullscreen();
        }
        
        console.log('Entering fullscreen mode');
    } else {
        // Exit fullscreen
        if (document.exitFullscreen) {
            document.exitFullscreen();
        } else if (document.webkitExitFullscreen) { // Safari
            document.webkitExitFullscreen();
        } else if (document.mozCancelFullScreen) { // Firefox
            document.mozCancelFullScreen();
        } else if (document.msExitFullscreen) { // IE/Edge
            document.msExitFullscreen();
        }
        
        console.log('Exiting fullscreen mode');
    }
}

// Handle fullscreen change events
function handleFullscreenChange() {
    const isFullscreen = !!(document.fullscreenElement || 
                            document.webkitFullscreenElement || 
                            document.mozFullScreenElement || 
                            document.msFullscreenElement);
    
    if (isFullscreen) {
        console.log('Entered fullscreen mode');
        // Resize canvas to fit fullscreen
        setTimeout(() => {
            resizeCanvas();
            if (is3DView && renderer3D) {
                const container = document.getElementById('threeD-container');
                if (container) {
                    renderer3D.setSize(container.clientWidth, container.clientHeight);
                    camera3D.aspect = container.clientWidth / container.clientHeight;
                    camera3D.updateProjectionMatrix();
                }
            }
        }, 100);
    } else {
        console.log('Exited fullscreen mode');
        // Restore normal canvas size
        setTimeout(() => {
            resizeCanvas();
            if (is3DView && renderer3D) {
                const container = document.getElementById('threeD-container');
                if (container) {
                    renderer3D.setSize(container.clientWidth, container.clientHeight);
                    camera3D.aspect = container.clientWidth / container.clientHeight;
                    camera3D.updateProjectionMatrix();
                }
            }
        }, 100);
    }
}

function toggleShortcuts() {
    const content = document.getElementById('shortcutsContent');
    const arrow = document.getElementById('shortcutsArrow');
    
    if (content) {
        content.classList.toggle('open');
    }
    
    if (arrow) {
        arrow.classList.toggle('rotated');
    }
}

function toggleExportMenu() {
    const popup = document.getElementById('exportPopup');
    popup.classList.toggle('active');
}

function closeExportMenu() {
    const popup = document.getElementById('exportPopup');
    popup.classList.remove('active');
}

// Close export menu when clicking outside
document.addEventListener('click', function(event) {
    const exportMenu = document.querySelector('.export-menu-container');
    const exportPopup = document.getElementById('exportPopup');
    if (exportMenu && exportPopup && !exportMenu.contains(event.target)) {
        exportPopup.classList.remove('active');
    }
});

function initializeShortcutsPanel() {
    // Restore collapsed state from localStorage
    try {
        const isCollapsed = localStorage.getItem('shortcutsPanelCollapsed') === 'true';
        const panel = document.getElementById('shortcutsPanel');
        const toggleTab = document.getElementById('shortcutsToggleTab');
        
        if (isCollapsed) {
            panel.classList.add('collapsed');
            toggleTab.style.display = 'inline-block';
        } else {
            toggleTab.style.display = 'none';
        }
    } catch (e) {
        // Ignore localStorage errors
    }
}


function initializeApp() {
    isShiftPressed = false;
    isCtrlPressed = false;
    isAltPressed = false;
    
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('keyup', handleKeyUp);
    
    // Fullscreen change handlers
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);
    
    // Add event handlers for configuration controls
    const colorOptions = document.querySelectorAll('input[name="trackColor"]');
    const suspensionHanging = document.getElementById('suspensionHanging');
    const suspensionMounted = document.getElementById('suspensionMounted');
    
    colorOptions.forEach(option => {
        option.addEventListener('change', (e) => {
            trackColor = e.target.value;
            
            // Update global state
            if (window.configuratorState) {
                window.configuratorState.selectedColor = trackColor;
            }
            
            calculateAndDisplayComponents();
            
            // Update 3D view if currently in 3D mode
            if (is3DView) {
                update3DFromCanvas();
            }
            
            // Dispatch event for lamp section filtering
            document.dispatchEvent(new CustomEvent('configuratorColorChanged', {
                detail: { color: trackColor }
            }));
        });
    });
    
    // Suspension toggle buttons
    function setSuspension(isHanging) {
        suspensionEnabled = isHanging;
        
        // Update button states
        if (suspensionHanging && suspensionMounted) {
            suspensionHanging.classList.toggle('active', isHanging);
            suspensionMounted.classList.toggle('active', !isHanging);
        }
        
        calculateAndDisplayComponents();
        
        // Update 3D view if currently in 3D mode
        if (is3DView) {
            update3DFromCanvas();
        }
    }
    
    if (suspensionHanging) {
        suspensionHanging.addEventListener('click', () => setSuspension(true));
    }
    
    if (suspensionMounted) {
        suspensionMounted.addEventListener('click', () => setSuspension(false));
    }
    
    // Initialize shortcuts panel state
    initializeShortcutsPanel();
    
    resizeCanvas();
    setCurrentTool('draw');
    saveState('Initial');
    calculateAndDisplayComponents();
    updatePowerButtonState();
    
    // Check if there's a configuration in the URL
    loadConfigFromURL();
    
    setTimeout(() => {
        window.focus();
        isShiftPressed = false;
        isCtrlPressed = false;
        isAltPressed = false;
    }, 100);
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeApp);
} else {
    initializeApp();
}

// Animation loop for pulse effect and power flow animation
function animationLoop() {
    // Update power flow animation offset
    powerFlowOffset = (powerFlowOffset + 0.5) % 20;
    
    // Redraw if there are outlets (for pulse animation), power connection (for flow animation), or in connect_power mode (for cursor)
    if (features.some(f => f.type === 'outlet') || powerConnection || currentTool === 'connect_power') {
        drawAll();
    }
    requestAnimationFrame(animationLoop);
}
animationLoop();

window.addEventListener('load', () => {
    if (!isShiftPressed && typeof handleKeyDown === 'function') {
        document.removeEventListener('keydown', handleKeyDown);
        document.removeEventListener('keyup', handleKeyUp);
        document.addEventListener('keydown', handleKeyDown);
        document.addEventListener('keyup', handleKeyUp);
    }
});

window.addEventListener('blur', () => {
    isShiftPressed = false;
    isCtrlPressed = false;
    isAltPressed = false;
});

// Save configuration to JSON file
window.saveConfiguration = function() {
    const config = {
        version: '1.0',
        timestamp: new Date().toISOString(),
        data: {
            points: points,
            segments: segments,
            features: features,
            powerConnection: powerConnection,
            pointCounter: pointCounter,
            canvasZoom: canvasZoom,
            canvasPanX: canvasPanX,
            canvasPanY: canvasPanY
        }
    };
    
    const json = JSON.stringify(config, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    
    const a = document.createElement('a');
    a.href = url;
    a.download = `track-configuration-${new Date().getTime()}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    console.log('Configuration saved successfully');
};

// Duplicate removed - using loadConfigurationFromFile instead

// Initialize canvas size
resizeCanvas();
drawAll();

window.addEventListener('resize', resizeCanvas);

// File input for loading configuration
const loadConfigInput = document.getElementById('loadConfigInput');
if (loadConfigInput) {
    loadConfigInput.addEventListener('change', (e) => {
        const file = e.target.files[0];
        if (file) {
            window.loadConfigurationFromFile(file);
        }
        // Reset input so the same file can be loaded again
        e.target.value = '';
    });
}

// Track color change listener - update 3D view when color changes
const trackColorInputs = document.querySelectorAll('input[name="trackColor"]');
trackColorInputs.forEach(input => {
    input.addEventListener('change', () => {
        console.log('Track color changed to:', input.value);
        trackColor = input.value;
        
        // Update global state
        if (window.configuratorState) {
            window.configuratorState.selectedColor = trackColor;
        }
        
        // Update 2D view
        drawAll();
        
        // Update 3D view if it's active
        if (is3DView && scene3D) {
            update3DFromCanvas();
        }
        
        // Dispatch event for lamp section filtering
        document.dispatchEvent(new CustomEvent('configuratorColorChanged', {
            detail: { color: trackColor }
        }));
    });
});

// Mobile: Touch Events Support for Canvas
function initTouchSupport() {
    if (!canvas) return;
    
    // Prevent default touch behaviors on canvas
    canvas.addEventListener('touchstart', (e) => {
        if (e.target === canvas) {
            e.preventDefault();
        }
    }, { passive: false });
    
    canvas.addEventListener('touchmove', (e) => {
        if (e.target === canvas) {
            e.preventDefault();
        }
    }, { passive: false });
    
    // Convert touch to mouse event
    function touchToMouse(touchEvent, mouseEventType) {
        const touch = touchEvent.changedTouches[0];
        const mouseEvent = new MouseEvent(mouseEventType, {
            clientX: touch.clientX,
            clientY: touch.clientY,
            screenX: touch.screenX,
            screenY: touch.screenY,
            button: 0,
            bubbles: true,
            cancelable: true
        });
        return mouseEvent;
    }
    
    // Touch start -> mousedown
    canvas.addEventListener('touchstart', (e) => {
        if (e.touches.length === 1) {
            const mouseEvent = touchToMouse(e, 'mousedown');
            canvas.dispatchEvent(mouseEvent);
        }
        // Two finger pinch for zoom (future enhancement)
        else if (e.touches.length === 2) {
            // Could add pinch-to-zoom here
        }
    }, { passive: false });
    
    // Touch move -> mousemove
    canvas.addEventListener('touchmove', (e) => {
        if (e.touches.length === 1) {
            const mouseEvent = touchToMouse(e, 'mousemove');
            canvas.dispatchEvent(mouseEvent);
        }
    }, { passive: false });
    
    // Touch end -> mouseup
    canvas.addEventListener('touchend', (e) => {
        const mouseEvent = touchToMouse(e, 'mouseup');
        canvas.dispatchEvent(mouseEvent);
    }, { passive: false });
    
    // Touch cancel -> mouseup
    canvas.addEventListener('touchcancel', (e) => {
        const mouseEvent = touchToMouse(e, 'mouseup');
        canvas.dispatchEvent(mouseEvent);
    }, { passive: false });
}

// Mobile: Collapsible Results Bottom Sheet
let mobileSheetInitialized = false;

function initMobileResultsSheet() {
    const resultsArea = document.getElementById('results-area');
    const resultsHeader = document.querySelector('.results-header');
    
    if (!resultsArea || !resultsHeader) {
        console.log('Results area or header not found');
        return;
    }
    
    // Check if mobile
    const isMobile = () => window.innerWidth <= 768;
    
    if (!isMobile()) {
        resultsArea.classList.remove('collapsed');
        return;
    }
    
    // Prevent multiple initializations
    if (mobileSheetInitialized) return;
    mobileSheetInitialized = true;
    
    console.log('Initializing mobile results sheet');
    
    // Start collapsed on mobile
    resultsArea.classList.add('collapsed');
    
    // Toggle on header click
    resultsHeader.addEventListener('click', (e) => {
        e.stopPropagation();
        resultsArea.classList.toggle('collapsed');
        console.log('Results area toggled:', resultsArea.classList.contains('collapsed') ? 'collapsed' : 'expanded');
    });
    
    // Touch swipe support
    let touchStartY = 0;
    let touchEndY = 0;
    let isSwiping = false;
    
    resultsHeader.addEventListener('touchstart', (e) => {
        touchStartY = e.touches[0].clientY;
        isSwiping = true;
    }, { passive: true });
    
    resultsHeader.addEventListener('touchmove', (e) => {
        if (isSwiping) {
            touchEndY = e.touches[0].clientY;
        }
    }, { passive: true });
    
    resultsHeader.addEventListener('touchend', () => {
        if (!isSwiping) return;
        
        const swipeDistance = touchStartY - touchEndY;
        
        // Swipe up - expand
        if (swipeDistance > 50) {
            resultsArea.classList.remove('collapsed');
            console.log('Swiped up - expanded');
        }
        // Swipe down - collapse
        else if (swipeDistance < -50) {
            resultsArea.classList.add('collapsed');
            console.log('Swiped down - collapsed');
        }
        
        isSwiping = false;
    }, { passive: true });
}

// Initialize touch support for mobile
initTouchSupport();

// Initialize on load and resize
if (window.innerWidth <= 768) {
    initMobileResultsSheet();
}

window.addEventListener('resize', () => {
    if (window.innerWidth <= 768) {
        initMobileResultsSheet();
    }
});
